import React, { useState, useEffect, useRef, useMemo } from 'react';
import { formatMoney } from '../utils/formatMoney';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { API_BASE_URL } from '../components/config/api';
import { productsAPI } from '../services/api';
import Navigation from './Navigation';
import './CashierLight.css';
import type { ProductSize, Product, Category, BasketItem, POSTerminal, SelectedOptionEntry, PosOptionGroup } from '../types/order';
import { useVatLookup } from '../hooks/useVatLookup';
import { useToast } from './shared/Toast';
import { useProductIngredients } from '../hooks/useProductIngredients';
import { useOrderQueue } from '../hooks/useOrderQueue';
import OrderQueueModal from './shared/OrderQueueModal';
import OpenPriceModal from './shared/OpenPriceModal';
import WeightModal from './shared/WeightModal';
import ProductOptionsModal from './shared/ProductOptionsModal';
import ProductCatalog from './shared/ProductCatalog';
import TakeAwayFavoritesModal from './TakeAwayFavoritesModal';
import UpsellPrompt from './shared/UpsellPrompt';
import { useUpsellSuggestions } from '../hooks/useUpsellSuggestions';
import { pickUpsell, UpsellPick } from '../utils/upsell';
import { productDisplayPrice, lineUnitPrice, sizeChannelPrice, defaultSize, isFeeProduct } from '../utils/pricing';
import { computeAutoFeeBasket, autoFeeNoteUnit, manualFeeLine, autoManagedFeeLineId, addOrMergeManualFee, isOwnAutoFeeLine } from '../utils/autoFee';
import { isWeightedOpenPrice, weightedOpenPriceLine, weightUnitOf } from '../utils/weightedOpenPrice';
import { editedLineBasePrice, lineOwnAmount, EditedLineOwn } from '../utils/basketLineEdit';
import { extraSimpleIngredients, removableSimpleIngredients, simpleIngredientNames } from '../utils/simpleIngredients';
import { rehydrateDraftBasket } from '../utils/draftRehydrate';
import { resolveBarcodeLine, findBarcodeMergeLine, BarcodeLookupData } from '../utils/barcodeLine';
import { addOrMergeBasketLine } from '../utils/basketMerge';
import { changeLineQuantity, patchBasketLine, removeBasketLine, removeParkedLines, restoreRemovedLines } from '../utils/basketOps';
import ProductSizeModal from './shared/ProductSizeModal';
import CustomerDisplaySettings from './shared/CustomerDisplaySettings';
import { useCustomerDisplay } from '../hooks/useCustomerDisplay';
import { changedDisplayItem, saleDisplayFrame } from '../utils/customerDisplay';
import BasketItemRow, { BASKET_V2_COLUMNS } from './shared/BasketItemRow';
import QuickAddSheet from './shared/QuickAddSheet';
import CashChangeSheet from './shared/CashChangeSheet';
import CashKeypad, { cashOutcome } from './shared/CashKeypad';
import { useTheme } from '../contexts/ThemeContext';
import { CATEGORY_ROW_OPTIONS, catalogLookOf, setCatalogPrefsV2, useCatalogPrefsV2 } from '../utils/catalogPrefsV2';
import { formatEuro } from '../utils/formatEuro';
import { swallowGhostClick } from '../utils/ghostClick';
import { useBackdropDismiss } from '../utils/backdropDismiss';
import NumpadModal from './shared/NumpadModal';
import ProductCustomizeModal, { ProductCustomizeResult } from './shared/ProductCustomizeModal';
import PromotionBanner, { EligiblePromotion } from './shared/PromotionBanner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import TouchKeyboard from './ui/TouchKeyboard';
import CountryCodeInput, { countryLabel } from './ui/CountryCodeInput';
import {
  ShoppingCart,
  Plus,
  Minus,
  X,
  Search,
  CreditCard,
  Banknote,
  Receipt,
  ChevronRight,
  Home,
  Settings2,
  Settings,
  Loader2,
  Check,
  CheckCircle,
  XCircle,
  FileText,
  PauseCircle,
  HandCoins,
  Wallet,
  ChevronDown,
  Ellipsis,
  Undo2,
  AlertTriangle
} from 'lucide-react';
import { TakeawayPaymentMethod, isNoTerminalMethod, isExtraMethod, cashReceiptPaymentField, invoicePaymentMethodId } from '../utils/takeawayPayment';
import CardFailureActions from './shared/CardFailureActions';
import {
  ApiResult, CARD_CHECK_MAX_POLLS, CARD_CHECK_POLL_MS, isCardCheckState, mustCheckFirst,
  requestCardCancel, requestCardCheck, requestCardToCash, sleep,
} from '../utils/takeawayCardRecovery';
import TakeAwayDepositSheet from './deposits/TakeAwayDepositSheet';
import TakeAwayDepositLine from './deposits/TakeAwayDepositLine';
import { useTakeAwayDeposit } from './deposits/useTakeAwayDeposit';
import { toCents } from './deposits/model';
import { useDailyDishesToday } from '../hooks/useDailyDishesToday';
import { applyDailyDishes } from '../utils/dailyDishes';

/** Τρόπος του παραστατικού χωρίς τερματικό: και 'deposit' όταν η προκαταβολή καλύπτει όλο το ποσό. */
type NoTerminalReceiptMethod = 'cash' | 'credit' | 'deposit';
/** Αποτέλεσμα έκδοσης απόδειξης χωρίς τερματικό: retry = ο server την αρνήθηκε προσωρινά (receipt_documents_pending). */
type ReceiptOutcome = { ok: boolean; retry?: boolean; message?: string };
const isDocumentsPending = (status: number, data: any) =>
  status === 409 && (data?.code === 'receipt_documents_pending' || data?.error === 'receipt_documents_pending');

interface Customer {
  name: string;
  notes?: string;
}

interface TakeAwayProps {
  onPageChange?: (page: string) => void;
}

const TakeAway: React.FC<TakeAwayProps> = ({ onPageChange }) => {
  const { showToast } = useToast();
  const { t } = useTranslation(['takeaway', 'common']);
  const { store, employee } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [basket, setBasket] = useState<BasketItem[]>([]);
  const [customer, setCustomer] = useState<Customer>({
    name: '',
    notes: ''
  });
  // No payment method pre-selected: the cashier MUST pick one before an order
  // can be completed (avoids accidentally closing a card order as cash).
  const [paymentMethod, setPaymentMethod] = useState<TakeawayPaymentMethod | null>(null);
  // «Περισσότερα»: IRIS και «Επί πιστώσει» κάτω από τα γρήγορα Μετρητά/Κάρτα (απόφαση 6/10/2026).
  // Με επιλεγμένο IRIS/«Επί πιστώσει» η σειρά μένει ανοιχτή, ώστε η επιλογή να φαίνεται.
  const [showMoreMethods, setShowMoreMethods] = useState(false);
  const moreMethodsOpen = showMoreMethods || isExtraMethod(paymentMethod);
  const [loading, setLoading] = useState(true);
  
  // «Νέα εμφάνιση» (ανά συσκευή — ThemeContext): κεφαλίδα, κατάλογος και πάνελ παραγγελίας.
  const { look } = useTheme();
  const catalogPrefs = useCatalogPrefsV2();
  const v2 = look === 'v2';
  const [showBasketMenu, setShowBasketMenu] = useState(false);
  const basketMenuBackdrop = useBackdropDismiss(() => setShowBasketMenu(false));
  // Νέα εμφάνιση — βελτιώσεις Take Away (απόφαση ιδιοκτήτη 11/10/2026):
  // γρήγορη προσθήκη (μέγεθος + υποχρεωτικές σε ένα παράθυρο), αναίρεση διαγραφής,
  // πληρωμή με ένα πάτημα από το πάνελ, λωρίδα «#57 · Ρέστα» αντί για παράθυρο 2″.
  const [quickAdd, setQuickAdd] = useState<{ product: Product; groups: PosOptionGroup[]; defaults: SelectedOptionEntry[] } | null>(null);
  const [undoRemoval, setUndoRemoval] = useState<{
    label: string; basket: BasketItem[]; orderDiscount: number; discountType: 'percent' | 'fixed'; fixedDiscountAmount: number;
  } | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // deposit: η προκαταβολή πελάτη που αφαιρέθηκε (τα ρέστα βγαίνουν από το υπόλοιπο).
  const [lastSale, setLastSale] = useState<{ total: number; change: number | null; deposit?: number | null } | null>(null);
  // Πληρωμή με ένα πάτημα (επαναφορά 16/10/2026): ο τρόπος μπαίνει στην κατάσταση και η πληρωμή τρέχει στην
  // επόμενη ζωγραφιά (useEffect παρακάτω), ώστε το handleCheckout να δει τον ΝΕΟ τρόπο και τα ρέστα.
  const [quickPayMethod, setQuickPayMethod] = useState<TakeawayPaymentMethod | null>(null);
  const quickPayRef = useRef<TakeawayPaymentMethod | null>(null);
  const [showCashSheet, setShowCashSheet] = useState(false);
  const [searchKbOpen, setSearchKbOpen] = useState(false);
  // Επιλογές ανά προϊόν στη μνήμη (5′): το πάτημα στην κάρτα δεν περιμένει τον server.
  const optionGroupsCacheRef = useRef(new Map<number, { at: number; groups: PosOptionGroup[] }>());
  // Πόσα τεμάχια κάθε προϊόντος έχει ήδη το καλάθι — μετρητής «×2» στην κάρτα (όχι τα τέλη).
  const basketCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    for (const b of basket) {
      if (b.product.fee_category != null) continue;
      counts[b.product.id] = (counts[b.product.id] || 0) + (b.weight ? 1 : b.quantity);
    }
    return counts;
  }, [basket]);
  // Η λωρίδα «#57 · Ρέστα» μένει ώσπου να ξεκινήσει η επόμενη παραγγελία.
  useEffect(() => {
    if (basket.length > 0) setLastSale(null);
  }, [basket.length]);

  // View state: 'categories' | 'subcategories' | 'products'
  const [currentView, setCurrentView] = useState<'categories' | 'subcategories' | 'products'>('categories');

  // Grid layout settings (persisted in localStorage)
  const [categoryColumns, setCategoryColumns] = useState<number>(() => {
    const saved = localStorage.getItem('takeaway_category_columns');
    return saved ? parseInt(saved) : 4;
  });
  const [productColumns, setProductColumns] = useState<number>(() => {
    const saved = localStorage.getItem('takeaway_product_columns');
    return saved ? parseInt(saved) : 5;
  });
  const [showGridSettings, setShowGridSettings] = useState(false);

  // ── Order queue ────────────────────────────────────────────────────────────
  const { queue, park, resume, remove, updateNotes } = useOrderQueue(store?.id);

  // Refs so the unmount cleanup can read the latest values without stale closure
  const basketRef = useRef(basket);
  const customerRef = useRef(customer);
  const paymentMethodRef = useRef(paymentMethod);
  const parkRef = useRef(park);
  useEffect(() => { basketRef.current = basket; }, [basket]);
  useEffect(() => { customerRef.current = customer; }, [customer]);
  useEffect(() => { paymentMethodRef.current = paymentMethod; }, [paymentMethod]);
  useEffect(() => { parkRef.current = park; }, [park]);

  // Manual override of the auto plastic-fee quantity. null = follow the
  // auto count; a number = the cashier waived/adjusted it (e.g. the customer
  // brought their own cup). Resets when the basket is emptied.
  const [feeOverride, setFeeOverride] = useState<number | null>(null);

  // Ρυθμίσεις take away. Δηλώνονται ΕΔΩ και όχι μαζί με τις υπόλοιπες πιο
  // κάτω, γιατί τις διαβάζει το effect του περιβαλλοντικού τέλους αμέσως
  // από κάτω — και ο πίνακας εξαρτήσεών του αποτιμάται στο render.
  // Χωρίς numpad_enabled / favorites_enabled από 12/10/2026: αριθμητικό μετρητών και «Αγαπημένα» πάντα.
  const [takeawaySettings, setTakeawaySettings] = useState<{
    print_ticket: boolean;
    discount_presets?: number[];
    discount_allow_custom_percent?: boolean;
    discount_allow_fixed_amount?: boolean;
    discount_max_percent?: number;
    discount_max_amount?: number;
    auto_plastic_fee_enabled?: boolean;
  } | null>(null);

  // Auto-add the code-16 single-use plastic fee whenever the basket contains
  // items flagged with auto_plastic_fee_count > 0. The fee row tracks the sum
  // of (qty × trigger count) across non-fee items. Catalog/size prices are net
  // of the fee in EVERY mode, so the fee is always added as its own basket line
  // (and a separate rec_type=2 line on the receipt). Platform orders, whose
  // prices already include the fee, carve it out at fiscal time instead.
  useEffect(() => {
    const feeProduct = products.find(p => p.fee_category === 16);
    if (!feeProduct) return;

    // Κατάστημα που σερβίρει σε πορσελάνη/γυαλί μπορεί να κλείσει την ΑΥΤΟΜΑΤΗ
    // συμπλήρωση (Ρυθμίσεις → Take away). Δεν σβήνουμε το προϊόν τέλους: ο
    // ταμίας το προσθέτει με το χέρι όταν χρειάζεται.
    //   • `!== false` → όσο δεν έχουν φορτώσει οι ρυθμίσεις ισχύει η προεπιλογή
    //     (τέλος ενεργό), ώστε μια αποτυχία φόρτωσης να μη σταματά χρεώσεις.
    //   • Όταν κλείσει, αφαιρούμε ΜΟΝΟ τη γραμμή που βάλαμε εμείς — την ξεχωρίζει
    //     το πρόθεμα `auto-fee-16-` του id. Έτσι ούτε η χειροκίνητη γραμμή
    //     εξαφανίζεται, ούτε μένει ορφανή μια αυτόματη αν ο διακόπτης γυρίσει
    //     με γεμάτο καλάθι (ή αν προλάβε να μπει πριν φορτώσουν οι ρυθμίσεις).
    // Ποσό = feeUnitGross (state_fees, απόφαση 1_fees_source), γραμμή χωρίς μέγεθος·
    // χωρίς ποσό δεν μπαίνει γραμμή (απόφαση 33). Κανόνες ποσότητας στο utils/autoFee.
    const autoEnabled = takeawaySettings?.auto_plastic_fee_enabled;
    const next = computeAutoFeeBasket({ basket, feeProduct, feeOverride, autoEnabled });
    // Ο ίδιος κανόνας πάνω στο ΤΡΕΧΟΝ καλάθι (utils/basketOps): το effect τρέχει μετά τη ζωγραφιά και
    // ένα είδος που μπήκε στην ουρά στο μεταξύ θα χανόταν με το `setBasket(next)` του αντιγράφου.
    // Χωρίς τέτοιο είδος (prev === basket) η γραμμή είναι η ίδια ακριβώς `next`.
    if (next !== basket) {
      setBasket(prev => (prev === basket ? next : computeAutoFeeBasket({ basket: prev, feeProduct, feeOverride, autoEnabled })));
    }
  }, [basket, products, feeOverride, takeawaySettings?.auto_plastic_fee_enabled]);

  // New order → forget any fee waiver.
  useEffect(() => {
    if (basket.length === 0 && feeOverride !== null) setFeeOverride(null);
  }, [basket.length, feeOverride]);

  // «Αγαπημένα» (migration 256): λίστα ανά ΚΑΤΑΣΤΗΜΑ, ίδια σε όλα τα ταμεία, που
  // την αλλάζει κάθε ταμίας από το μενού χρήστη. Με έστω ένα αγαπημένο ο κατάλογος
  // δείχνει πρώτη την κατηγορία «Αγαπημένα». Πάντα — ο διακόπτης καταργήθηκε 12/10/2026.
  const [favoriteIds, setFavoriteIds] = useState<number[]>([]);
  const [favoritesView, setFavoritesView] = useState(false);
  const [showFavoritesModal, setShowFavoritesModal] = useState(false);
  const favoritesSeqRef = useRef(0);

  const loadFavorites = React.useCallback(async () => {
    if (!store?.id) return;
    const seq = ++favoritesSeqRef.current;
    try {
      const response = await fetch(`${API_BASE_URL}/api/takeaway/${store.id}/favorites`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      if (seq === favoritesSeqRef.current) setFavoriteIds(Array.isArray(data.product_ids) ? data.product_ids : []);
    } catch (error) {
      console.error('Failed to fetch takeaway favorites:', error);
    }
  }, [store?.id]);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  // Άδειασε η λίστα ενώ ο ταμίας ήταν μέσα → πίσω στις κατηγορίες.
  useEffect(() => {
    if (favoritesView && favoriteIds.length === 0) {
      setFavoritesView(false);
      setCurrentView('categories');
    }
  }, [favoritesView, favoriteIds.length]);

  // Ανοίγει με φρέσκια λίστα: μπορεί να την άλλαξε άλλο ταμείο.
  const openFavorites = () => {
    loadFavorites();
    setShowFavoritesModal(true);
  };

  // Κάθε πάτημα αποθηκεύεται αμέσως. Η απάντηση φέρνει όλη τη λίστα· μετρά μόνο η
  // πιο πρόσφατη, ώστε γρήγορα διαδοχικά πατήματα να μην επαναφέρουν παλιά εικόνα.
  // Σε αποτυχία ξαναφορτώνει ό,τι ισχύει στον server.
  const toggleFavorite = async (productId: number) => {
    const isOn = favoriteIds.includes(productId);
    setFavoriteIds(ids => (isOn ? ids.filter(id => id !== productId) : [...ids.filter(id => id !== productId), productId]));
    const seq = ++favoritesSeqRef.current;
    try {
      const response = await fetch(`${API_BASE_URL}/api/takeaway/${store?.id}/favorites/${productId}`, {
        method: isOn ? 'DELETE' : 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (seq === favoritesSeqRef.current && Array.isArray(data.product_ids)) setFavoriteIds(data.product_ids);
    } catch (error) {
      console.error('Failed to update takeaway favorite:', error);
      showToast('error', 'Δεν αποθηκεύτηκε η αλλαγή στα αγαπημένα');
      loadFavorites();
    }
  };

  // Auto-park on navigation away (both SPA unmount and full page reload)
  useEffect(() => {
    const doPark = () => {
      // Καλάθι παραγγελίας που περιμένει πληρωμή κάρτας: η παραγγελία υπάρχει ήδη στον server (με
      // δελτίο κουζίνας). Ως πρόχειρο ξαναχτυπιόταν ως ΝΕΑ — και αν η κάρτα είχε περάσει, διπλή χρέωση.
      if (currentOrderIdRef.current) return;
      if (basketRef.current.length > 0) {
        parkRef.current({
          type: 'takeaway',
          basket: basketRef.current,
          customer: customerRef.current,
          paymentMethod: paymentMethodRef.current ?? 'cash'
        });
        basketRef.current = []; // prevent double-park if both cleanup and beforeunload fire
      }
    };

    window.addEventListener('beforeunload', doPark);
    return () => {
      doPark(); // SPA unmount path
      window.removeEventListener('beforeunload', doPark);
    };
  }, []);

  const [showQueueModal, setShowQueueModal] = useState(false);

  // «Σε αναμονή» (έγκριση ιδιοκτήτη 24/10/2026): ως τότε το καλάθι άδειαζε ΚΑΙ όταν ο server δεν
  // κρατούσε το πρόχειρο (η παραγγελία χανόταν), και έσβηνε ό,τι πατήθηκε όσο περίμενε. Τώρα:
  // αποτυχία → όλα μένουν ως έχουν· επιτυχία → φεύγουν μόνο όσα παρκαρίστηκαν (utils/basketOps).
  const handlePark = async () => {
    if (basket.length === 0) return;
    const parked = basket;
    const draftId = await park({ type: 'takeaway', basket: parked, customer, paymentMethod: paymentMethod ?? 'cash' });
    if (draftId == null) {
      showToast('error', t('takeaway:parkFailed'));
      return;
    }
    setBasket(prev => removeParkedLines(prev, parked));
    setCustomer({ name: '', notes: '' });
    setPaymentMethod(null);
  };

  const handleResumeDraft = async (id: number) => {
    const draft = await resume(id);
    if (!draft || draft.type !== 'takeaway') return;
    // Προϊόντα/μεγέθη ξαναδεμένα με τον τρέχοντα κατάλογο κατά id (τα ids μεγεθών
    // αλλάζουν όταν ξαναφτιαχτούν)· τα ποσά του προχείρου μένουν ως έχουν. Αν ο
    // κατάλογος δεν έχει φορτώσει ακόμη, το καλάθι επανέρχεται αυτούσιο όπως πριν.
    const { basket: rehydrated, unresolved } = rehydrateDraftBasket(draft.basket, products);
    setBasket(rehydrated);
    // Άλλη παραγγελία: η προκαταβολή που είχε διαλεχτεί για το προηγούμενο καλάθι δεν περνά σε αυτή.
    deposit.remove();
    if (unresolved.length > 0) {
      console.warn('[draft] γραμμές χωρίς αντιστοίχιση στον κατάλογο:', unresolved);
      showToast('warning', t('takeaway:draftItemsChanged', { count: unresolved.length }));
    }
    setCustomer(draft.customer);
    // ΠΟΤΕ προεπιλεγμένος τρόπος πληρωμής, ούτε σε παραγγελία από τις Εκκρεμείς:
    // ο ταμίας ρωτά τον πελάτη (βλ. Ολοκλήρωση). Το πρόχειρο αποθηκεύει 'cash'
    // όταν δεν είχε επιλεγεί τίποτα, και η επαναφορά του έβγαζε ΜΕΤΡΗΤΑ έτοιμα —
    // κάρτα που πληρωνόταν μπορούσε να κλείσει κατά λάθος ως μετρητά.
    setPaymentMethod(null);
  };
  // ──────────────────────────────────────────────────────────────────────────

  // Persist grid settings to localStorage
  useEffect(() => {
    localStorage.setItem('takeaway_category_columns', String(categoryColumns));
  }, [categoryColumns]);
  useEffect(() => {
    localStorage.setItem('takeaway_product_columns', String(productColumns));
  }, [productColumns]);

  // Modal states
  const [showSizeModal, setShowSizeModal] = useState(false);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedSize, setSelectedSize] = useState<ProductSize | null>(null);
  const [removedIngredients, setRemovedIngredients] = useState<number[]>([]);
  const [addedExtras, setAddedExtras] = useState<{ raw_material_id: number; name: string; quantity: number; price: number }[]>([]);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successOrderCode, setSuccessOrderCode] = useState('');
  const [productDiscount, setProductDiscount] = useState<number>(0);
  const [orderDiscount, setOrderDiscount] = useState<number>(0);
  const [appliedPromotionId, setAppliedPromotionId] = useState<number | null>(null);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [tempOrderDiscount, setTempOrderDiscount] = useState<number>(0);
  const [showNumpad, setShowNumpad] = useState(false);
  const [numpadValue, setNumpadValue] = useState('');
  const [numpadTarget, setNumpadTarget] = useState<'orderPercent' | 'orderFixed' | 'tempDiscount' | 'productDiscount'>('orderPercent');
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState<number | null>(null);
  const [ecrEnabled, setEcrEnabled] = useState(false);
  const [trainingModeEnabled, setTrainingModeEnabled] = useState(false);
  const [isTrainingOrder, setIsTrainingOrder] = useState(false);
  const holdTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // «Ολοκλήρωση» (νέα εμφάνιση): το φόντο κλείνει μόνο όταν και το πάτημα ξεκίνησε στο φόντο.
  const checkoutPressOnBackdropRef = React.useRef(false);
  // Αναμονή κάρτας: η οδηγία «η ακύρωση γίνεται στο τερματικό» (κουμπί «Ακύρωση»).
  const [cardCancelHelp, setCardCancelHelp] = useState(false);
  const { ingredients: productIngredients, extras: availableExtras, loading: loadingMaterials, fetchBySize: fetchIngredientsBySize, clear: clearIngredients } = useProductIngredients();

  // Option groups state
  const [productOptionGroups, setProductOptionGroups] = useState<PosOptionGroup[]>([]);
  const [pendingSelectedOptions, setPendingSelectedOptions] = useState<SelectedOptionEntry[]>([]);
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [optionModalPendingSize, setOptionModalPendingSize] = useState<ProductSize | null>(null);

  // POS Payment states
  const [posTerminals, setPosTerminals] = useState<POSTerminal[]>([]);
  const [selectedTerminal, setSelectedTerminal] = useState<POSTerminal | null>(null);
  const [showTerminalModal, setShowTerminalModal] = useState(false);
  const [showPaymentProcessingModal, setShowPaymentProcessingModal] = useState(false);
  // checking  = ρωτάμε τον server/πάροχο τι έγινε με την κάρτα (καμία ενέργεια στο μεταξύ)
  // failed    = ΣΙΓΟΥΡΑ δεν χρεώθηκε → «Ξανά κάρτα» / «Μετρητά» / «Ακύρωση» στην ίδια παραγγελία
  // uncertain = δεν ξέρουμε αν χρεώθηκε → μόνο «Έλεγχος ξανά» (ούτε νέα χρέωση ούτε μετρητά)
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'checking' | 'success' | 'failed' | 'uncertain'>('idle');
  const [paymentMessage, setPaymentMessage] = useState('');
  // Η παραγγελία που περιμένει πληρωμή κάρτας. Όσο υπάρχει, το παράθυρο πληρωμής μένει ανοιχτό (το
  // καλάθι δεν αλλάζει) και κάθε νέα προσπάθεια πάει στην ΙΔΙΑ παραγγελία — ποτέ δεύτερη.
  const [currentOrderId, setCurrentOrderId] = useState<number | null>(null);
  const currentOrderIdRef = useRef<number | null>(null);
  useEffect(() => { currentOrderIdRef.current = currentOrderId; }, [currentOrderId]);
  // Πόσα χρεώνει η κάρτα σε αυτή την παραγγελία (λεπτά): το order.deposit.remaining του server όταν αφαιρέθηκε
  // προκαταβολή πελάτη — ο server αρνείται (409 amount_mismatch) οτιδήποτε άλλο από σύνολο − προκαταβολή.
  const [orderDueCents, setOrderDueCents] = useState<number | null>(null);
  const orderDueRef = useRef<number | null>(null);
  const [generatingReceipt, setGeneratingReceipt] = useState(false);
  const [isPaymentRetry, setIsPaymentRetry] = useState(false);
  // Μία ενέργεια κάρτας τη φορά (διπλό πάτημα σε οθόνη αφής = δεύτερη χρέωση).
  const cardBusyRef = useRef(false);
  const [cardActionBusy, setCardActionBusy] = useState(false);
  // Το ποσό δεν ταιριάζει με την παραγγελία: μένει μόνο η «Ακύρωση».
  const [cardFailureCancelOnly, setCardFailureCancelOnly] = useState(false);
  // Νέα εμφάνιση: «Μετρητά» μετά από κάρτα που δεν πέρασε ανοίγει το ίδιο παράθυρο ρέστων με κάθε πληρωμή μετρητών.
  const [showCardCashSheet, setShowCardCashSheet] = useState(false);
  // Η υπογραφή Oxygen της τελευταίας προσπάθειας που ΔΕΝ πέρασε (ακυρώνεται όταν αυτό είναι σίγουρο).
  const pendingSignatureIdRef = useRef<string | number | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Invoice states
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  // On-screen keyboard for the invoice modal (touch-only POS screens): which
  // field is being edited + numeric vs full-text layout.
  const [invoiceKb, setInvoiceKb] = useState<{ field: string; mode: 'numeric' | 'text'; label: string; maxLength?: number } | null>(null);

  const invoiceKbValue = (): string => {
    if (!invoiceKb) return '';
    const c: any = invoiceCustomer || {};
    switch (invoiceKb.field) {
      case 'vat_search': return invoiceVatNumber;
      case 'vat_number': return c.vat_number || '';
      case 'country': return invoiceCountryQuery;
      case 'name': return c.name || '';
      case 'street': return c.address?.street || '';
      case 'number': return c.address?.number || '';
      case 'city': return c.address?.city || '';
      case 'postal_code': return c.address?.postal_code || '';
      case 'branch_code': return String(c.branch_code ?? '');
      default: return '';
    }
  };

  const invoiceKbChange = (next: string) => {
    if (!invoiceKb) return;
    const c: any = invoiceCustomer || {};
    switch (invoiceKb.field) {
      case 'vat_search': setInvoiceVatNumber(next.replace(/\D/g, '')); break;
      // Foreign VAT numbers can contain letters and exceed 9 chars (e.g. NL...B01)
      // — only the Greek AADE *search* field is digits-only.
      case 'vat_number': setInvoiceCustomer({ ...c, vat_number: next.replace(/[^0-9A-Za-z]/g, '').toUpperCase() }); break;
      case 'country': setInvoiceCountryQuery(next); break;
      case 'name': setInvoiceCustomer({ ...c, name: next }); break;
      case 'street': setInvoiceCustomer({ ...c, address: { ...c.address, street: next } }); break;
      case 'number': setInvoiceCustomer({ ...c, address: { ...c.address, number: next } }); break;
      case 'city': setInvoiceCustomer({ ...c, address: { ...c.address, city: next } }); break;
      case 'postal_code': setInvoiceCustomer({ ...c, address: { ...c.address, postal_code: next.replace(/\D/g, '') } }); break;
      case 'branch_code': setInvoiceCustomer({ ...c, branch_code: parseInt(next.replace(/\D/g, ''), 10) || 0 }); break;
      default: break;
    }
  };

  const invoiceKbDone = () => {
    const wasVatSearch = invoiceKb?.field === 'vat_search';
    setInvoiceKb(null);
    // Convenience: closing the numpad on a complete ΑΦΜ fires the lookup directly.
    if (wasVatSearch && invoiceVatNumber.length === 9) lookupVatNumber();
  };
  const { vatNumber: invoiceVatNumber, setVatNumber: setInvoiceVatNumber, customer: invoiceCustomer, setCustomer: setInvoiceCustomer, isLoading: lookingUpVat, error: vatLookupError, setError: setVatLookupError, lookup: vatLookup, reset: resetVatLookup } = useVatLookup(); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  // Τιμολόγιο που δεν εκδόθηκε ενώ η παραγγελία καταχωρήθηκε (με προκαταβολή, ή 409 receipt_documents_pending):
  // το ξαναπάτημα βγάζει το τιμολόγιο στην ΙΔΙΑ παραγγελία — νέα θα έπεφτε σε deposit_insufficient.
  const invoiceOrderRef = useRef<{ orderId: number; shortCode: string } | null>(null);
  // Country picker is a type-ahead: the query text is separate state so the
  // TouchKeyboard can type into it; the submitted value stays an ISO code.
  const [invoiceCountryQuery, setInvoiceCountryQuery] = useState(countryLabel('GR'));
  // AADE lookup only covers Greek businesses — foreign invoices are entered by hand.
  const [manualInvoiceEntry, setManualInvoiceEntry] = useState(false);
  useEffect(() => {
    setInvoiceCountryQuery(countryLabel(invoiceCustomer?.country_code || 'GR'));
  }, [invoiceCustomer?.country_code]);

  const [cashReceived, setCashReceived] = useState<string>('');
  const [discountType, setDiscountType] = useState<'percent' | 'fixed'>('percent');
  const [fixedDiscountAmount, setFixedDiscountAmount] = useState<number>(0);

  // Add to basket animation
  const [addedToCartAnimation, setAddedToCartAnimation] = useState(false);
  const [lastAddedProduct, setLastAddedProduct] = useState<string>('');

  // Edit basket item
  const [editingBasketItem, setEditingBasketItem] = useState<BasketItem | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Weight-variable products
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [weightInput, setWeightInput] = useState<string>('');

  // Open price products
  const [showOpenPriceModal, setShowOpenPriceModal] = useState(false);
  const [openPriceInput, setOpenPriceInput] = useState<string>('');
  // «Βάρος + τιμή κιλού»: το βάρος του πρώτου βήματος, όσο ο ταμίας δίνει την τιμή κιλού.
  const [weightForOpenPrice, setWeightForOpenPrice] = useState<number | null>(null);

  // Προτάσεις πώλησης (κεντρικά → «Προτάσεις πώλησης», διακόπτης οργανισμού): μία φορά ανά
  // παραγγελία, στο «Ολοκλήρωση», ΠΡΙΝ ανοίξει η πληρωμή. Από εκεί περνούν όλοι οι τρόποι
  // (μετρητά/κάρτα/IRIS/πίστωση/τιμολόγιο/εκπαίδευση) και η πληρωμή ανοίγει με το ΝΕΟ καλάθι·
  // μέσα στο «Πληρωμή» οι handlers κρατούν το καλάθι του render τους (closure).
  const { suggestions: upsellSuggestions, recordImpression: recordUpsellImpression } =
    useUpsellSuggestions(store?.id, 'take_away');
  const [upsellPick, setUpsellPick] = useState<UpsellPick | null>(null);
  // Μετά το «Προσθήκη»: όταν κλείσει όποιο παράθυρο άνοιξε το προϊόν (μέγεθος, επιλογές,
  // ζύγι, ανοιχτή τιμή) ανοίγει η πληρωμή — είτε μπήκε το προϊόν είτε ακυρώθηκε.
  const [upsellContinue, setUpsellContinue] = useState(false);
  const upsellShownRef = useRef(false);
  // Η πρόταση από την οποία προέρχεται η ΕΠΟΜΕΝΗ γραμμή του καλαθιού.
  const upsellTagRef = useRef<number | null>(null);
  const takeUpsellTag = () => {
    const id = upsellTagRef.current;
    upsellTagRef.current = null;
    return id ? { upsell_suggestion_id: id } : {};
  };
  useEffect(() => {
    if (basket.length === 0) upsellShownRef.current = false;
  }, [basket.length]);
  const hiddenCategoryIds = useMemo(() => {
    const hidden = new Set<number>();
    categories.forEach(c => {
      if (c.is_hidden) hidden.add(c.id);
      (c.subcategories || []).forEach(s => { if (c.is_hidden || s.is_hidden) hidden.add(s.id); });
    });
    return hidden;
  }, [categories]);

  // Barcode scanner
  const [barcodeError, setBarcodeError] = useState('');
  const [scanningBarcode, setScanningBarcode] = useState(false);
  const barcodeBufferRef = React.useRef('');
  const barcodeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch TakeAway settings
  const fetchTakeawaySettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/takeaway/${store?.id}/settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setTakeawaySettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch takeaway settings:', error);
    }
  };

  const { hasPermission } = usePermissions();

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const data = await productsAPI.getCategories(store!.id);
      setCategories(data.filter((cat: Category) => cat.is_active));
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  // Fetch products
  const fetchProducts = async () => {
    try {
      setLoading(true);
      optionGroupsCacheRef.current.clear();
      const data = await productsAPI.getProducts(store!.id, { available_only: true });
      setProducts(data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  // «Πιάτα ημέρας» (migration 278): η κατηγορία πρώτη και μόνο τα σημερινά (ο server κρύβει ήδη τα υπόλοιπα
  // στο available_only). Κάθε αλλαγή (εδώ, άλλο ταμείο, διαχείριση) ξαναφέρνει τα προϊόντα: νέο πιάτο, νέα
  // τιμή, ή πιάτο που μόλις μπήκε στα σημερινά. Κλειστή λειτουργία = κανένα αίτημα, κατάλογος όπως πριν.
  const { today: dailyToday } = useDailyDishesToday(store?.id, store?.daily_dishes_enabled === true, () => { fetchProducts(); });
  const dailyCatalog = useMemo(() => applyDailyDishes(categories, products, dailyToday), [categories, products, dailyToday]);

  // Fetch document types if ECR is enabled
  const fetchDocumentTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/fiscal/stores/${store?.id}/cashier-document-types`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEcrEnabled(data.ecrEnabled);
        setTrainingModeEnabled(data.trainingModeEnabled || false);
        setDocumentTypes(data.documentTypes);
        
        // Set default document type to ΑΠΟΔΕΙΞΗ ΛΙΑΝΙΚΗΣ ΠΩΛΗΣΗΣ
        if (data.documentTypes.length > 0) {
          const defaultDoc = data.documentTypes.find((doc: any) => 
            doc.name === 'ΑΠΟΔΕΙΞΗ ΛΙΑΝΙΚΗΣ ΠΩΛΗΣΗΣ'
          );
          setSelectedDocumentType(defaultDoc?.id || data.documentTypes[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch document types:', error);
    }
  };

  // Fetch POS terminals
  const fetchPosTerminals = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/pos/terminals`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const activeTerminals = data.filter((t: POSTerminal) => t.is_active);
        setPosTerminals(activeTerminals);
        
        // Set default terminal if available
        const defaultTerminal = activeTerminals.find((t: POSTerminal) => t.is_default);
        if (defaultTerminal) {
          setSelectedTerminal(defaultTerminal);
        } else if (activeTerminals.length > 0) {
          setSelectedTerminal(activeTerminals[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch POS terminals:', error);
    }
  };

  useEffect(() => {
    if (store?.id) {
      fetchCategories();
      fetchProducts();
      fetchDocumentTypes();
      fetchPosTerminals();
      fetchTakeawaySettings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  // Global barcode scanner listener — captures scanner input without a visible input box.
  // Scanners type very fast (< 50ms/char) and end with Enter. Humans type slowly.
  useEffect(() => {
    if (!store?.barcode_enabled) return;

    const SCANNER_TIMEOUT_MS = 50; // max ms between chars from a scanner
    const MIN_BARCODE_LENGTH = 6;  // ignore accidental short sequences

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an actual input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      if (e.key === 'Enter') {
        const buf = barcodeBufferRef.current;
        if (buf.length >= MIN_BARCODE_LENGTH) {
          handleBarcodeScan(buf);
        }
        barcodeBufferRef.current = '';
        if (barcodeTimerRef.current) clearTimeout(barcodeTimerRef.current);
        return;
      }

      // Only accumulate printable single chars
      if (e.key.length !== 1) return;

      barcodeBufferRef.current += e.key;

      // Reset buffer if no new char arrives within SCANNER_TIMEOUT_MS
      if (barcodeTimerRef.current) clearTimeout(barcodeTimerRef.current);
      barcodeTimerRef.current = setTimeout(() => {
        barcodeBufferRef.current = '';
      }, SCANNER_TIMEOUT_MS);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (barcodeTimerRef.current) clearTimeout(barcodeTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.barcode_enabled]);

  // Auto-select correct document type based on basket contents
  useEffect(() => {
    if (!ecrEnabled || documentTypes.length === 0 || basket.length === 0) return;
    
    const basketCategory = getBasketProductCategory();
    const requiredInvoiceType = basketCategory === 'services' ? '11.2' : '11.1';
    
    // Check if current selection is valid
    const currentDoc = documentTypes.find(d => d.id === selectedDocumentType);
    if (currentDoc?.invoice_type_code !== requiredInvoiceType) {
      // Find and select the correct document type
      const correctDoc = documentTypes.find(d => d.invoice_type_code === requiredInvoiceType);
      if (correctDoc) {
        setSelectedDocumentType(correctDoc.id);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basket, documentTypes, ecrEnabled]);

  // Τέλη (fee_category 8/16) με το χέρι: ποσό = feeUnitGross (state_fees, απόφαση
  // 1_fees_source), ΠΑΝΤΑ χωρίς μέγεθος — ούτε modal μεγεθών, ούτε option groups, ούτε
  // ανοιχτή τιμή/ζύγι. Αλλιώς ένα τέλος με μέγεθος (7700 «ΣΑΚΟΥΛΑ») χρεωνόταν βάση +
  // μέγεθος. Χωρίς ποσό δεν μπαίνει γραμμή (απόφαση 33): μήνυμα στον ταμία και τέλος.
  // Η γραμμή φτιάχνεται στο utils/autoFee (manualFeeLine), κοινό με το Delivery και με tests.
  const addFeeManually = (product: Product) => {
    setSelectedProduct(null);
    const validation = canAddToBasket(product);
    if (!validation.allowed) {
      showToast('warning', validation.message || 'Μη έγκυρη επιλογή');
      return;
    }
    const feeLine = manualFeeLine(product);
    if (!feeLine) {
      showToast('error', t('feeAmountMissing'));
      return;
    }
    // Δεύτερο πάτημα = +1 στη χειροκίνητη γραμμή (όπως κάθε είδος)· ποτέ στη γραμμή του
    // αυτοματισμού, που την ορίζει ο κανόνας/feeOverride (utils/autoFee).
    setBasket(prev => addOrMergeManualFee(prev, feeLine, autoManagedFeeLineId({
      basket: prev,
      feeProduct: products.find(p => p.fee_category === 16),
      feeOverride,
      autoEnabled: takeawaySettings?.auto_plastic_fee_enabled
    })));
    setLastAddedProduct(product.name);
    setAddedToCartAnimation(true);
    setTimeout(() => setAddedToCartAnimation(false), 1500);
  };

  // Το long-press ενός τέλους το προσθέτει ήδη· το click που ακολουθεί στο mouseup του ίδιου
  // πλακιδίου δεν πρέπει να βάλει δεύτερη γραμμή. Μηδενίζεται σε κάθε νέο πάτημα
  // (handleMouseDown), όπως το longPressTriggeredRef του Delivery· μόνο για τέλη, ώστε η ροή
  // των υπόλοιπων προϊόντων να μείνει ως έχει.
  const feeLongPressRef = useRef(false);

  // Handle product click
  const handleProductClick = async (product: Product) => {
    if (isFeeProduct(product)) {
      if (feeLongPressRef.current) {
        feeLongPressRef.current = false;
        return;
      }
      addFeeManually(product);
      return;
    }
    setSelectedProduct(product);

    // «Βάρος + τιμή κιλού» (ζυγιζόμενο ΚΑΙ ελεύθερης τιμής): πρώτα το βάρος, μετά η τιμή κιλού
    // (utils/weightedOpenPrice). ΠΡΙΝ από τον κλάδο της ανοιχτής τιμής, που θα ζητούσε μόνο ποσό.
    if (isWeightedOpenPrice(product)) {
      setWeightForOpenPrice(null);
      setWeightInput('');
      setShowWeightModal(true);
      return;
    }

    // If product has open price, show price input numpad
    if (product.is_open_price) {
      setWeightForOpenPrice(null);
      setOpenPriceInput('');
      setShowOpenPriceModal(true);
      return;
    }
    
    // If product is weight-variable, show weight input modal
    if (product.is_weight_variable) {
      setWeightInput('');
      setShowWeightModal(true);
      return;
    }
    
    // Fetch option groups — quick add shows ONLY required groups
    const groups = await fetchProductOptionGroups(product.id);
    const requiredGroups = groups.filter(g => g.is_required);

    // Νέα εμφάνιση: μέγεθος ΚΑΙ υποχρεωτικές επιλογές σε ένα παράθυρο (QuickAddSheet).
    if (v2 && ((product.sizes && product.sizes.length > 1) || requiredGroups.length > 0)) {
      setQuickAdd({ product, groups: requiredGroups, defaults: defaultOptionsOf(requiredGroups) });
      return;
    }

    // If product has multiple sizes, show size selection modal
    if (product.sizes && product.sizes.length > 1) {
      setSelectedSize(defaultSize(product.sizes));
      setShowSizeModal(true);
    } else if (product.sizes && product.sizes.length === 1) {
      const onlySz = product.sizes[0];
      if (requiredGroups.length > 0) {
        // Show only required option groups before adding
        setProductOptionGroups(requiredGroups);
        setOptionModalPendingSize(onlySz);
        setShowOptionModal(true);
      } else {
        // Χωρίς υποχρεωτικές = χωρίς επιλογές, όπως η γραμμή χωρίς μέγεθος από κάτω. Ως 24/10/2026 έπαιρνε
        // το pendingSelectedOptions του render ΠΡΙΝ το await — τις επιλογές του ΠΡΟΗΓΟΥΜΕΝΟΥ προϊόντος
        // όταν το παράθυρό του έκλεινε χωρίς προσθήκη, μαζί με τις προσαυξήσεις τους (έγκριση ιδιοκτήτη).
        addToBasketWithSize(product, onlySz);
      }
    } else {
      if (requiredGroups.length > 0) {
        setProductOptionGroups(requiredGroups);
        setOptionModalPendingSize(null);
        setShowOptionModal(true);
      } else {
        addToBasketWithoutSize(product);
      }
    }
  };

  // Handle long press for customization
  const handleProductLongPress = async (product: Product) => {
    if (isFeeProduct(product)) {
      feeLongPressRef.current = true;
      addFeeManually(product);
      return;
    }
    setSelectedProduct(product);
    const initialSize = defaultSize(product.sizes);
    setSelectedSize(initialSize);
    setRemovedIngredients([]);
    setAddedExtras([]);
    setSpecialInstructions('');
    setProductDiscount(0);
    setPendingSelectedOptions([]);

    // Fetch ingredients and ALL option groups in parallel
    await Promise.all([
      fetchProductOptionGroups(product.id), // sets productOptionGroups + pendingSelectedOptions defaults
      initialSize ? fetchSizeMaterials(initialSize.id) : Promise.resolve(clearIngredients())
    ]);

    setShowCustomizeModal(true);
  };

  const handleMouseDown = (product: Product) => {
    feeLongPressRef.current = false;
    const timer = setTimeout(() => {
      handleProductLongPress(product);
    }, 500); // 500ms for long press
    setLongPressTimer(timer);
  };

  const handleMouseUp = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  // Check if product can be added to basket (no mixing services with products/merchandise)
  const canAddToBasket = (product: Product): { allowed: boolean; message?: string } => {
    if (basket.length === 0) {
      return { allowed: true };
    }

    const productType = product.product_type || 'ΠΡΟΙΟΝ';
    const isService = productType === 'ΥΠΗΡΕΣΙΑ';
    
    // Check existing basket items
    const hasServices = basket.some(item => item.product.product_type === 'ΥΠΗΡΕΣΙΑ');
    const hasProductsOrMerchandise = basket.some(item => 
      item.product.product_type === 'ΠΡΟΙΟΝ' || 
      item.product.product_type === 'ΕΜΠΟΡΕΥΜΑ' ||
      !item.product.product_type // Default to ΠΡΟΙΟΝ if not set
    );

    if (isService && hasProductsOrMerchandise) {
      return { 
        allowed: false, 
        message: t('cannotMixServicesWithProducts')
      };
    }

    if (!isService && hasServices) {
      return {
        allowed: false,
        message: t('cannotMixProductsWithServices')
      };
    }

    return { allowed: true };
  };

  const openCheckoutModal = () => {
    // Πληρωμή με ένα πάτημα: η πρόταση πώλησης έχει ήδη περάσει, πάμε στον τρόπο.
    if (quickPayRef.current) {
      const method = quickPayRef.current;
      quickPayRef.current = null;
      runQuickPay(method);
      return;
    }
    setCashReceived('');
    setShowMoreMethods(false);
    setShowCheckoutModal(true);
  };

  /** «Ολοκλήρωση»: πρώτα η πρόταση πώλησης (μία φορά ανά παραγγελία), μετά η πληρωμή. */
  const startCheckout = () => {
    if (!upsellShownRef.current) {
      const pick = pickUpsell(upsellSuggestions, basket, products, {
        canAdd: (p) => canAddToBasket(p).allowed,
        hiddenCategoryIds,
      });
      if (pick) {
        upsellShownRef.current = true;
        recordUpsellImpression(pick.suggestion.id);
        setUpsellPick(pick);
        return;
      }
    }
    openCheckoutModal();
  };

  // «Προσθήκη» = ακριβώς σαν πάτημα στον κατάλογο (μεγέθη/υποχρεωτικές επιλογές ανοίγουν τα
  // γνωστά παράθυρα), με την κανονική τιμή. Η γραμμή παίρνει τη σήμανση της πρότασης.
  const handleUpsellAdd = async (product: Product) => {
    upsellTagRef.current = upsellPick ? upsellPick.suggestion.id : null;
    setUpsellPick(null);
    try {
      await handleProductClick(product);
    } finally {
      setUpsellContinue(true);
    }
  };

  const handleUpsellDecline = () => {
    setUpsellPick(null);
    openCheckoutModal();
  };

  useEffect(() => {
    if (!upsellContinue) return;
    if (showSizeModal || showOptionModal || showCustomizeModal || showWeightModal || showOpenPriceModal || quickAdd) return;
    upsellTagRef.current = null; // ακυρώθηκε χωρίς γραμμή: η σήμανση δεν περνά σε επόμενο προϊόν
    setUpsellContinue(false);
    openCheckoutModal();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upsellContinue, showSizeModal, showOptionModal, showCustomizeModal, showWeightModal, showOpenPriceModal, quickAdd]);

  // Get basket product type category (for document type validation)
  const getBasketProductCategory = (): 'products' | 'services' | null => {
    if (basket.length === 0) return null;
    
    const hasServices = basket.some(item => item.product.product_type === 'ΥΠΗΡΕΣΙΑ');
    if (hasServices) return 'services';
    return 'products';
  };

  // Add product to basket with selected size
  const addToBasketWithSize = (product: Product, size: ProductSize, customizations?: {
    removedIngredients?: number[];
    removedIngredientNames?: string[];
    addedExtras?: { raw_material_id: number; name: string; quantity: number; price: number }[];
    selected_options?: SelectedOptionEntry[];
    specialInstructions?: string;
    discount?: number
  }) => {
    // Validate product type compatibility
    const validation = canAddToBasket(product);
    if (!validation.allowed) {
      showToast('warning', validation.message || 'Μη έγκυρη επιλογή');
      return;
    }

    const extrasPrice = customizations?.addedExtras?.reduce((sum, extra) => sum + (extra.price * extra.quantity), 0) || 0;
    const optionsPrice = customizations?.selected_options?.reduce((sum, o) => sum + o.price_modifier, 0) || 0;
    let totalPrice = lineUnitPrice(product, size, 'take_away') + extrasPrice + optionsPrice;

    // Apply discount if provided
    if (customizations?.discount && customizations.discount > 0) {
      totalPrice = totalPrice * (1 - customizations.discount / 100);
    }

    const basketItem: BasketItem = {
      id: `${product.id}-${Date.now()}`,
      product,
      selectedSize: size,
      quantity: 1,
      removedIngredients: customizations?.removedIngredients || [],
      removedIngredientNames: customizations?.removedIngredientNames || [],
      addedExtras: customizations?.addedExtras || [],
      selected_options: customizations?.selected_options?.length ? customizations.selected_options : undefined,
      specialInstructions: customizations?.specialInstructions || '',
      discount: customizations?.discount,
      totalPrice,
      ...takeUpsellTag()
    };

    setBasket(prev => addOrMergeBasketLine(prev, basketItem));
    
    // Trigger add to cart animation
    setLastAddedProduct(product.name);
    setAddedToCartAnimation(true);
    setTimeout(() => setAddedToCartAnimation(false), 1500);
    
    // Reset modal states
    setShowSizeModal(false);
    setShowCustomizeModal(false);
    setSelectedProduct(null);
    setSelectedSize(null);
    setRemovedIngredients([]);
    setAddedExtras([]);
    setSpecialInstructions('');
    setProductDiscount(0);
    setProductOptionGroups([]);
    setPendingSelectedOptions([]);
    setShowOptionModal(false);
    setOptionModalPendingSize(null);
  };

  /** Προϊόν ΧΩΡΙΣ μέγεθος με επιλογές (από το παράθυρο υποχρεωτικών επιλογών ή τη γρήγορη
   *  προσθήκη). false = δεν μπήκε (ασυμβίβαστο είδος — έχει βγει ήδη μήνυμα). */
  const addNoSizeWithOptions = (product: Product, options: SelectedOptionEntry[]): boolean => {
    const validation = canAddToBasket(product);
    if (!validation.allowed) { showToast('warning', validation.message || 'Μη έγκυρη επιλογή'); return false; }
    const optionsPrice = options.reduce((s, o) => s + o.price_modifier, 0);
    const basketItem: BasketItem = {
      id: `${product.id}-${Date.now()}`,
      product,
      selectedSize: null,
      quantity: 1,
      removedIngredients: [],
      addedExtras: [],
      selected_options: options.length ? [...options] : undefined,
      specialInstructions: '',
      totalPrice: lineUnitPrice(product, null, 'take_away') + optionsPrice
    };
    setBasket(prev => addOrMergeBasketLine(prev, basketItem));
    setLastAddedProduct(product.name);
    setAddedToCartAnimation(true);
    setTimeout(() => setAddedToCartAnimation(false), 1500);
    return true;
  };

  // Add product to basket without size. `unitPrice`: η τιμή του barcode (utils/barcodeLine) —
  // ανοιχτή τιμή/ζυγιζόμενο με κανονικό barcode μπαίνουν χωρίς μέγεθος σε 0 €, όπως ως τώρα,
  // ενώ το lineUnitPrice χωρίς μέγεθος θα τα τιμολογούσε στο προεπιλεγμένο.
  const addToBasketWithoutSize = (product: Product, unitPrice?: number) => {
    // Validate product type compatibility
    const validation = canAddToBasket(product);
    if (!validation.allowed) {
      showToast('warning', validation.message || 'Μη έγκυρη επιλογή');
      return;
    }

    const price = unitPrice ?? lineUnitPrice(product, null, 'take_away');

    const basketItem: BasketItem = {
      id: `${product.id}-${Date.now()}`,
      product,
      selectedSize: null, // No size for ΕΜΠΟΡΕΥΜΑ products
      ...takeUpsellTag(),
      quantity: 1,
      removedIngredients: [],
      addedExtras: [],
      specialInstructions: '',
      discount: undefined,
      totalPrice: price
    };

    setBasket(prev => addOrMergeBasketLine(prev, basketItem));
    
    // Trigger add to cart animation
    setLastAddedProduct(product.name);
    setAddedToCartAnimation(true);
    setTimeout(() => setAddedToCartAnimation(false), 1500);
    
    // Reset modal states
    setSelectedProduct(null);
  };

  // Add weight-variable product to basket
  const addWeightVariableToBasket = (product: Product, weight: number) => {
    // Validate product type compatibility
    const validation = canAddToBasket(product);
    if (!validation.allowed) {
      showToast('warning', validation.message || 'Μη έγκυρη επιλογή');
      return;
    }

    // Τιμή κιλού = η τιμή του προεπιλεγμένου μεγέθους, ίδια με το πλακίδιο.
    const pricePerUnit = productDisplayPrice(product, 'take_away');
    const totalPrice = pricePerUnit * weight;
    const weightUnit = product.selling_unit || 'KG'; // Default to KG if not specified
    const unitLabel = weightUnit === 'LT' ? 'lt' : 'kg';
    
    const basketItem: BasketItem = {
      id: `${product.id}-${Date.now()}`,
      product,
      selectedSize: null,
      quantity: 1, // For weight products, quantity is always 1, weight determines amount
      removedIngredients: [],
      addedExtras: [],
      specialInstructions: '',
      discount: undefined,
      totalPrice,
      weight,
      weightUnit,
      ...takeUpsellTag()
    };

    // Πάντα νέα γραμμή (ζυγιζόμενο), στο ΤΡΕΧΟΝ καλάθι — όχι `[...basket, …]` του αντιγράφου (utils/basketOps).
    setBasket(prev => [...prev, basketItem]);

    // Trigger add to cart animation
    setLastAddedProduct(`${product.name} (${weight.toFixed(3)}${unitLabel})`);
    setAddedToCartAnimation(true);
    setTimeout(() => setAddedToCartAnimation(false), 1500);
    
    // Reset modal states
    setShowWeightModal(false);
    setSelectedProduct(null);
    setWeightInput('');
  };

  // «Βάρος + τιμή κιλού», βήμα 1: το βάρος μένει στην άκρη και ανοίγει η τιμή κιλού. Και τα δύο
  // παράθυρα αλλάζουν στο ίδιο render — το effect της πρότασης πώλησης δεν βλέπει κενό ανάμεσα.
  const takeWeightForOpenPrice = (_product: Product, weight: number) => {
    setWeightForOpenPrice(weight);
    setShowWeightModal(false);
    setWeightInput('');
    setOpenPriceInput('');
    setShowOpenPriceModal(true);
  };

  // «Βάρος + τιμή κιλού», βήμα 2: γραμμή ζυγιζόμενου (ποσότητα 1, βάρος, σύνολο = βάρος × τιμή
  // κιλού με μία στρογγυλοποίηση) — ίδια με του addWeightVariableToBasket, με την τιμή του ταμία.
  const addWeightedOpenPriceToBasket = (product: Product, weight: number, pricePerUnit: number) => {
    const validation = canAddToBasket(product);
    if (!validation.allowed) {
      showToast('warning', validation.message || 'Μη έγκυρη επιλογή');
      return;
    }
    const line = weightedOpenPriceLine(product, weight, pricePerUnit);
    if (!line) return;
    const basketItem: BasketItem = { ...line, ...takeUpsellTag() };
    setBasket(prev => [...prev, basketItem]);

    const unitLabel = basketItem.weightUnit === 'LT' ? 'lt' : 'kg';
    setLastAddedProduct(`${product.name} (${weight.toFixed(3)}${unitLabel})`);
    setAddedToCartAnimation(true);
    setTimeout(() => setAddedToCartAnimation(false), 1500);

    setShowOpenPriceModal(false);
    setSelectedProduct(null);
    setOpenPriceInput('');
    setWeightForOpenPrice(null);
  };

  // Add open-price product to basket with manually entered price
  const addOpenPriceToBasket = (product: Product, price: number) => {
    const validation = canAddToBasket(product);
    if (!validation.allowed) {
      showToast('warning', validation.message || 'Μη έγκυρη επιλογή');
      return;
    }

    const defaultSz = defaultSize(product.sizes);

    const basketItem: BasketItem = {
      id: `${product.id}-${Date.now()}`,
      product,
      selectedSize: defaultSz,
      ...takeUpsellTag(),
      quantity: 1,
      removedIngredients: [],
      addedExtras: [],
      specialInstructions: '',
      discount: undefined,
      totalPrice: price
    };

    setBasket(prev => addOrMergeBasketLine(prev, basketItem));

    setLastAddedProduct(`${product.name} (${formatMoney(price)})`);
    setAddedToCartAnimation(true);
    setTimeout(() => setAddedToCartAnimation(false), 1500);

    setShowOpenPriceModal(false);
    setSelectedProduct(null);
    setOpenPriceInput('');
  };

  // Fetch ingredients for a product size (delegated to shared hook)
  const fetchSizeMaterials = async (sizeId: number) => {
    await fetchIngredientsBySize(sizeId);
  };

  const defaultOptionsOf = (groups: PosOptionGroup[]): SelectedOptionEntry[] => {
    const defaults: SelectedOptionEntry[] = [];
    for (const g of groups) {
      const def = g.choices.find(c => c.is_default);
      if (def) defaults.push({ group_id: g.id, group_name: g.name, choice_id: def.id, choice_name: def.name, price_modifier: def.price_modifier });
    }
    return defaults;
  };

  const applyOptionGroups = (groups: PosOptionGroup[]) => {
    setProductOptionGroups(groups);
    // Pre-select defaults
    setPendingSelectedOptions(defaultOptionsOf(groups));
    return groups;
  };

  /** Παράθυρο προϊόντος που κλείνει (με ή χωρίς προσθήκη): οι επιλογές του δεν μένουν για το επόμενο. */
  const forgetPendingOptions = () => {
    setProductOptionGroups([]);
    setPendingSelectedOptions([]);
    setOptionModalPendingSize(null);
  };

  const OPTION_GROUPS_TTL_MS = 5 * 60 * 1000;
  const freshOptionGroups = (productId: number) => {
    const hit = optionGroupsCacheRef.current.get(productId);
    return hit && Date.now() - hit.at < OPTION_GROUPS_TTL_MS ? hit.groups : null;
  };

  /** Μόνο το αίτημα (χωρίς αλλαγή κατάστασης) — το χρησιμοποιεί και η προφόρτωση. */
  const requestOptionGroups = async (productId: number): Promise<PosOptionGroup[] | null> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/products/${productId}/option-groups`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (!res.ok) return null;
      const groups: PosOptionGroup[] = await res.json();
      if (v2) optionGroupsCacheRef.current.set(productId, { at: Date.now(), groups });
      return groups;
    } catch (e) {
      console.error('Failed to fetch option groups:', e);
      return null;
    }
  };

  const fetchProductOptionGroups = async (productId: number) => {
    const cached = v2 ? freshOptionGroups(productId) : null;
    if (cached) return applyOptionGroups(cached);
    const groups = await requestOptionGroups(productId);
    return groups ? applyOptionGroups(groups) : [];
  };

  // Νέα εμφάνιση: προφόρτωση των επιλογών για ό,τι φαίνεται στον κατάλογο (αγαπημένα ή η
  // επιλεγμένη κατηγορία), λίγες κάθε φορά, ώστε το πρώτο πάτημα να μην περιμένει.
  useEffect(() => {
    if (!v2 || products.length === 0) return;
    let cancelled = false;
    const ids = new Set<number>();
    if (selectedCategory) {
      ids.add(selectedCategory);
      categories.find(c => c.id === selectedCategory)?.subcategories?.forEach(sub => ids.add(sub.id));
    }
    const visible = favoritesView || (!selectedCategory && currentView === 'categories')
      ? favoriteIds
      : products.filter(p => (selectedCategory ? ids.has(p.category_id) : true)).map(p => p.id);
    const queue = visible.filter(id => !freshOptionGroups(id)).slice(0, 40);
    const worker = async () => {
      while (!cancelled && queue.length > 0) {
        const id = queue.shift() as number;
        await requestOptionGroups(id);
      }
    };
    void Promise.all([worker(), worker(), worker()]);
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v2, products, favoriteIds, favoritesView, selectedCategory, currentView]);

  // Barcode scan handler
  const handleBarcodeScan = async (code: string) => {
    if (!code.trim() || scanningBarcode) return;
    setScanningBarcode(true);
    setBarcodeError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/products/barcode-lookup?code=${encodeURIComponent(code.trim())}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (!response.ok) {
        const err = await response.json();
        setBarcodeError(err.error || t('barcodeProductNotFound'));
        setTimeout(() => setBarcodeError(''), 3000);
        setScanningBarcode(false);
        return;
      }
      const data = await response.json() as BarcodeLookupData;

      // Μέγεθος και τιμή της γραμμής από το utils/barcodeLine: το προεπιλεγμένο μέγεθος
      // (αποφάσεις 12/F1-7)· ζυγιστικό → ποσά του server ανά κανάλι· ανοιχτή τιμή → χωρίς μέγεθος.
      const line = resolveBarcodeLine({ data, catalog: products, channel: 'take_away' });

      if (line.kind === 'error') {
        // Τέλος χωρίς ποσό state_fees: το ίδιο μήνυμα με το πλακίδιο (απόφαση 33).
        const noPriceKey = isFeeProduct(data.product) ? 'feeAmountMissing' : 'barcodeNoPrice';
        setBarcodeError(t(line.reason === 'no_price' ? noPriceKey : 'barcodeProductUnavailable'));
        setTimeout(() => setBarcodeError(''), 3000);
        setScanningBarcode(false);
        return;
      }

      const product = line.product;

      // Τέλος με barcode: ίδια ροή με το click (απόφαση 1 ποσό, απόφαση 33 χωρίς ποσό → καμία
      // γραμμή), ΠΡΙΝ από ζύγι/αύξηση ποσότητας.
      if (isFeeProduct(product)) {
        addFeeManually(product);
        setScanningBarcode(false);
        return;
      }

      // Το line.kind ακολουθεί το data.scan.type· γράφονται και τα δύο για τον TypeScript.
      if (data.scan.type === 'weighted' && line.kind === 'weighted') {
        // Το μέγεθος (μόνο σε canonical) πάει ως selectedSize: το κατώτατο του server είναι
        // τιμή μεγέθους × βάρος (F1-7). Η τιμή είναι το σύνολο του server, όχι δικός μας υπολογισμός.
        const { weight, total } = line;
        const basketItem: BasketItem = {
          id: `${product.id}-${Date.now()}`,
          product,
          selectedSize: line.size,
          quantity: 1,
          removedIngredients: [],
          addedExtras: [],
          specialInstructions: '',
          discount: undefined,
          totalPrice: total,
          weight,
          weightUnit: 'kg'
        };
        setBasket(prev => [...prev, basketItem]);
        setLastAddedProduct(`${product.name} (${weight.toFixed(3)}kg)`);
        setAddedToCartAnimation(true);
        setTimeout(() => setAddedToCartAnimation(false), 1500);
      } else if (line.kind === 'line') {
        // Κανονικό: ΑΥΣΤΗΡΟΣ κανόνας συγχώνευσης (F1-W4-3, δες utils/barcodeLine) — ποσότητα +1
        // ΜΟΝΟ σε γραμμή ίδιου προϊόντος/μεγέθους, χωρίς επιλογές/σημείωση/έκπτωση και με ίδια
        // τιμή μονάδας· αλλιώς νέα γραμμή, που τα helpers χτίζουν ΑΚΡΙΒΩΣ στην τιμή του barcode.
        const existingItem = findBarcodeMergeLine(basket, line);
        if (existingItem) {
          setBasket(prev => prev.map(item =>
            item.id === existingItem.id ? { ...item, quantity: item.quantity + 1 } : item
          ));
        } else if (line.size) {
          addToBasketWithSize(product, line.size);
        } else {
          addToBasketWithoutSize(product, line.unitPrice);
        }
        setLastAddedProduct(product.name);
        setAddedToCartAnimation(true);
        setTimeout(() => setAddedToCartAnimation(false), 1500);
      }
    } catch (error) {
      setBarcodeError(t('barcodeSearchError'));
      setTimeout(() => setBarcodeError(''), 3000);
    }
    setScanningBarcode(false);
  };

  // Toggle ingredient removal
  const toggleIngredientRemoval = (ingredientId: number) => {
    setRemovedIngredients(prev =>
      prev.includes(ingredientId)
        ? prev.filter(id => id !== ingredientId)
        : [...prev, ingredientId]
    );
  };

  // Update basket item quantity — ΠΑΝΤΑ στο τρέχον καλάθι (utils/basketOps): με το αντίγραφο της
  // ζωγραφιάς, ένα είδος που περίμενε ακόμη στην ουρά (π.χ. μόλις επιβεβαιωμένο μέγεθος) χανόταν.
  const updateQuantity = (itemId: string, delta: number) => {
    setBasket(prev => changeLineQuantity(prev, itemId, delta));
  };

  // Remove item from basket (ίδιος λόγος)
  const removeFromBasket = (itemId: string) => {
    setBasket(prev => removeBasketLine(prev, itemId));
  };

  // Open edit modal for basket item. We open the SAME modal used by the
  // long-press flow (ProductCustomizeModal) so the cashier sees every field
  // they saw when adding the item — including option groups (μέγεθος/ζάχαρη
  // /γάλα). Previously this used a simplified inline modal that hid options,
  // so editing a coffee dropped its sugar/milk/size selections from view.
  const openEditModal = async (item: BasketItem) => {
    setEditingBasketItem(item);
    setSelectedProduct(item.product);
    setSelectedSize(item.selectedSize ?? null);
    setRemovedIngredients(item.removedIngredients);
    setAddedExtras(item.addedExtras.map(e => ({ ...e, raw_material_id: e.raw_material_id || 0 })));
    setSpecialInstructions(item.specialInstructions || '');
    setProductDiscount(item.discount || 0);
    // Seed the previously-selected options so the modal lights up the same
    // choices the cashier picked the first time.
    setPendingSelectedOptions(item.selected_options || []);

    // Fetch option groups + ingredients in parallel (mirrors handleProductLongPress).
    // fetchProductOptionGroups would otherwise overwrite pendingSelectedOptions
    // with the defaults — re-apply the basket item's selections right after.
    await Promise.all([
      fetchProductOptionGroups(item.product.id),
      item.selectedSize ? fetchSizeMaterials(item.selectedSize.id) : Promise.resolve(),
    ]);
    if (item.selected_options && item.selected_options.length > 0) {
      setPendingSelectedOptions(item.selected_options);
    }

    setShowEditModal(true);
  };

  // Γραμμή ανοιχτής τιμής ή ζυγιζόμενου: το ποσό της πριν την επεξεργασία (utils/basketLineEdit).
  // Με προωθητική: το original_unit_price, γιατί το discount είναι τότε το ποσοστό της προωθητικής.
  const lineOwnOf = (item: BasketItem): EditedLineOwn | null => {
    if (!item.product.is_open_price && !item.weight) return null;
    const promo = item.applied_promotion_id != null && item.original_unit_price != null;
    return {
      amount: lineOwnAmount(
        promo ? Number(item.original_unit_price) : item.totalPrice,
        promo ? 0 : item.discount,
        item.addedExtras.reduce((sum, extra) => sum + (extra.price * extra.quantity), 0)
          + (item.selected_options || []).reduce((sum, o) => sum + (o.price_modifier || 0), 0)),
      sizeId: item.selectedSize?.id ?? null,
    };
  };

  // Update basket item after editing.
  // selected_options were missing from this function entirely because the old
  // simplified edit modal didn't expose option groups. Now that the shared
  // ProductCustomizeModal is used, the options round-trip too.
  const updateBasketItem = (options: {
    removedIngredients?: number[];
    removedIngredientNames?: string[];
    addedExtras?: { raw_material_id: number; name: string; quantity: number; price: number }[];
    selected_options?: SelectedOptionEntry[];
    specialInstructions?: string;
    discount?: number;
    newSize?: ProductSize;
  }) => {
    if (!editingBasketItem || !selectedProduct) return;

    const sizeToUse = options.newSize || editingBasketItem.selectedSize;
    const extrasPrice = options.addedExtras?.reduce((sum, extra) => sum + (extra.price * extra.quantity), 0) || 0;
    const optionsPrice = options.selected_options?.reduce((sum, o) => sum + (o.price_modifier || 0), 0) || 0;
    // Ζυγιζόμενο/ανοιχτή τιμή: το ποσό της γραμμής· ζυγιζόμενο με άλλο μέγεθος: κιλό × βάρος.
    let totalPrice = editedLineBasePrice(selectedProduct, sizeToUse, editingBasketItem.weight, 'take_away', lineOwnOf(editingBasketItem)) + extrasPrice + optionsPrice;

    // Apply discount if provided
    if (options.discount && options.discount > 0) {
      totalPrice = totalPrice * (1 - options.discount / 100);
    }

    // Prefer caller-supplied names (the shared modal computes them); fall back
    // to lookup against productIngredients if the caller didn't include them.
    let removedIngredientNames: string[] = options.removedIngredientNames || [];
    if (
      removedIngredientNames.length === 0 &&
      options.removedIngredients && options.removedIngredients.length > 0 &&
      productIngredients.length > 0
    ) {
      removedIngredientNames = productIngredients
        .filter((ing: any) => options.removedIngredients?.includes(ing.raw_material_id || ing.id))
        .map((ing: any) => ing.name);
    }

    // Στο τρέχον καλάθι (utils/basketOps): ό,τι άλλο άλλαξε όσο ήταν ανοιχτό το παράθυρο μένει.
    const editingId = editingBasketItem.id;
    setBasket(prev => patchBasketLine(prev, editingId, {
      selectedSize: sizeToUse,
      removedIngredients: options.removedIngredients || [],
      removedIngredientNames,
      addedExtras: options.addedExtras || [],
      selected_options: options.selected_options?.length ? options.selected_options : undefined,
      specialInstructions: options.specialInstructions || '',
      discount: options.discount,
      totalPrice
    }));

    // Reset states
    setShowEditModal(false);
    setEditingBasketItem(null);
    setSelectedProduct(null);
    setSelectedSize(null);
    setRemovedIngredients([]);
    setAddedExtras([]);
    setSpecialInstructions('');
    setProductDiscount(0);
    setPendingSelectedOptions([]);
    setProductOptionGroups([]);
  };

  // State-fee rows (πλαστική σακούλα κωδ. 8, πλαστικά μιας χρήσης κωδ. 16)
  // are passthrough collections to the Greek state — they DO NOT discount when
  // an order-level discount is applied. Per-product discount on the trigger
  // product only affects that product's price; the auto-added fee row stays
  // at full price by construction (it's a separate basket item).
  const isStateFee = (item: BasketItem) => item.product.fee_category != null;

  const splitSubtotal = () => {
    let revenue = 0;
    let fees = 0;
    for (const it of basket) {
      const line = it.totalPrice * it.quantity;
      if (isStateFee(it)) fees += line;
      else revenue += line;
    }
    return { revenue, fees };
  };

  // Calculate basket total — order-level discount applies to revenue only.
  const calculateTotal = () => {
    const { revenue, fees } = splitSubtotal();

    if (discountType === 'fixed' && fixedDiscountAmount > 0) {
      return Math.max(0, revenue - fixedDiscountAmount) + fees;
    }
    if (discountType === 'percent' && orderDiscount > 0) {
      return revenue * (1 - orderDiscount / 100) + fees;
    }
    return revenue + fees;
  };

  const previousDisplayBasket = useRef<BasketItem[]>([]);
  const activeDisplayItemId = useRef<string | undefined>(undefined);
  const activeDisplayItem = useMemo(() => changedDisplayItem(previousDisplayBasket.current, basket, activeDisplayItemId.current), [basket]);
  useEffect(() => {
    previousDisplayBasket.current = basket;
    activeDisplayItemId.current = activeDisplayItem?.id;
  }, [basket, activeDisplayItem]);
  // Προκαταβολή πελάτη (απόφαση χρήστη 14/10/2026, components/deposits/useTakeAwayDeposit): μόνο με ανοιχτό
  // και έτοιμο διακόπτη, ποτέ σε εκπαιδευτική παραγγελία. Το απόδειξη/τιμολόγιο φεύγει για ΟΛΟ το ποσό
  // (ο server δηλώνει το κομμάτι της προκαταβολής)· ό,τι «πληρώνεται τώρα» — ρέστα, κάρτα/IRIS, οθόνη πελάτη,
  // λωρίδα ολοκλήρωσης — είναι το ΥΠΟΛΟΙΠΟ.
  const orderTotalCents = Math.round(calculateTotal() * 100);
  const deposit = useTakeAwayDeposit({
    storeId: store?.id,
    totalCents: orderTotalCents,
    active: basket.length > 0,
    canAdd: !isTrainingOrder,
    onInvalidated: () => showToast('warning', t('deposits:takeaway.reselect')),
  });
  /** Ό,τι μένει να πληρωθεί τώρα (€): το σύνολο χωρίς την προκαταβολή. */
  const amountDue = deposit.dueCents / 100;
  const hasDeposit = deposit.appliedCents > 0;
  /** Η κάρτα αυτής της παραγγελίας: ό,τι είπε ο server (order.deposit.remaining), αλλιώς το υπόλοιπο εδώ. */
  const cardDue = orderDueCents != null ? orderDueCents / 100 : amountDue;

  // Οθόνη πελάτη: μόνο με τον διακόπτη του καταστήματος (Ρυθμίσεις → «Οθόνη πελάτη», migration 249).
  // Κλειστός: η ενότητα στο γρανάζι δεν εμφανίζεται και τίποτα δεν στέλνεται.
  const customerDisplay = useCustomerDisplay(store?.id, saleDisplayFrame(
    activeDisplayItem, hasDeposit ? amountDue : calculateTotal(), showCheckoutModal || generatingReceipt || generatingInvoice || showPaymentProcessingModal,
    paymentMethod === 'cash' && cashReceived !== '' ? Number(cashReceived) : undefined,
    hasDeposit ? 'ΥΠΟΛΟΙΠΟ' : undefined,
  ), basket.length > 0, store?.customer_display_enabled === true);

  // Calculate VAT — order-level discount ratio applies only to non-fee items.
  const calculateVAT = () => {
    const { revenue } = splitSubtotal();
    const discountedRevenue = (() => {
      if (discountType === 'fixed' && fixedDiscountAmount > 0) {
        return Math.max(0, revenue - fixedDiscountAmount);
      }
      if (discountType === 'percent' && orderDiscount > 0) {
        return revenue * (1 - orderDiscount / 100);
      }
      return revenue;
    })();
    const revenueRatio = revenue > 0 ? discountedRevenue / revenue : 1;
    return basket.reduce((total, item) => {
      const vatRate = item.product.vat / 100;
      const ratio = isStateFee(item) ? 1 : revenueRatio;
      const itemTotal = item.totalPrice * item.quantity * ratio;
      return total + (itemTotal * vatRate / (1 + vatRate));
    }, 0);
  };

  // Apply a promotion: walk the basket and discount matching items. Ίδιος υπολογισμός με πριν, στο
  // ΤΡΕΧΟΝ καλάθι (setBasket(prev => …), utils/basketOps): με το αντίγραφο της ζωγραφιάς χανόταν
  // ό,τι περίμενε ακόμη στην ουρά — π.χ. όταν το PromotionBanner αφαιρεί μόνο του μια προωθητική.
  const promotionAppliedTo = (current: BasketItem[], promo: EligiblePromotion): BasketItem[] => {
    // First, undo any currently applied promotion so we always start from original prices
    let working = current.map(item => {
      if (item.applied_promotion_id && item.original_unit_price != null) {
        return {
          ...item,
          totalPrice: item.original_unit_price,
          discount: 0,
          applied_promotion_id: null,
          applied_promotion_name: null,
          original_unit_price: null
        };
      }
      return item;
    });
    // Map promo lines by product_id for quick lookup. The eligibility computation
    // already capped each line against max_discount_percent.
    const linesByProduct = new Map<number, typeof promo.lines[number]>();
    for (const ln of promo.lines) linesByProduct.set(ln.product_id, ln);

    working = working.map(item => {
      const line = linesByProduct.get(item.product.id);
      if (!line) return item;
      const original = item.totalPrice;
      const newPrice = line.new_unit_price;
      const discountPct = original > 0 ? Math.round(((original - newPrice) / original) * 1000) / 10 : 0;
      return {
        ...item,
        totalPrice: newPrice,
        discount: discountPct,
        applied_promotion_id: promo.promotion_id,
        applied_promotion_name: promo.name,
        original_unit_price: original
      };
    });
    return working;
  };
  const applyPromotion = (promo: EligiblePromotion) => {
    setBasket(prev => promotionAppliedTo(prev, promo));
    setAppliedPromotionId(promo.promotion_id);
  };

  const removePromotion = () => {
    setBasket(prev => prev.map(item => {
      if (!item.applied_promotion_id || item.original_unit_price == null) return item;
      return {
        ...item,
        totalPrice: item.original_unit_price,
        discount: 0,
        applied_promotion_id: null,
        applied_promotion_name: null,
        original_unit_price: null
      };
    }));
    setAppliedPromotionId(null);
  };

  // Build promotion-eligibility input from the current basket. Use original_unit_price
  // when a promo is already applied so the eligibility check sees pre-discount prices.
  const promotionItems = basket.map(item => ({
    product_id: item.product.id,
    quantity: item.quantity,
    unit_price: item.original_unit_price ?? item.totalPrice
  }));

  // Handle checkout
  const handleCheckout = async () => {
    if (basket.length === 0) {
      showToast('warning', 'Το καλάθι είναι άδειο');
      return;
    }

    // Check if ECR is enabled and document type is selected
    if (ecrEnabled && !selectedDocumentType) {
      showToast('warning', 'Διάλεξε τύπο παραστατικού');
      return;
    }

    // Η προκαταβολή καλύπτει όλο το ποσό: κανένας άλλος τρόπος, κανένα τερματικό.
    if (deposit.covered) {
      await processCashPayment('deposit');
      return;
    }

    // For card/IRIS payments
    if (paymentMethod === 'card' || paymentMethod === 'iris') {
      // If ECR is enabled, use POS terminal
      if (ecrEnabled) {
        if (posTerminals.length === 0) {
          showToast('error', 'Δεν έχουν ρυθμιστεί POS τερματικά — ενημέρωσε τον διαχειριστή.');
          return;
        }
        // Νέα εμφάνιση: με ΕΝΑ ενεργό τερματικό δεν ρωτάμε ποιο (απόφαση ιδιοκτήτη 11/10/2026).
        // Ίδια χρέωση με το κουμπί του παραθύρου· η «Δοκιμή ξανά» ανοίγει πάλι το παράθυρο.
        if (v2 && posTerminals.length === 1 && selectedTerminal) {
          await processCardPayment(false);
          return;
        }
        setShowTerminalModal(true);
        return;
      }
      
      // If ECR is disabled: card without a POS terminal
      await processCardWithoutTerminal();
      return;
    }

    // Μετρητά ή «Επί πιστώσει»: ίδια ροή χωρίς τερματικό, με τον τρόπο ΡΗΤΑ. Παλιά ό,τι
    // δεν ήταν κάρτα/IRIS γινόταν σιωπηλά μετρητά (κωδ. 3, συρτάρι).
    if (isNoTerminalMethod(paymentMethod)) {
      await processCashPayment(paymentMethod);
    }
  };
  
  // Card without a POS terminal (ECR disabled). ΟΧΙ το «Επί πιστώσει» (κωδ. 5) —
  // αυτό πάει από το processCashPayment('credit').
  const processCardWithoutTerminal = async () => {
    const orderData = {
      orderType: 'takeaway',
      paymentMethod: 'card', // Still record as card payment
      items: basket,
      total: calculateTotal(),
      subtotal: calculateTotal() - calculateVAT(),
      vat: calculateVAT(),
      customer: {
        name: customer.name || t('customerDefault'),
        notes: customer.notes
      },
      createdBy: employee?.id,
      storeId: store?.id,
      documentTypeId: null, // No fiscal document without ECR
      orderDiscount: discountType === 'percent' && orderDiscount > 0 ? orderDiscount : undefined,
      orderDiscountType: (discountType === 'fixed' && fixedDiscountAmount > 0) ? 'fixed' : (orderDiscount > 0 ? 'percent' : undefined),
      orderDiscountFixed: discountType === 'fixed' && fixedDiscountAmount > 0 ? fixedDiscountAmount : undefined,
      ...deposit.orderFields()
    };

    try {
      setGeneratingReceipt(true);
      
      // Submit order to backend
      const response = await fetch(`${API_BASE_URL}/api/orders/${store?.id}/takeaway`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        deposit.onServerError(error.error || error.code);
        throw new Error(error.message || t('common:errors.createOrder'));
      }

      const result = await response.json();
      
      // Show success
      customerDisplay.complete(amountDue);
      setSuccessOrderCode(result.order.shortCode);
      showOrderSuccess();
      
      // Clear basket and reset
      setBasket([]);
      setCustomer({ name: '', notes: '' });
      setOrderDiscount(0);
      setDiscountType('percent');
      setFixedDiscountAmount(0);
      setPaymentMethod(null);

      // Auto-close success modal after 2 seconds
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 2000);
      
    } catch (error) {
      console.error('Card (no terminal) payment error:', error);
      showToast('error', error instanceof Error ? error.message : 'Η πληρωμή απέτυχε');
    } finally {
      setGeneratingReceipt(false);
    }
  };

  // Μετρητά ή «Επί πιστώσει»: ίδια παραγγελία, ίδιο παραστατικό (11.1/11.2), ίδια ροή χωρίς ECR.
  // Διαφέρουν μόνο ο τρόπος που δηλώνεται και τα ρέστα (μόνο στα μετρητά).
  // 'deposit': η προκαταβολή πελάτη καλύπτει ΟΛΟ το ποσό — απόδειξη με paymentMethod 'deposit', χωρίς συρτάρι.
  const processCashPayment = async (method: NoTerminalReceiptMethod, opts: { afterCardAttempt?: boolean; cashGiven?: number | null } = {}) => {
    // Εκπαίδευση: μόνο μετρητά, όπως πάντα — το «Επί πιστώσει» βγάζει πάντα παραστατικό.
    // Ποτέ μετά από προσπάθεια κάρτας («Μετρητά» στην αποτυχία): εκεί επιχειρήθηκε ήδη πραγματική χρέωση.
    // Ποτέ με προκαταβολή πελάτη: ο server τη δέχεται μόνο σε κανονική παραγγελία.
    const training = !opts.afterCardAttempt && isTrainingOrder && trainingModeEnabled && method === 'cash' && !deposit.selection;
    if (method === 'deposit' && !deposit.covered) {
      showToast('warning', t('deposits:takeaway.notCovered'));
      return;
    }
    const orderData = {
      orderType: 'takeaway',
      paymentMethod: method,
      items: basket,
      total: calculateTotal(),
      subtotal: calculateTotal() - calculateVAT(),
      vat: calculateVAT(),
      customer: {
        name: customer.name || t('customerDefault'),
        notes: customer.notes
      },
      createdBy: employee?.id,
      storeId: store?.id,
      documentTypeId: ecrEnabled && !training ? selectedDocumentType : null,
      isTraining: training,
      orderDiscount: discountType === 'percent' && orderDiscount > 0 ? orderDiscount : undefined,
      orderDiscountType: (discountType === 'fixed' && fixedDiscountAmount > 0) ? 'fixed' : (orderDiscount > 0 ? 'percent' : undefined),
      orderDiscountFixed: discountType === 'fixed' && fixedDiscountAmount > 0 ? fixedDiscountAmount : undefined,
      ...(training ? {} : deposit.orderFields())
    };

    try {
      setGeneratingReceipt(true);
      
      // Submit order to backend
      const response = await fetch(`${API_BASE_URL}/api/orders/${store?.id}/takeaway`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        // Προκαταβολή που δεν ισχύει πια (π.χ. χρησιμοποιήθηκε σε άλλο ταμείο): το μήνυμα του server, καμία παραγγελία.
        deposit.onServerError(error.error || error.code);
        throw new Error(error.message || t('common:errors.createOrder'));
      }

      const result = await response.json();
      const orderId = result.order.id;
      const shortCode = result.order.shortCode;

      // Show success modal — ρέστα στην οθόνη πελάτη μόνο στα μετρητά
      const finish = () => {
        customerDisplay.complete(amountDue, method === 'cash' && cashReceived !== '' ? Number(cashReceived) : undefined);
        setSuccessOrderCode(shortCode);
        showOrderSuccess(opts.cashGiven);
        if (method === 'credit') showToast('success', t('takeaway:creditRecorded'));

        // Clear basket and reset
        resetOrder();
        setIsTrainingOrder(false);

        // Hide modal after 2 seconds
        setTimeout(() => {
          setShowSuccessModal(false);
        }, 2000);
      };

      // If ECR is enabled and not a training order, generate fiscal receipt
      if (ecrEnabled && selectedDocumentType && !training) {
        const doc = await generateCashReceipt(orderId, method);
        if (doc.retry) {
          holdForDocumentRetry({ orderId, shortCode, method, message: doc.message, finish });
          return;
        }
      }
      finish();

    } catch (error) {
      console.error('Error submitting order:', error);
      showToast('error', `Αποτυχία καταχώρησης παραγγελίας: ${error instanceof Error ? error.message : 'άγνωστο σφάλμα'}`);
    } finally {
      setGeneratingReceipt(false);
    }
  };

  // Generate cash receipt (fiscal document) — και για «Επί πιστώσει», με paymentMethod 'credit'.
  // Με προκαταβολή πελάτη: ΙΔΙΑ σύνολα (όλη η παραγγελία)· ο server δηλώνει μόνος του το κομμάτι της
  // προκαταβολής και το υπόλοιπο με τον τρόπο αυτό ('deposit' = υπόλοιπο 0). 409 receipt_documents_pending
  // (το 8.4/8.5 της προκαταβολής δεν έφτασε στον πάροχο) → retry: ο ταμίας ξαναβγάζει την απόδειξη.
  const generateCashReceipt = async (orderId: number, method: NoTerminalReceiptMethod): Promise<ReceiptOutcome> => {
    try {
      // Build order items for fiscal document — include per-item discount info for print
      const orderItems = basket.map(item => {
        const discountPct = item.discount || 0;
        const finalUnitPrice = item.totalPrice; // already discounted per-unit price
        const originalUnitPrice = discountPct > 0
          ? parseFloat((finalUnitPrice / (1 - discountPct / 100)).toFixed(4))
          : finalUnitPrice;
        return {
          productId: item.product.id,
          productName: item.product.name,
          quantity: item.quantity,
          unitPrice: item.totalPrice, // gross per-unit price (backend multiplies by qty)
          displayUnitPrice: originalUnitPrice,
          displayTotalPrice: parseFloat((finalUnitPrice * item.quantity).toFixed(2)),
          vatRate: item.product.vat,
          discountPercent: discountPct,
          discountAmount: parseFloat(((originalUnitPrice - finalUnitPrice) * item.quantity).toFixed(2)),
          weight: item.weight || null,
          weight_unit: item.weightUnit || null,
          // Customisation fields — consumed by printFormatter for the fiscal receipt
          size: item.selectedSize ? { name: item.selectedSize.name } : null,
          added_extras: item.addedExtras || [],
          selected_options: item.selected_options || [],
          removed_ingredients: item.removedIngredients || [],
          special_instructions: item.specialInstructions || null
        };
      });

      // Calculate totals WITH order discount applied (handles both percent and fixed)
      const subtotal = basket.reduce((total, item) => total + (item.totalPrice * item.quantity), 0);
      const discountedTotal = calculateTotal();
      const vatTotal = calculateVAT();
      const netTotal = discountedTotal - vatTotal;

      // Total discount for print (per-item discounts + order-level discount).
      // Order-level discount is computed against the revenue portion only —
      // state fees are passthrough and never discount.
      const perItemDiscountTotal = orderItems.reduce((s, i) => s + (i.discountAmount || 0), 0);
      const revenueSubtotal = splitSubtotal().revenue;
      const orderLevelDiscountAmount = discountType === 'percent' && orderDiscount > 0
        ? revenueSubtotal * orderDiscount / 100
        : (discountType === 'fixed' ? Math.min(fixedDiscountAmount || 0, revenueSubtotal) : 0);
      const totalDiscount = Math.round((perItemDiscountTotal + orderLevelDiscountAmount) * 100) / 100;

      // Get the selected document type's invoice_type_code
      const selectedDoc = documentTypes.find(d => d.id === selectedDocumentType);

      const response = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/fiscal/receipt/cash`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify({
          orderId,
          orderItems,
          totalNetAmount: netTotal,
          totalVatAmount: vatTotal,
          totalGrossAmount: discountedTotal,
          totalDiscount,
          documentTypeId: selectedDocumentType,
          invoiceTypeCode: selectedDoc?.invoice_type_code, // '11.1' or '11.2'
          // Μετρητά: κανένα πεδίο (ίδιο σώμα με πριν)· «Επί πιστώσει»: paymentMethod 'credit' (κωδ. 5)·
          // προκαταβολή που καλύπτει όλο το ποσό: paymentMethod 'deposit'
          ...cashReceiptPaymentField(method)
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.error('Failed to generate fiscal receipt:', error);
        if (isDocumentsPending(response.status, error)) {
          return { ok: false, retry: true, message: error.message || t('deposits:code.receipt_documents_pending') };
        }
        // Don't throw - order was successful, just receipt failed
        return { ok: false, message: error.message };
      }
      await response.json().catch(() => null);
      // Receipt will be printed automatically by Print Agent
      return { ok: true };
    } catch (error) {
      console.error('Error generating fiscal receipt:', error);
      return { ok: false };
    }
  };

  // ---- Παραστατικό που ο server αρνήθηκε προσωρινά (409 receipt_documents_pending) ----
  // Η παραγγελία ΥΠΑΡΧΕΙ ήδη (με την προκαταβολή της): «Ξανά έκδοση» βγάζει την απόδειξη στην ΙΔΙΑ παραγγελία —
  // ποτέ νέα. Το καλάθι μένει όπως είναι (από αυτό χτίζεται η απόδειξη) και δεν γίνεται πρόχειρο.
  const [docRetry, setDocRetry] = useState<{
    orderId: number; shortCode: string; method: NoTerminalReceiptMethod; message?: string; finish: () => void;
  } | null>(null);
  const [docRetryBusy, setDocRetryBusy] = useState(false);
  const holdForDocumentRetry = (hold: { orderId: number; shortCode: string; method: NoTerminalReceiptMethod; message?: string; finish: () => void }) => {
    currentOrderIdRef.current = hold.orderId; // όχι αυτόματο πρόχειρο στην έξοδο: η παραγγελία υπάρχει
    setSuccessOrderCode(hold.shortCode);
    setDocRetry(hold);
  };
  const retryDocument = async () => {
    if (!docRetry || docRetryBusy) return;
    setDocRetryBusy(true);
    try {
      const doc = await generateCashReceipt(docRetry.orderId, docRetry.method);
      if (doc.retry) {
        setDocRetry({ ...docRetry, message: doc.message });
        return;
      }
      const { finish } = docRetry;
      setDocRetry(null);
      finish();
    } finally {
      setDocRetryBusy(false);
    }
  };
  // Δεν λύνεται τώρα: η παραγγελία μένει για τον υπεύθυνο (όπως η αμφίβολη κάρτα) και το ταμείο ελευθερώνεται.
  const leaveDocumentForManager = () => {
    const code = docRetry?.shortCode || successOrderCode;
    setDocRetry(null);
    setShowPaymentProcessingModal(false);
    resetOrder();
    setIsPaymentRetry(false);
    setIsTrainingOrder(false);
    showToast('warning', t('deposits:takeaway.docLeft', { code }));
  };

  // Lookup VAT number using AADE API (via shared hook)
  const lookupVatNumber = async () => {
    if (store?.id) {
      const found = await vatLookup(store.id);
      if (found) setManualInvoiceEntry(false);
    }
  };

  // Generate invoice (fiscal document with customer details)
  const generateInvoice = async () => {
    if (!invoiceCustomer) {
      setVatLookupError('Please lookup customer details first');
      return;
    }

    // Η προκαταβολή καλύπτει όλο το ποσό: τρόπος 'deposit' στην παραγγελία, «Επί πιστώσει» (5) στο τιμολόγιο —
    // τους τρόπους τους βγάζει ο server από την προκαταβολή.
    const covered = deposit.covered;
    if (!covered && !paymentMethod) return;

    setGeneratingInvoice(true);

    try {
      // Create order first — ή η ΙΔΙΑ παραγγελία, αν το τιμολόγιο της προηγούμενης προσπάθειας δεν εκδόθηκε.
      let held = invoiceOrderRef.current;
      if (!held) {
        const orderData = {
          orderType: 'takeaway',
          paymentMethod: covered ? 'deposit' : paymentMethod,
          items: basket,
          total: calculateTotal(),
          subtotal: calculateTotal() - calculateVAT(),
          vat: calculateVAT(),
          customer: {
            name: invoiceCustomer.name,
            notes: `Invoice for VAT: ${invoiceCustomer.vat_number}`
          },
          createdBy: employee?.id,
          storeId: store?.id,
          documentTypeId: ecrEnabled ? selectedDocumentType : null,
          orderDiscount: discountType === 'percent' && orderDiscount > 0 ? orderDiscount : undefined,
          orderDiscountType: (discountType === 'fixed' && fixedDiscountAmount > 0) ? 'fixed' : (orderDiscount > 0 ? 'percent' : undefined),
          orderDiscountFixed: discountType === 'fixed' && fixedDiscountAmount > 0 ? fixedDiscountAmount : undefined,
          ...deposit.orderFields()
        };

        const orderResponse = await fetch(`${API_BASE_URL}/api/orders/${store?.id}/takeaway`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
          },
          body: JSON.stringify(orderData)
        });

        if (!orderResponse.ok) {
          // Το μήνυμα του server (π.χ. προκαταβολή που δεν ισχύει πια), όχι γενικό «αποτυχία».
          const error = await orderResponse.json().catch(() => ({}));
          deposit.onServerError(error.error || error.code);
          throw new Error(error.message || t('common:errors.createOrder'));
        }

        const orderResult = await orderResponse.json();
        held = {
          orderId: orderResult.order?.id || orderResult.id,
          // Το παράθυρο βάζει ήδη «#» μπροστά· η απάντηση έχει shortCode (όχι
          // order_code), οπότε έπεφτε στο `#${id}` και έβγαινε «##151234».
          shortCode: orderResult.order?.shortCode || orderResult.order?.short_code || String(orderResult.order?.id || orderResult.id),
        };
      }
      const orderId = held.orderId;

      // Build order items for fiscal document — include per-item discount info for print
      const orderItems = basket.map(item => {
        const discountPct = item.discount || 0;
        const finalUnitPrice = item.totalPrice;
        const originalUnitPrice = discountPct > 0
          ? parseFloat((finalUnitPrice / (1 - discountPct / 100)).toFixed(4))
          : finalUnitPrice;
        return {
          productId: item.product.id,
          productName: item.product.name,
          quantity: item.quantity,
          unitPrice: item.totalPrice,
          displayUnitPrice: originalUnitPrice,
          displayTotalPrice: parseFloat((finalUnitPrice * item.quantity).toFixed(2)),
          vatRate: item.product.vat,
          discountPercent: discountPct,
          discountAmount: parseFloat(((originalUnitPrice - finalUnitPrice) * item.quantity).toFixed(2)),
          weight: item.weight || null,
          weight_unit: item.weightUnit || null,
          // Customisation fields — consumed by printFormatter for the fiscal receipt
          size: item.selectedSize ? { name: item.selectedSize.name } : null,
          added_extras: item.addedExtras || [],
          selected_options: item.selected_options || [],
          removed_ingredients: item.removedIngredients || [],
          special_instructions: item.specialInstructions || null
        };
      });

      // Calculate totals WITH order discount applied (handles both percent and fixed)
      const subtotal = basket.reduce((total, item) => total + (item.totalPrice * item.quantity), 0);
      const discountedTotal = calculateTotal();
      const vatTotal = calculateVAT();
      const netTotal = discountedTotal - vatTotal;

      // Total discount for print — order-level discount is on revenue only.
      const perItemDiscountTotal = orderItems.reduce((s, i) => s + (i.discountAmount || 0), 0);
      const revenueSubtotal = splitSubtotal().revenue;
      const orderLevelDiscountAmount = discountType === 'percent' && orderDiscount > 0
        ? revenueSubtotal * orderDiscount / 100
        : (discountType === 'fixed' ? Math.min(fixedDiscountAmount || 0, revenueSubtotal) : 0);
      const totalDiscount = Math.round((perItemDiscountTotal + orderLevelDiscountAmount) * 100) / 100;

      // Get the selected document type's invoice_type_code
      const selectedDoc = documentTypes.find(d => d.id === selectedDocumentType);

      const invoiceResponse = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/fiscal/invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify({
          orderId,
          orderItems,
          totalNetAmount: netTotal,
          totalVatAmount: vatTotal,
          totalGrossAmount: discountedTotal,
          totalDiscount,
          documentTypeId: selectedDocumentType,
          invoiceTypeCode: selectedDoc?.invoice_type_code, // '1.1' or '2.1'
          // 3 μετρητά · 5 «Επί πιστώσει» — και 5 όταν η προκαταβολή καλύπτει όλο το ποσό
          paymentMethodId: covered ? 5 : invoicePaymentMethodId(paymentMethod),
          customer: invoiceCustomer
        })
      });

      if (!invoiceResponse.ok) {
        const error = await invoiceResponse.json().catch(() => ({}));
        // Η παραγγελία καταχωρήθηκε: με προκαταβολή (ή όταν ο server ζητά «δοκίμασε σε λίγο») το ξαναπάτημα
        // βγάζει το τιμολόγιο στην ΙΔΙΑ παραγγελία.
        if (deposit.selection || isDocumentsPending(invoiceResponse.status, error)) {
          invoiceOrderRef.current = held;
          currentOrderIdRef.current = held.orderId; // όχι αυτόματο πρόχειρο στην έξοδο
        }
        throw new Error(error.message || t('common:errors.invoice'));
      }

      await invoiceResponse.json().catch(() => null);
      invoiceOrderRef.current = null;

      // Invoice will be printed automatically by Print Agent

      // Close modal and show success — ίδιος καθαρισμός με τις άλλες πληρωμές.
      // Πριν έμεναν ο τρόπος πληρωμής και οι Σημειώσεις για τον ΕΠΟΜΕΝΟ πελάτη.
      setShowInvoiceModal(false);
      setInvoiceVatNumber('');
      setInvoiceCustomer(null);
      customerDisplay.complete(amountDue, paymentMethod === 'cash' && cashReceived !== '' ? Number(cashReceived) : undefined);
      setBasket([]);
      setCustomer({ name: '', notes: '' });
      setPaymentMethod(null);
      setOrderDiscount(0);
      setDiscountType('percent');
      setFixedDiscountAmount(0);
      currentOrderIdRef.current = null;
      setSuccessOrderCode(held.shortCode);
      showOrderSuccess();
      if (!covered && paymentMethod === 'credit') showToast('success', t('takeaway:creditRecorded'));

      // Auto-close success modal after 3 seconds
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 3000);

    } catch (error: any) {
      console.error('Invoice generation error:', error);
      setVatLookupError(error.message || t('common:errors.invoice'));
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Κλείσιμο του τιμολογίου («×» / «Άκυρο»). Αν η παραγγελία καταχωρήθηκε αλλά το τιμολόγιο δεν βγήκε, δεν
  // ξαναχτυπιέται ως νέα: μένει για τον υπεύθυνο (όπως η αμφίβολη κάρτα) και το ταμείο ελευθερώνεται.
  const closeInvoiceModal = () => {
    const held = invoiceOrderRef.current;
    setShowInvoiceModal(false);
    setInvoiceVatNumber('');
    setInvoiceCustomer(null);
    setVatLookupError(null);
    setInvoiceKb(null);
    setManualInvoiceEntry(false);
    if (held) {
      resetOrder();
      showToast('warning', t('deposits:takeaway.docLeft', { code: held.shortCode }));
    }
  };

  // Check if selected document type is an invoice type (1.1 or 2.1)
  const isInvoiceDocumentType = () => {
    const selectedDoc = documentTypes.find(d => d.id === selectedDocumentType);
    return selectedDoc?.invoice_type_code === '1.1' || selectedDoc?.invoice_type_code === '2.1';
  };

  // Πληρωμή με κάρτα/IRIS στο τερματικό POS.
  //
  // ΜΙΑ παραγγελία ανά πληρωμή (αποφάσεις ιδιοκτήτη 12/10/2026): η παραγγελία δημιουργείται στην ΠΡΩΤΗ
  // προσπάθεια και κάθε επόμενη πάει στην ΙΔΙΑ — ποτέ δεύτερη παραγγελία για το ίδιο καλάθι. Ό,τι δεν
  // είναι καθαρή έγκριση (απόρριψη, 409, 500, 504 του nginx, σφάλμα δικτύου) δεν το κρίνει το ταμείο:
  // ρωτά τον server (checkCardOutcome), που ξέρει αν τρέχει ακόμα η χρέωση και τι λέει ο πάροχος.
  // Το isRetry μένει για συμβατότητα με τους καλούντες· την επανάληψη την κρίνει το currentOrderId.
  const processCardPayment = async (_isRetry: boolean = false) => {
    if (!selectedTerminal) {
      showToast('warning', 'Διάλεξε POS τερματικό');
      return;
    }
    // Διπλό πάτημα στο «Πληρωμή» (οθόνη αφής): μία χρέωση τη φορά. Ο server αρνείται κι αυτός (409).
    if (cardBusyRef.current) return;
    cardBusyRef.current = true;

    setShowTerminalModal(false);
    setShowPaymentProcessingModal(true);
    setPaymentStatus('processing');
    setCardFailureCancelOnly(false);
    setPaymentMessage('Έναρξη πληρωμής...');

    let orderId = currentOrderId;

    try {
      if (!orderId) {
        const orderData = {
          orderType: 'takeaway',
          paymentMethod: paymentMethod === 'iris' ? 'iris' : 'card',
          items: basket,
          total: calculateTotal(),
          subtotal: calculateTotal() - calculateVAT(),
          vat: calculateVAT(),
          customer: {
            name: customer.name || t('customerDefault'),
            notes: customer.notes
          },
          createdBy: employee?.id,
          storeId: store?.id,
          documentTypeId: ecrEnabled ? selectedDocumentType : null,
          orderDiscount: discountType === 'percent' && orderDiscount > 0 ? orderDiscount : undefined,
          orderDiscountType: (discountType === 'fixed' && fixedDiscountAmount > 0) ? 'fixed' : (orderDiscount > 0 ? 'percent' : undefined),
          orderDiscountFixed: discountType === 'fixed' && fixedDiscountAmount > 0 ? fixedDiscountAmount : undefined,
          ...deposit.orderFields()
        };

        setPaymentMessage('Δημιουργία παραγγελίας...');
        const orderResponse = await fetch(`${API_BASE_URL}/api/orders/${store?.id}/takeaway`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
          },
          body: JSON.stringify(orderData)
        });

        if (!orderResponse.ok) {
          const error = await orderResponse.json().catch(() => ({}));
          // Η προκαταβολή δεν ισχύει: καμία παραγγελία, καμία χρέωση — πίσω στο καλάθι με το μήνυμα του server
          // (το «Ξανά κάρτα» θα χρέωνε αλλιώς ΟΛΟ το ποσό, χωρίς την προκαταβολή που ζήτησε ο πελάτης).
          if (deposit.selection && deposit.onServerError(error.error || error.code)) {
            closeCardModalKeepBasket();
            showToast('error', error.message || t('common:errors.createOrder'));
            return;
          }
          throw new Error(error.message || t('common:errors.createOrder'));
        }

        const orderResult = await orderResponse.json();
        orderId = orderResult.order.id as number;
        // Με προκαταβολή η κάρτα χρεώνει ΜΟΝΟ το υπόλοιπο που λέει ο server.
        const due = orderResult.order?.deposit ? toCents(orderResult.order.deposit.remaining) : Math.round(calculateTotal() * 100);
        orderDueRef.current = due;
        setOrderDueCents(due);
        currentOrderIdRef.current = orderId;
        setCurrentOrderId(orderId);
        setSuccessOrderCode(orderResult.order.shortCode);
      } else {
        setPaymentMessage('Νέα προσπάθεια στην ίδια παραγγελία...');
      }

      // Now process POS payment
      const isIris = paymentMethod === 'iris';
      setPaymentMessage(isIris ? 'Αναμονή πληρωμής IRIS...\nΣκάναρε το QR στο τερματικό' : 'Αναμονή πληρωμής κάρτας...\nΠέρασε την κάρτα στο τερματικό');

      // Καθαρή αξία/ΦΠΑ: ΟΛΗ η παραγγελία (ο server υπογράφει το σύνολο του παραστατικού)· ποσό χρέωσης: το υπόλοιπο.
      const discountedTotal = calculateTotal();
      const vatTotal = calculateVAT();
      const netTotal = discountedTotal - vatTotal;
      const chargeCents = orderDueRef.current ?? Math.round(discountedTotal * 100);

      const paymentResponse = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/pos/payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify({
          terminalId: selectedTerminal.id,
          orderId,
          amount: chargeCents, // Convert to cents
          netAmount: Math.round(netTotal * 100),
          vatAmount: Math.round(vatTotal * 100),
          currency: 'EUR',
          documentTypeId: ecrEnabled ? selectedDocumentType : null,
          requireSignature: ecrEnabled,
          isRetry: _isRetry,
          isIris // IRIS payment flag for terminal
        })
      });

      // Η χρέωση κρατά ως ~190″· το nginx μπορεί να κόψει νωρίτερα με σελίδα HTML (504/502), όχι JSON.
      let paymentData: any = null;
      try {
        paymentData = await paymentResponse.json();
      } catch {
        paymentData = null;
      }

      if (paymentResponse.ok && paymentData?.success) {
        await completeApprovedCard(orderId, paymentData);
        return;
      }

      // Η υπογραφή Oxygen μιας προσπάθειας που δεν πέρασε ακυρώνεται μόνο όταν ο server πει «δεν χρεώθηκε».
      pendingSignatureIdRef.current = paymentData?.signatureData?.signatureId ?? pendingSignatureIdRef.current;
      const failureMessage = paymentData
        ? (paymentData.message || paymentData.error || 'Η κάρτα απορρίφθηκε')
        : ((paymentResponse.status === 504 || paymentResponse.status === 502 || paymentResponse.status === 408)
          ? 'Έληξε ο χρόνος αναμονής του διακομιστή.'
          : `Σφάλμα επικοινωνίας με τον διακομιστή (${paymentResponse.status}).`);
      await checkCardOutcome(orderId, { failureMessage, code: paymentData?.code || paymentData?.error || null });
    } catch (error) {
      console.error('Error processing card payment:', error);
      if (orderId) {
        // Σφάλμα δικτύου ΜΕΤΑ τη δημιουργία της παραγγελίας: η χρέωση μπορεί να ξεκίνησε.
        await checkCardOutcome(orderId, { failureMessage: error instanceof Error ? error.message : null });
      } else {
        // Η παραγγελία δεν δημιουργήθηκε — καμία χρέωση δεν ξεκίνησε.
        setPaymentStatus('failed');
        setPaymentMessage(error instanceof Error ? error.message : 'Η πληρωμή απέτυχε');
      }
    } finally {
      cardBusyRef.current = false;
    }
  };

  // Η κάρτα ΕΓΚΡΙΘΗΚΕ (απευθείας ή μετά από έλεγχο): απόθεμα, παραστατικό, «Επιτυχής πληρωμή».
  // Ίδια συνέχεια και στις δύο περιπτώσεις — ο server έχει ήδη εκδώσει την απόδειξη και η κλήση του
  // browser βγαίνει διπλότυπη στο success-dedup.
  const completeApprovedCard = async (orderId: number, paymentData: any) => {
    pendingSignatureIdRef.current = null;
    customerDisplay.complete(orderDueRef.current != null ? orderDueRef.current / 100 : amountDue);
    setPaymentStatus('success');
    setPaymentMessage('Επιτυχής πληρωμή!');

    // Deduct stock after successful card payment
    try {
      setPaymentMessage('Updating inventory...');
      await fetch(`${API_BASE_URL}/api/orders/${store?.id}/orders/${orderId}/deduct-stock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });
    } catch (stockError) {
      console.error('⚠️ Failed to deduct stock:', stockError);
      // Continue even if stock deduction fails - can be adjusted manually
    }

    // Generate fiscal document for card payment (receipt or invoice)
    if (ecrEnabled && selectedDocumentType && orderId) {
      if (isInvoiceDocumentType() && invoiceCustomer) {
        setPaymentMessage('Generating invoice...');
        await generateCardInvoice(orderId, paymentData);
      } else {
        setPaymentMessage('Generating receipt...');
        await generateCardReceipt(orderId, paymentData);
      }
    }

    // Clear basket after short delay
    setTimeout(() => {
      setShowPaymentProcessingModal(false);
      showOrderSuccess(); // κλασική: παράθυρο 2″· νέα εμφάνιση: λωρίδα «Παραγγελία #… ολοκληρώθηκε»
      resetOrder();
      setIsPaymentRetry(false);

      setTimeout(() => {
        setShowSuccessModal(false);
      }, 2000);
    }, 1500);
  };

  // Η υπογραφή Oxygen της προσπάθειας που δεν πέρασε. Μόνο όταν είναι ΣΙΓΟΥΡΟ ότι η κάρτα δεν χρεώθηκε
  // (μια υπογραφή που μπορεί να χρησιμοποίησε το τερματικό δεν ακυρώνεται). Best-effort, όπως πριν.
  const cancelUnusedSignature = async () => {
    const signatureId = pendingSignatureIdRef.current;
    pendingSignatureIdRef.current = null;
    if (!signatureId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/fiscal/signature/${signatureId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify({ reason: 'payment_cancelled_by_user' })
      });
      if (!response.ok) console.error('Failed to cancel signature:', response.status);
    } catch (error) {
      console.error('Error cancelling signature:', error);
    }
  };

  // Έξοδος από το παράθυρο πληρωμής ΧΩΡΙΣ πληρωμή: το καλάθι μένει (για αλλαγή ή άδειασμα).
  const closeCardModalKeepBasket = () => {
    setShowPaymentProcessingModal(false);
    setShowTerminalModal(false);
    setPaymentStatus('idle');
    setPaymentMessage('');
    setIsPaymentRetry(false);
    setCardFailureCancelOnly(false);
    currentOrderIdRef.current = null;
    setCurrentOrderId(null);
    orderDueRef.current = null;
    setOrderDueCents(null);
  };

  // «Τι έγινε με την κάρτα;» — ρωτά τον server (και αυτός τον πάροχο για ό,τι είναι αμφίβολο).
  // Όσο η χρέωση τρέχει ακόμα (π.χ. το nginx έκοψε στα 60″ ενώ το τερματικό περιμένει), ξαναρωτά μόνο του.
  const checkCardOutcome = async (orderId: number, opts: { failureMessage?: string | null; code?: string | null } = {}) => {
    setPaymentStatus('checking');
    setPaymentMessage(t('takeaway:cardChecking'));
    for (let attempt = 0; attempt < CARD_CHECK_MAX_POLLS; attempt++) {
      if (!mountedRef.current) return;
      const r = await requestCardCheck(store?.id ?? '', orderId);
      const data = r.data;
      if (!r.ok || !data || !isCardCheckState(data.state)) {
        setPaymentStatus('uncertain');
        setPaymentMessage(data?.message || t('takeaway:cardCheckUnreachable'));
        return;
      }
      if (data.state === 'in_flight') {
        setPaymentMessage(t('takeaway:cardStillWaiting'));
        await sleep(CARD_CHECK_POLL_MS);
        continue;
      }
      if (data.state === 'approved') {
        await completeApprovedCard(orderId, data.payment || { success: true, transactionId: null });
        return;
      }
      if (data.state === 'not_charged') {
        await cancelUnusedSignature();
        setCardFailureCancelOnly(opts.code === 'amount_mismatch');
        setPaymentStatus('failed');
        setPaymentMessage(opts.failureMessage || data.reason || t('takeaway:cardNotCharged'));
        return;
      }
      if (data.state === 'order_cancelled') {
        closeCardModalKeepBasket();
        showToast('warning', data.message || t('takeaway:cardOrderCancelled'));
        return;
      }
      if (data.state === 'order_closed') {
        setShowPaymentProcessingModal(false);
        resetOrder();
        setIsPaymentRetry(false);
        showToast('warning', data.message || t('takeaway:cardOrderClosed'));
        return;
      }
      // uncertain
      setPaymentStatus('uncertain');
      setPaymentMessage(data.message || t('takeaway:cardUncertainHint'));
      return;
    }
    setPaymentStatus('uncertain');
    setPaymentMessage(t('takeaway:cardStillWaiting'));
  };

  // Ο server αρνήθηκε «Μετρητά»/«Ακύρωση»: μάθε τι ισχύει, μην το μαντέψεις.
  const handleCardRecoveryRefusal = async (orderId: number, r: ApiResult) => {
    const code: string | undefined = r.data?.code || r.data?.error;
    if (mustCheckFirst(code)) {
      await checkCardOutcome(orderId);
      return;
    }
    if (code === 'order_cancelled') {
      closeCardModalKeepBasket();
      showToast('warning', r.data?.message || t('takeaway:cardOrderCancelled'));
      return;
    }
    if (code === 'order_not_card') {
      setShowPaymentProcessingModal(false);
      resetOrder();
      setIsPaymentRetry(false);
      showToast('warning', r.data?.message || t('takeaway:cardOrderClosed'));
      return;
    }
    // Παραστατικό που υπάρχει ήδη: δεν το λύνει ο ταμίας.
    setPaymentStatus(code === 'order_has_document' ? 'uncertain' : 'failed');
    if (code === 'amount_mismatch') setCardFailureCancelOnly(true);
    setPaymentMessage(r.data?.message || t('takeaway:cardActionFailed'));
  };

  // «Ξανά κάρτα»: νέα χρέωση στην ΙΔΙΑ παραγγελία. Νέα εμφάνιση με ΕΝΑ τερματικό: κατευθείαν, όπως η
  // πληρωμή με ένα πάτημα (απόφαση 11/10/2026)· αλλιώς το παράθυρο τερματικού όπως πάντα.
  const retryFailedCard = () => {
    if (v2 && posTerminals.length === 1 && selectedTerminal) {
      void processCardPayment(true);
      return;
    }
    setPaymentStatus('idle');
    setShowPaymentProcessingModal(false);
    setIsPaymentRetry(true);
    setShowTerminalModal(true);
  };

  // Παράθυρο τερματικού → «Άκυρο». Με παραγγελία που περιμένει πληρωμή επιστρέφει στις επιλογές της:
  // ως τώρα έκλεινε σκέτο, το καλάθι άλλαζε και η επανάληψη χρέωνε τη νέα τιμή στην ΠΑΛΙΑ παραγγελία.
  const closeTerminalModal = () => {
    setShowTerminalModal(false);
    if (currentOrderId) {
      setShowPaymentProcessingModal(true);
      setPaymentStatus('failed');
    }
  };

  // «Μετρητά» στην ΙΔΙΑ παραγγελία: ο server αλλάζει τον τρόπο πληρωμής μόνο αν η κάρτα ΣΙΓΟΥΡΑ δεν
  // χρεώθηκε, και μετά βγαίνει η απόδειξη μετρητών όπως σε κάθε πώληση μετρητών. Χωρίς το κράτημα 2″ της
  // εκπαίδευσης: εδώ έχει ήδη επιχειρηθεί πραγματική χρέωση.
  // Νέα εμφάνιση: πρώτα το παράθυρο ρέστων (ίδιο με κάθε πληρωμή μετρητών)· η «Πληρωμή» του ολοκληρώνει.
  const payFailedCardWithCash = async () => {
    if (cardBusyRef.current) return;
    if (v2) {
      setCashReceived('');
      setShowPaymentProcessingModal(false);
      setShowCardCashSheet(true);
      return;
    }
    await settleFailedCardWithCash(null);
  };

  // Παράθυρο ρέστων μετά από κάρτα → «Πληρωμή».
  const payCardCashSheet = () => {
    const given = cashReceived !== '' ? Number(cashReceived) : null;
    setShowCardCashSheet(false);
    setShowPaymentProcessingModal(true);
    void settleFailedCardWithCash(given);
  };

  // Παράθυρο ρέστων μετά από κάρτα → κλείσιμο: πίσω στις τρεις επιλογές, η παραγγελία περιμένει ακόμα.
  const closeCardCashSheet = () => {
    setShowCardCashSheet(false);
    setCashReceived('');
    setShowPaymentProcessingModal(true);
  };

  /** @param cashGiven «Δόθηκαν» του παραθύρου ρέστων (νέα εμφάνιση)· null = χωρίς ποσό / κλασική. */
  const settleFailedCardWithCash = async (cashGiven: number | null) => {
    if (cardBusyRef.current) return;
    const orderId = currentOrderId;
    if (!orderId) {
      // Δεν δημιουργήθηκε παραγγελία (απέτυχε πριν): κανονική πώληση μετρητών — ποτέ εκπαίδευση.
      setShowPaymentProcessingModal(false);
      setPaymentStatus('idle');
      await processCashPayment('cash', { afterCardAttempt: true, cashGiven });
      return;
    }
    cardBusyRef.current = true;
    setCardActionBusy(true);
    // Ο server συγκρίνει με το ΣΥΝΟΛΟ της παραγγελίας (και με προκαταβολή)· τα ρέστα βγαίνουν από το υπόλοιπο.
    const total = calculateTotal();
    const due = orderDueRef.current != null ? orderDueRef.current / 100 : amountDue;
    try {
      setPaymentMessage(t('takeaway:cardSwitchingToCash'));
      const r = await requestCardToCash(store?.id ?? '', orderId, total);
      if (!r.ok) {
        await handleCardRecoveryRefusal(orderId, r);
        return;
      }
      await cancelUnusedSignature();
      const finish = () => {
        // Ρέστα στην οθόνη πελάτη όπως σε κάθε πώληση μετρητών.
        if (cashGiven !== null) customerDisplay.complete(due, cashGiven);
        else customerDisplay.complete(due);
        setShowPaymentProcessingModal(false);
        showOrderSuccess(cashGiven); // κλασική: παράθυρο 2″· νέα εμφάνιση: λωρίδα με τα ρέστα
        showToast('success', t('takeaway:cardPaidCash'));
        resetOrder();
        setIsPaymentRetry(false);
        setTimeout(() => {
          setShowSuccessModal(false);
        }, 2000);
      };
      if (ecrEnabled && selectedDocumentType) {
        if (isInvoiceDocumentType() && invoiceCustomer) await generateCashInvoiceForOrder(orderId);
        else {
          const doc = await generateCashReceipt(orderId, 'cash');
          if (doc.retry) {
            setShowPaymentProcessingModal(false);
            holdForDocumentRetry({ orderId, shortCode: successOrderCode, method: 'cash', message: doc.message, finish });
            return;
          }
        }
      }
      finish();
    } finally {
      cardBusyRef.current = false;
      setCardActionBusy(false);
    }
  };

  // «Ακύρωση»: ακυρώνει την παραγγελία (μόνο ο ταμίας που την έκανε, μέσα σε 15′, χωρίς χρέωση/παραστατικό).
  // Το καλάθι μένει: ο πελάτης μπορεί να αλλάξει κάτι και να ξαναπληρώσει ως νέα παραγγελία.
  const cancelFailedCardOrder = async () => {
    if (cardBusyRef.current) return;
    const orderId = currentOrderId;
    if (!orderId) {
      closeCardModalKeepBasket();
      return;
    }
    cardBusyRef.current = true;
    setCardActionBusy(true);
    try {
      setPaymentMessage(t('takeaway:cardCancelling'));
      const r = await requestCardCancel(store?.id ?? '', orderId);
      if (!r.ok) {
        await handleCardRecoveryRefusal(orderId, r);
        return;
      }
      await cancelUnusedSignature();
      closeCardModalKeepBasket();
      showToast('success', t('takeaway:cardOrderCancelled'));
    } finally {
      cardBusyRef.current = false;
      setCardActionBusy(false);
    }
  };

  // «Έλεγχος ξανά» στο κλείδωμα της αμφίβολης χρέωσης.
  const checkCardAgain = async () => {
    const orderId = currentOrderId;
    if (cardBusyRef.current || !orderId) return;
    cardBusyRef.current = true;
    setCardActionBusy(true);
    try {
      await checkCardOutcome(orderId);
    } finally {
      cardBusyRef.current = false;
      setCardActionBusy(false);
    }
  };

  // Αμφίβολη χρέωση που δεν λύνεται τώρα: η παραγγελία μένει ΟΠΩΣ είναι (για τον υπεύθυνο) και το ταμείο
  // ελευθερώνεται για τον επόμενο πελάτη. Καμία χρέωση, κανένα μετρητό, κανένα πρόχειρο για ξαναχτύπημα.
  const leaveUncertainForManager = () => {
    const code = successOrderCode;
    pendingSignatureIdRef.current = null;
    setShowPaymentProcessingModal(false);
    resetOrder();
    setIsPaymentRetry(false);
    showToast('warning', t('takeaway:cardLeftForManager', { code }));
  };

  // Τιμολόγιο μετρητών (κωδ. 3) για παραγγελία κάρτας που πληρώθηκε τελικά με μετρητά — ίδιο σώμα με το
  // τιμολόγιο της κάρτας, με τρόπο πληρωμής μετρητά.
  const generateCashInvoiceForOrder = async (orderId: number) => {
    const currentCustomer = invoiceCustomer;
    if (!currentCustomer) return;
    try {
      const orderItems = basket.map(item => {
        const discountPct = item.discount || 0;
        const finalUnitPrice = item.totalPrice;
        const originalUnitPrice = discountPct > 0
          ? parseFloat((finalUnitPrice / (1 - discountPct / 100)).toFixed(4))
          : finalUnitPrice;
        return {
          productId: item.product.id,
          productName: item.product.name,
          quantity: item.quantity,
          unitPrice: item.totalPrice,
          displayUnitPrice: originalUnitPrice,
          displayTotalPrice: parseFloat((finalUnitPrice * item.quantity).toFixed(2)),
          vatRate: item.product.vat,
          discountPercent: discountPct,
          discountAmount: parseFloat(((originalUnitPrice - finalUnitPrice) * item.quantity).toFixed(2)),
          weight: item.weight || null,
          weight_unit: item.weightUnit || null
        };
      });
      const discountedTotal = calculateTotal();
      const vatTotal = calculateVAT();
      const perItemDiscountTotal = orderItems.reduce((s, i) => s + (i.discountAmount || 0), 0);
      const revenueSubtotal = basket.reduce((sum, it) => sum + (isStateFee(it) ? 0 : it.totalPrice * it.quantity), 0);
      const orderLevelDiscountAmount = discountType === 'percent' && orderDiscount > 0
        ? revenueSubtotal * orderDiscount / 100
        : (discountType === 'fixed' ? Math.min(fixedDiscountAmount || 0, revenueSubtotal) : 0);
      const totalDiscount = Math.round((perItemDiscountTotal + orderLevelDiscountAmount) * 100) / 100;
      const selectedDoc = documentTypes.find(d => d.id === selectedDocumentType);
      const response = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/fiscal/invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify({
          orderId,
          orderItems,
          totalNetAmount: discountedTotal - vatTotal,
          totalVatAmount: vatTotal,
          totalGrossAmount: discountedTotal,
          totalDiscount,
          documentTypeId: selectedDocumentType,
          invoiceTypeCode: selectedDoc?.invoice_type_code,
          paymentMethodId: invoicePaymentMethodId('cash'),
          customer: currentCustomer
        })
      });
      if (!response.ok) {
        console.error('❌ Failed to generate cash invoice:', await response.json().catch(() => ({})));
      } else {
        await response.json();
        setInvoiceCustomer(null);
        setInvoiceVatNumber('');
      }
    } catch (error) {
      console.error('Error generating cash invoice:', error);
    }
  };

  // Generate card receipt (fiscal document with POS transaction)
  const generateCardReceipt = async (orderId: number, paymentData: any) => {
    // Capture basket items NOW before any async operations that might clear it
    const currentBasket = [...basket];
    
    try {
      // Build order items for fiscal document — include per-item discount info for print
      const orderItems = currentBasket.map(item => {
        const discountPct = item.discount || 0;
        const finalUnitPrice = item.totalPrice;
        const originalUnitPrice = discountPct > 0
          ? parseFloat((finalUnitPrice / (1 - discountPct / 100)).toFixed(4))
          : finalUnitPrice;
        return {
          productId: item.product.id,
          productName: item.product.name,
          quantity: item.quantity,
          unitPrice: item.totalPrice,
          displayUnitPrice: originalUnitPrice,
          displayTotalPrice: parseFloat((finalUnitPrice * item.quantity).toFixed(2)),
          vatRate: item.product.vat,
          discountPercent: discountPct,
          discountAmount: parseFloat(((originalUnitPrice - finalUnitPrice) * item.quantity).toFixed(2)),
          weight: item.weight || null,
          weight_unit: item.weightUnit || null
        };
      });

      // Calculate totals WITH order discount applied (handles both percent and fixed)
      const subtotal = currentBasket.reduce((total, item) => total + (item.totalPrice * item.quantity), 0);
      const discountedTotal = calculateTotal();
      const vatTotal = calculateVAT();
      const netTotal = discountedTotal - vatTotal;

      // Total discount for print — order-level discount is on revenue only.
      const perItemDiscountTotal = orderItems.reduce((s, i) => s + (i.discountAmount || 0), 0);
      const revenueSubtotal = currentBasket.reduce((t, it) => t + (isStateFee(it) ? 0 : it.totalPrice * it.quantity), 0);
      const orderLevelDiscountAmount = discountType === 'percent' && orderDiscount > 0
        ? revenueSubtotal * orderDiscount / 100
        : (discountType === 'fixed' ? Math.min(fixedDiscountAmount || 0, revenueSubtotal) : 0);
      const totalDiscount = Math.round((perItemDiscountTotal + orderLevelDiscountAmount) * 100) / 100;

      // Get signature data from payment response
      const signatureData = paymentData.signatureData;

      const response = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/fiscal/receipt/pos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify({
          orderId,
          orderItems,
          totalNetAmount: netTotal,
          totalVatAmount: vatTotal,
          totalGrossAmount: discountedTotal,
          totalDiscount,
          posTransactionId: paymentData.transactionId,
          oxygenSignatureId: signatureData?.signatureId,
          transactionId: paymentData.transactionId || paymentData.response?.TransactionId,
          signature: signatureData?.signedText,
          invoiceSeries: signatureData?.invoiceSeries,
          invoiceNumber: signatureData?.invoiceNumber,
          cardInfo: {
            cardType: paymentData.response?.CardScheme || null,
            cardNumber: paymentData.response?.CardNo || null,
            transactionId: paymentData.response?.transactionId || null,
            authCode: paymentData.response?.ApprovalCode || null
          }
        })
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('❌ Failed to generate fiscal receipt:', error);
      } else {
        await response.json();
        // Receipt will be printed automatically by Print Agent
      }
    } catch (error) {
      console.error('Error generating fiscal receipt:', error);
    }
  };

  // Generate card invoice (fiscal document with POS transaction and customer details)
  const generateCardInvoice = async (orderId: number, paymentData: any) => {
    // Capture basket items and customer NOW before any async operations
    const currentBasket = [...basket];
    const currentCustomer = invoiceCustomer;
    
    if (!currentCustomer) {
      console.error('❌ No customer data for invoice');
      return;
    }

    try {
      // Build order items for fiscal document
      const orderItems = currentBasket.map(item => {
        const discountPct = item.discount || 0;
        const finalUnitPrice = item.totalPrice;
        const originalUnitPrice = discountPct > 0
          ? parseFloat((finalUnitPrice / (1 - discountPct / 100)).toFixed(4))
          : finalUnitPrice;
        return {
          productId: item.product.id,
          productName: item.product.name,
          quantity: item.quantity,
          unitPrice: item.totalPrice,
          displayUnitPrice: originalUnitPrice,
          displayTotalPrice: parseFloat((finalUnitPrice * item.quantity).toFixed(2)),
          vatRate: item.product.vat,
          discountPercent: discountPct,
          discountAmount: parseFloat(((originalUnitPrice - finalUnitPrice) * item.quantity).toFixed(2)),
          weight: item.weight || null,
          weight_unit: item.weightUnit || null
        };
      });

      // Calculate totals WITH order discount applied (handles both percent and fixed)
      const subtotal = currentBasket.reduce((total, item) => total + (item.totalPrice * item.quantity), 0);
      const discountedTotal = calculateTotal();
      const vatTotal = calculateVAT();
      const netTotal = discountedTotal - vatTotal;

      // Total discount for print — order-level discount is on revenue only.
      const perItemDiscountTotal = orderItems.reduce((s, i) => s + (i.discountAmount || 0), 0);
      const revenueSubtotal = currentBasket.reduce((t, it) => t + (isStateFee(it) ? 0 : it.totalPrice * it.quantity), 0);
      const orderLevelDiscountAmount = discountType === 'percent' && orderDiscount > 0
        ? revenueSubtotal * orderDiscount / 100
        : (discountType === 'fixed' ? Math.min(fixedDiscountAmount || 0, revenueSubtotal) : 0);
      const totalDiscount = Math.round((perItemDiscountTotal + orderLevelDiscountAmount) * 100) / 100;

      // Get the selected document type's invoice_type_code
      const selectedDoc = documentTypes.find(d => d.id === selectedDocumentType);

      const response = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/fiscal/invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify({
          orderId,
          orderItems,
          totalNetAmount: netTotal,
          totalVatAmount: vatTotal,
          totalGrossAmount: discountedTotal,
          totalDiscount,
          documentTypeId: selectedDocumentType,
          invoiceTypeCode: selectedDoc?.invoice_type_code, // '1.1' or '2.1'
          paymentMethodId: paymentMethod === 'iris' ? 8 : 7, // 7=Card/POS, 8=IRIS
          customer: currentCustomer,
          cardInfo: {
            cardType: paymentData.response?.CardScheme || null,
            cardNumber: paymentData.response?.CardNo || null,
            transactionId: paymentData.response?.transactionId || null,
            authCode: paymentData.response?.ApprovalCode || null
          }
        })
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('❌ Failed to generate invoice:', error);
      } else {
        await response.json();
        // Invoice will be printed automatically by Print Agent
        
        // Clear invoice customer data
        setInvoiceCustomer(null);
        setInvoiceVatNumber('');
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
    }
  };

  // Numpad for discount input (no keyboard needed). The shared NumpadModal
  // handles the keypad UI + clamping; the page just routes the confirmed
  // value to whichever target the cashier was editing.
  const openNumpad = (target: typeof numpadTarget, currentValue: number) => {
    setNumpadTarget(target);
    setNumpadValue(currentValue > 0 ? String(currentValue) : '');
    setShowNumpad(true);
  };

  const handleSharedNumpadConfirm = (val: number) => {
    if (numpadTarget === 'orderPercent') setOrderDiscount(val);
    else if (numpadTarget === 'orderFixed') setFixedDiscountAmount(val);
    else if (numpadTarget === 'tempDiscount') setTempOrderDiscount(val);
    else if (numpadTarget === 'productDiscount') setProductDiscount(val);
    setShowNumpad(false);
  };

  // Reset order after successful payment
  /** Μετά την πληρωμή: κλασική = παράθυρο 2″· νέα εμφάνιση = λωρίδα στο πάνελ με τα ρέστα, ο
   *  κατάλογος είναι αμέσως έτοιμος. Τα ποσά από την ΙΔΙΑ ζωγραφιά με την πληρωμή (πριν το reset). */
  // cashGiven: «Δόθηκαν» όταν πληρώθηκε με μετρητά παραγγελία κάρτας που δεν πέρασε — εκεί ο τρόπος
  // πληρωμής της οθόνης είναι ακόμα «κάρτα», αλλά τα ρέστα πρέπει να φανούν όπως σε κάθε πώληση μετρητών.
  const showOrderSuccess = (cashGiven?: number | null) => {
    if (!v2) {
      setShowSuccessModal(true);
      return;
    }
    const total = calculateTotal();
    // Με προκαταβολή πελάτη τα ρέστα βγαίνουν από ό,τι πληρώθηκε τώρα (το υπόλοιπο), όχι από το σύνολο.
    const due = hasDeposit ? amountDue : total;
    const given = cashGiven !== undefined ? cashGiven
      : (!deposit.covered && paymentMethod === 'cash' && cashReceived !== '' ? Number(cashReceived) : null);
    setLastSale({
      total,
      change: given !== null && given >= due ? Math.round((given - due) * 100) / 100 : null,
      deposit: hasDeposit ? deposit.appliedCents / 100 : null,
    });
  };

  const resetOrder = () => {
    setBasket([]);
    setCustomer({ name: '', notes: '' });
    setOrderDiscount(0);
    setDiscountType('percent');
    setFixedDiscountAmount(0);
    setPaymentMethod(null);
    setPaymentStatus('idle');
    setPaymentMessage('');
    setCardFailureCancelOnly(false);
    currentOrderIdRef.current = null;
    setCurrentOrderId(null);
    orderDueRef.current = null;
    setOrderDueCents(null);
    invoiceOrderRef.current = null;
    deposit.remove();
  };

  // «Ολοκλήρωση με προκαταβολή»: η προκαταβολή καλύπτει όλο το ποσό — απόδειξη 'deposit' (ή τιμολόγιο με
  // «Επί πιστώσει» 5, αφού ο ταμίας βάλει τα στοιχεία του πελάτη). Κανένα τερματικό, κανένα συρτάρι.
  const completeWithDeposit = () => {
    if (!deposit.covered) {
      showToast('warning', t('deposits:takeaway.notCovered'));
      return;
    }
    if (ecrEnabled && !selectedDocumentType) {
      showToast('warning', 'Διάλεξε τύπο παραστατικού');
      return;
    }
    setCashReceived('');
    setPaymentMethod(null);
    if (isInvoiceDocumentType()) {
      setShowInvoiceModal(true);
      return;
    }
    void processCashPayment('deposit');
  };

  const handlePageChange = (page: string) => {
    if (page !== 'takeaway') {
      if (onPageChange) {
        onPageChange(page);
      } else {
        window.location.pathname = page === 'dashboard' ? '/' : `/${page}`;
      }
    }
  };

  // Helper: Get selected category object (used by header breadcrumb)
  const getSelectedCategory = () => categories.find(c => c.id === selectedCategory);

  // Handle category selection
  const handleCategorySelect = (category: Category) => {
    setFavoritesView(false);
    setSelectedCategory(category.id);
    setSearchTerm('');
    
    if (category.subcategories && category.subcategories.length > 0) {
      // Has subcategories - go to subcategories view
      setCurrentView('subcategories');
    } else {
      // No subcategories - go directly to products
      setSelectedSubcategory(null);
      setCurrentView('products');
    }
  };

  // Handle subcategory selection
  const handleSubcategorySelect = (subcategory: Category | null) => {
    setFavoritesView(false);
    setSelectedSubcategory(subcategory?.id || null);
    setSearchTerm('');
    setCurrentView('products');
  };

  // «Αγαπημένα»: προβολή προϊόντων χωρίς κατηγορία, με τη σειρά της λίστας.
  const handleFavoritesSelect = () => {
    setFavoritesView(true);
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSearchTerm('');
    setCurrentView('products');
  };

  // Νέα εμφάνιση: «Όλα» φαίνεται και με επιλεγμένη κατηγορία, οπότε καθαρίζει και αυτήν
  // (το handleSubcategorySelect(null) κρατά την κατηγορία).
  const handleAllProductsSelect = () => {
    setFavoritesView(false);
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSearchTerm('');
    setCurrentView('products');
  };

  // Handle back navigation
  const handleBack = () => {
    if (favoritesView) {
      setFavoritesView(false);
      setCurrentView('categories');
      return;
    }
    if (currentView === 'products') {
      const category = getSelectedCategory();
      if (category?.subcategories && category.subcategories.length > 0) {
        setCurrentView('subcategories');
      } else {
        setSelectedCategory(null);
        setCurrentView('categories');
      }
    } else if (currentView === 'subcategories') {
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setCurrentView('categories');
    }
  };

  // Home button — jump straight back to the catalog root from any depth.
  const handleHome = () => {
    setFavoritesView(false);
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSearchTerm('');
    setCurrentView('categories');
  };

  // Ρυθμίσεις συσκευής (γρανάζι): στήλες, οθόνη πελάτη, εμφάνιση. Κοινές και στις δύο εμφανίσεις.
  const gridSettingsPopover = showGridSettings && (
    <div className="absolute right-0 top-full mt-2 w-72 bg-surface-2 border border-line-strong rounded-xl shadow-2xl shadow-black/15 dark:shadow-black/40 z-50 p-4">
      <h3 className="text-t1 font-bold text-sm mb-4 uppercase tracking-wide">{t('gridSettings')}</h3>

      {/* Category Columns */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-t2 text-sm font-medium">{t('categoriesLabel')}</label>
          <span className="text-blue-600 dark:text-blue-400 font-bold text-sm">{categoryColumns} {t('columns')}</span>
        </div>
        <div className="flex gap-1">
          {[2, 3, 4, 5, 6].map(n => (
            <button
              key={n}
              onClick={() => setCategoryColumns(n)}
              className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
                categoryColumns === n
                  ? 'bg-blue-600 text-white'
                  : 'bg-surface-3 text-t2 hover:bg-surface-4'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {/* Product Columns */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-2">
          <label className="text-t2 text-sm font-medium">{t('productsLabel')}</label>
          <span className="text-blue-600 dark:text-blue-400 font-bold text-sm">{productColumns} {t('columns')}</span>
        </div>
        <div className="flex gap-1">
          {[2, 3, 4, 5, 6, 7, 8].map(n => (
            <button
              key={n}
              onClick={() => setProductColumns(n)}
              className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
                productColumns === n
                  ? 'bg-blue-600 text-white'
                  : 'bg-surface-3 text-t2 hover:bg-surface-4'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      <div className="pt-3 border-t border-line-strong">
        <p className="text-t3 dark:text-t4 text-xs">{t('gridHint')}</p>
      </div>
      <CustomerDisplaySettings {...customerDisplay} />

      {/* Κατάλογος (utils/catalogPrefsV2, ανά συσκευή): διάταξη, χρώμα, σειρές κατηγοριών, «Όλα». Η διάταξη
          «σε βήματα» είναι ό,τι έμεινε από τον παλιό διακόπτη «Κλασική/Νέα» — η εμφάνιση είναι πλέον μία. */}
      {v2 && (
        <div className="mt-4 pt-3 border-t border-line-strong space-y-3">
          <div>
            <div className="text-t2 text-sm font-medium mb-2">{t('v2.catalogLayout')}</div>
            <div className="flex gap-1" role="group" aria-label={t('v2.catalogLayout')}>
              {(['single', 'steps'] as const).map(option => (
                <button key={option} type="button" aria-pressed={catalogPrefs.catalogLayout === option}
                  onClick={() => setCatalogPrefsV2({ catalogLayout: option })}
                  className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
                    catalogPrefs.catalogLayout === option ? 'bg-blue-600 text-white' : 'bg-surface-3 text-t2 hover:bg-surface-4'
                  }`}>
                  {t(`v2.catalogLayout_${option}`)}
                </button>
              ))}
            </div>
            <p className="text-t3 dark:text-t4 text-xs mt-2">{t('v2.catalogLayoutHint')}</p>
          </div>
          <div>
            <div className="text-t2 text-sm font-medium mb-2">{t('v2.catalogColor')}</div>
            <div className="flex gap-1" role="group" aria-label={t('v2.catalogColor')}>
              {(['none', 'soft', 'solid'] as const).map(option => (
                <button key={option} type="button" aria-pressed={catalogPrefs.categoryColor === option}
                  onClick={() => setCatalogPrefsV2({ categoryColor: option })}
                  className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
                    catalogPrefs.categoryColor === option ? 'bg-blue-600 text-white' : 'bg-surface-3 text-t2 hover:bg-surface-4'
                  }`}>
                  {t(`v2.catalogColor_${option}`)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-t2 text-sm font-medium">{t('v2.catalogRows')}</div>
              <span className="text-t3 text-xs">{t('v2.catalogRowsHint')}</span>
            </div>
            <div className="flex gap-1" role="group" aria-label={t('v2.catalogRows')}>
              {CATEGORY_ROW_OPTIONS.map(n => (
                <button key={n} type="button" aria-pressed={catalogPrefs.categoryRows === n}
                  onClick={() => setCatalogPrefsV2({ categoryRows: n })}
                  className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
                    catalogPrefs.categoryRows === n ? 'bg-blue-600 text-white' : 'bg-surface-3 text-t2 hover:bg-surface-4'
                  }`}>
                  {n}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-t2 text-sm font-medium mb-2">{t('v2.catalogAll')}</div>
            <div className="flex gap-1" role="group" aria-label={t('v2.catalogAll')}>
              {([false, true] as const).map(option => (
                <button key={String(option)} type="button" aria-pressed={catalogPrefs.showAll === option}
                  onClick={() => setCatalogPrefsV2({ showAll: option })}
                  className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
                    catalogPrefs.showAll === option ? 'bg-blue-600 text-white' : 'bg-surface-3 text-t2 hover:bg-surface-4'
                  }`}>
                  {option ? t('common:yes') : t('common:no')}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const basketLineCount = basket.filter(b => b.product.fee_category !== 16).length;
  const basketUnitCount = basket.filter(b => b.product.fee_category !== 16).reduce((sum, b) => sum + (b.weight ? 1 : b.quantity), 0);
  const parkedCount = queue.filter(p => p.type === 'takeaway').length;
  const clearBasket = () => { setBasket([]); setOrderDiscount(0); setDiscountType('percent'); setFixedDiscountAmount(0); };

  // ---- Νέα εμφάνιση: αναίρεση ----
  const offerUndo = (label: string) => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndoRemoval({ label, basket, orderDiscount, discountType, fixedDiscountAmount });
    undoTimerRef.current = setTimeout(() => setUndoRemoval(null), 4000);
  };
  const undoLastRemoval = () => {
    if (!undoRemoval) return;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    // Ξαναμπαίνουν ΜΟΝΟ οι γραμμές που έφυγαν (utils/basketOps): ως 24/10/2026 το αντίγραφο
    // αντικαθιστούσε όλο το καλάθι και έσβηνε ό,τι είχε μπει ή αλλάξει μέσα στα 4″.
    const snapshot = undoRemoval.basket;
    setBasket(prev => restoreRemovedLines(prev, snapshot));
    setOrderDiscount(undoRemoval.orderDiscount);
    setDiscountType(undoRemoval.discountType);
    setFixedDiscountAmount(undoRemoval.fixedDiscountAmount);
    setUndoRemoval(null);
  };
  const removeLineWithUndo = (itemId: string) => {
    const line = basket.find(b => b.id === itemId);
    offerUndo(t('v2.removed', { name: line?.product.name || '' }));
    removeFromBasket(itemId);
  };
  const clearBasketWithUndo = () => {
    offerUndo(t('v2.cleared'));
    clearBasket();
  };

  // ---- Νέα εμφάνιση: γρήγορη προσθήκη ----
  const handleQuickAdd = (size: ProductSize | null, options: SelectedOptionEntry[]) => {
    if (!quickAdd) return;
    const { product } = quickAdd;
    setQuickAdd(null);
    if (size) addToBasketWithSize(product, size, { selected_options: options.length ? options : undefined });
    else addNoSizeWithOptions(product, options);
    forgetPendingOptions();
  };
  // Κλείσιμο χωρίς προσθήκη: οι προεπιλογές του προϊόντος (applyOptionGroups) φεύγουν μαζί του.
  const closeQuickAdd = () => {
    setQuickAdd(null);
    forgetPendingOptions();
  };

  // Νέα εμφάνιση: «Ολοκλήρωση» → το παράθυρο πληρωμής, όπως το κλασικό (απόφαση ιδιοκτήτη 15/10/2026:
  // στα καταστήματα δεν άρεσαν τα «Μετρητά / Κάρτα» με ένα πάτημα στο κάτω μέρος).
  // ---- Νέα εμφάνιση: πληρωμή με ένα πάτημα («Μετρητά» / «Κάρτα» στο πάνελ, επαναφορά 16/10/2026) ----
  // Τρέχει ΜΕΤΑ τη ζωγραφιά που έβαλε τον τρόπο (και τα μετρητά), ώστε το handleCheckout —
  // ίδιο με το κουμπί «Πληρωμή» του παραθύρου — να διαβάσει τις νέες τιμές.
  useEffect(() => {
    if (!quickPayMethod || paymentMethod !== quickPayMethod) return;
    setQuickPayMethod(null);
    void handleCheckout();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickPayMethod, paymentMethod]);

  // Ίδια σειρά με το «Άλλος τρόπος»: πρώτα η πρόταση πώλησης (startCheckout), μετά ο τρόπος.
  const startQuickPay = (method: TakeawayPaymentMethod) => {
    if (basket.length === 0) return;
    setShowBasketMenu(false);
    quickPayRef.current = method;
    startCheckout();
  };
  const runQuickPay = (method: TakeawayPaymentMethod) => {
    setCashReceived('');
    setPaymentMethod(method);
    // Τιμολόγιο: τα στοιχεία πελάτη πρώτα, όπως από το παράθυρο πληρωμής.
    if (isInvoiceDocumentType()) {
      setShowInvoiceModal(true);
      return;
    }
    // Μετρητά: ΠΑΝΤΑ το παράθυρο ρέστων (απόφαση ιδιοκτήτη 12/10) — ολοκληρώνει μόνο η «Πληρωμή» του.
    if (method === 'cash') {
      setShowCashSheet(true);
      return;
    }
    setQuickPayMethod(method);
  };
  const payCash = () => {
    setShowCashSheet(false);
    setQuickPayMethod('cash');
  };
  const closeCashSheet = () => {
    setShowCashSheet(false);
    setCashReceived('');
    setIsTrainingOrder(false);
    setPaymentMethod(null);
  };
  // Μετρητά: κράτημα 2″ = εκπαιδευτική παραγγελία, ΑΚΡΙΒΩΣ όπως στο κουμπί «Πληρωμή» του παραθύρου·
  // ποτέ με προκαταβολή πελάτη (ο server τη δέχεται μόνο σε κανονική παραγγελία).
  const cashHoldStart = () => {
    if (basket.length === 0) return;
    if (trainingModeEnabled && !deposit.selection) {
      holdTimerRef.current = setTimeout(() => { setIsTrainingOrder(true); }, 2000);
    }
  };
  const cashHoldEnd = (pay: boolean) => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (pay) startQuickPay('cash');
  };
  const docLabel = (doc: { invoice_type_code?: string; name: string }) =>
    doc.invoice_type_code?.startsWith('11.') ? t('v2.receipt')
      : (doc.invoice_type_code === '1.1' || doc.invoice_type_code === '2.1') ? t('v2.invoice')
      : doc.name;
  const v2DocTypes = documentTypes.filter(doc => {
    const basketCategory = getBasketProductCategory();
    if (!basketCategory) return doc.invoice_type_code === '11.1' || doc.invoice_type_code === '1.1';
    if (basketCategory === 'services') return doc.invoice_type_code === '11.2' || doc.invoice_type_code === '2.1';
    return doc.invoice_type_code === '11.1' || doc.invoice_type_code === '1.1';
  });

  useEffect(() => { if (paymentStatus !== 'processing') setCardCancelHelp(false); }, [paymentStatus]);

  // «Πληρωμή» του παραθύρου ολοκλήρωσης (και στις δύο εμφανίσεις): κράτημα 2″ με μετρητά =
  // εκπαιδευτική παραγγελία· η πληρωμή στο pointerup, με swallowGhostClick για την αφή.
  // Νέα εμφάνιση: με μετρητά κάτω από το σύνολο το κουμπί κλειδώνει (όπως το παράθυρο ρέστων).
  // Με προκαταβολή πελάτη «σύνολο» εδώ = το ΥΠΟΛΟΙΠΟ (αυτό δίνει ο πελάτης τώρα).
  const cashShortInCheckout = paymentMethod === 'cash' && !deposit.covered && cashOutcome(amountDue, cashReceived).missing !== null;
  const clearPayHold = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };
  const payPressStart = () => {
    if (!paymentMethod) return;
    // Με προκαταβολή πελάτη ποτέ εκπαίδευση (ο server τη δέχεται μόνο σε κανονική παραγγελία).
    if (trainingModeEnabled && paymentMethod === 'cash' && !deposit.selection) {
      holdTimerRef.current = setTimeout(() => {
        setIsTrainingOrder(true);
      }, 2000);
    }
  };
  const payPressEnd = (e: React.PointerEvent) => {
    clearPayHold();
    if (!paymentMethod) return;
    if (v2 && cashShortInCheckout) return;
    // Το παράθυρο κλείνει εδώ· το click της αφής θα έπεφτε σε ό,τι είναι από κάτω.
    swallowGhostClick(e);
    setShowCheckoutModal(false);
    handleCheckout();
  };

  /**
   * Νέα εμφάνιση — «Ολοκλήρωση»: ίδιο περιεχόμενο και ίδια λογική με το κλασικό παράθυρο (τρόπος,
   * παραστατικό, έκπτωση, σημειώσεις, ρέστα), στη μορφή της νέας εμφάνισης (απόφαση ιδιοκτήτη
   * 15/10/2026: «κάτι σαν το παλιό, απλά να ταιριάζει με το UI μας»).
   */
  const renderCheckoutV2 = () => {
    const total = calculateTotal();
    const subtotal = basket.reduce((sum, item) => sum + (item.totalPrice * item.quantity), 0);
    // Προκαταβολή πελάτη που καλύπτει όλο το ποσό: κανένας τρόπος, κανένα αριθμητικό μετρητών.
    const isCash = paymentMethod === 'cash' && !deposit.covered;
    // Ρέστα από ό,τι πληρώνεται ΤΩΡΑ: το υπόλοιπο μετά την προκαταβολή (χωρίς προκαταβολή = το σύνολο).
    const { change } = cashOutcome(amountDue, cashReceived);
    const sectionLabel = 'text-sm font-semibold text-t2 mb-2 block';
    const chip = (on: boolean) => `h-12 rounded-xl border text-base font-semibold transition-all active:scale-[0.98] ${
      on ? 'bg-brand-soft border-brand-line text-brand ring-1 ring-brand' : 'bg-surface-1 border-line text-t1 hover:bg-surface-3'
    }`;
    const methodBtn = (id: TakeawayPaymentMethod, label: React.ReactNode, icon?: React.ReactNode) => (
      <button key={id} type="button" aria-pressed={paymentMethod === id}
        onClick={() => { setPaymentMethod(id); if (id !== 'cash') setCashReceived(''); }}
        className={`${chip(paymentMethod === id)} h-16 flex items-center justify-center gap-2 px-3`}>
        {icon}{label}
      </button>
    );
    const presets: number[] = takeawaySettings?.discount_presets || [5, 10, 15, 20];
    const customPercent = orderDiscount > 0 && !presets.includes(orderDiscount);
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
        onPointerDown={e => { checkoutPressOnBackdropRef.current = e.target === e.currentTarget; }}
        onClick={e => {
          const fromBackdrop = checkoutPressOnBackdropRef.current && e.target === e.currentTarget;
          checkoutPressOnBackdropRef.current = false;
          if (fromBackdrop) setShowCheckoutModal(false);
        }}
        data-testid="takeaway-checkout-modal">
        <div role="dialog" aria-modal="true" aria-labelledby="takeaway-checkout-title"
          className={`w-full ${isCash ? 'max-w-4xl' : 'max-w-xl'} max-h-full flex flex-col rounded-2xl bg-surface-2 border border-line shadow-2xl overflow-hidden`}>
          <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-line">
            <div>
              <h2 id="takeaway-checkout-title" className="text-xl font-bold text-t1">{t('takeaway:completeOrder')}</h2>
              <p className="text-sm text-t3">{t('v2.lines', { count: basketLineCount })} · {t('v2.units', { count: basketUnitCount })}</p>
            </div>
            <button type="button" onClick={() => setShowCheckoutModal(false)} aria-label={t('common:close', { defaultValue: 'Κλείσιμο' })}
              className="w-11 h-11 shrink-0 rounded-xl hover:bg-surface-3 flex items-center justify-center text-t3">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className={`flex-1 min-h-0 overflow-y-auto p-5 ${isCash ? 'grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_20rem] gap-5' : ''}`}>
            <div className="space-y-4">
              {/* Προκαταβολή πελάτη (14/10/2026): η ΕΠΙΛΕΓΜΕΝΗ φαίνεται πριν από τον τρόπο (αλλάζει το ποσό· αν
                  καλύπτει όλο το ποσό, τρόπος δεν χρειάζεται). Η επιλογή της ξεκινά από το «Περισσότερα»
                  (απόφαση ιδιοκτήτη 15/10/2026). */}
              {deposit.showControl && deposit.selection && (
                <div>
                  <span className={sectionLabel}>{t('deposits:takeaway.title')}</span>
                  <TakeAwayDepositLine selection={deposit.selection} appliedCents={deposit.appliedCents} dueCents={deposit.dueCents}
                    onOpen={deposit.openSheet} onRemove={deposit.remove} />
                </div>
              )}

              {deposit.covered ? (
                <p className="text-sm text-t2" data-testid="checkout-deposit-covers">{t('deposits:takeaway.completeHint')}</p>
              ) : (
              <div>
                <span className={sectionLabel}>{t('takeaway:paymentMethod')}</span>
                <div className="grid grid-cols-3 gap-2">
                  {methodBtn('cash', t('takeaway:cashLabel'), <Banknote className="h-5 w-5" />)}
                  {methodBtn('card', t('takeaway:cardLabel'), <CreditCard className="h-5 w-5" />)}
                  <button type="button" onClick={() => setShowMoreMethods(!moreMethodsOpen)}
                    aria-expanded={moreMethodsOpen} aria-controls="takeaway-more-payment-methods"
                    className={`${chip(paymentMethod === 'iris' || paymentMethod === 'credit')} h-16 flex items-center justify-center gap-1 px-3 text-sm`}>
                    {paymentMethod === 'iris' ? 'IRIS' : paymentMethod === 'credit' ? t('common:payment.creditCaps') : t('takeaway:morePaymentMethods')}
                    <ChevronDown className={`h-4 w-4 transition-transform ${moreMethodsOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>
                {moreMethodsOpen && (
                  <div id="takeaway-more-payment-methods"
                    className={`grid ${deposit.showControl && !deposit.selection ? 'grid-cols-3' : 'grid-cols-2'} gap-2 mt-2`}>
                    {methodBtn('iris', 'IRIS')}
                    {methodBtn('credit', t('common:payment.creditCaps'), <HandCoins className="h-5 w-5" />)}
                    {deposit.showControl && !deposit.selection && (
                      <button type="button" data-testid="takeaway-deposit-open" onClick={deposit.openSheet}
                        className={`${chip(false)} h-16 flex items-center justify-center gap-2 px-2 text-sm`}>
                        <Wallet className="h-5 w-5 shrink-0" />
                        <span className="leading-tight">{t('deposits:takeaway.button')}</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
              )}

              {ecrEnabled && v2DocTypes.length > 1 && (
                <div>
                  <span className={sectionLabel}>{t('takeaway:documentType')}</span>
                  <div role="group" aria-label={t('takeaway:documentType')} className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-surface-1 border border-line">
                    {v2DocTypes.map(doc => (
                      <button key={doc.id} type="button" aria-pressed={selectedDocumentType === doc.id}
                        onClick={() => setSelectedDocumentType(doc.id)}
                        className={`h-11 rounded-lg text-base font-semibold transition-colors ${
                          selectedDocumentType === doc.id ? 'bg-surface-2 text-t1 border border-line shadow-sm' : 'text-t3 hover:text-t1'
                        }`}>
                        {docLabel(doc)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {hasPermission('takeaway_apply_discount') && (
                <div>
                  <span className={sectionLabel}>{t('takeaway:discount')}</span>
                  {takeawaySettings?.discount_allow_fixed_amount && (
                    <div className="grid grid-cols-2 gap-1 p-1 mb-2 rounded-xl bg-surface-1 border border-line">
                      <button type="button" aria-pressed={discountType === 'percent'}
                        onClick={() => { setDiscountType('percent'); setFixedDiscountAmount(0); }}
                        className={`h-10 rounded-lg text-sm font-semibold ${discountType === 'percent' ? 'bg-surface-2 text-t1 border border-line shadow-sm' : 'text-t3 hover:text-t1'}`}>
                        {t('takeaway:discountPercentType')}
                      </button>
                      <button type="button" aria-pressed={discountType === 'fixed'}
                        onClick={() => { setDiscountType('fixed'); setOrderDiscount(0); }}
                        className={`h-10 rounded-lg text-sm font-semibold ${discountType === 'fixed' ? 'bg-surface-2 text-t1 border border-line shadow-sm' : 'text-t3 hover:text-t1'}`}>
                        {t('takeaway:discountFixedType')}
                      </button>
                    </div>
                  )}
                  {discountType === 'percent' ? (
                    <div className="flex flex-wrap gap-2">
                      <button type="button" aria-pressed={orderDiscount === 0} onClick={() => setOrderDiscount(0)}
                        className={`${chip(orderDiscount === 0)} flex-1 min-w-[3.5rem]`}>0%</button>
                      {presets.map(preset => (
                        <button key={preset} type="button" aria-pressed={orderDiscount === preset}
                          onClick={() => setOrderDiscount(Math.min(preset, takeawaySettings?.discount_max_percent || 100))}
                          className={`${chip(orderDiscount === preset)} flex-1 min-w-[3.5rem]`}>
                          {preset}%
                        </button>
                      ))}
                      {takeawaySettings?.discount_allow_custom_percent && (
                        <button type="button" onClick={() => openNumpad('orderPercent', customPercent ? orderDiscount : 0)}
                          className={`${chip(customPercent)} px-4 tabular-nums`}>
                          {customPercent ? `${orderDiscount}%` : '…%'}
                        </button>
                      )}
                    </div>
                  ) : (
                    <button type="button" onClick={() => openNumpad('orderFixed', fixedDiscountAmount)}
                      className={`${chip(fixedDiscountAmount > 0)} w-full tabular-nums`}>
                      {fixedDiscountAmount > 0 ? formatEuro(fixedDiscountAmount) : '— €'}
                    </button>
                  )}
                </div>
              )}

              <div>
                <span className={sectionLabel}>{t('takeaway:notes')}</span>
                <Input
                  placeholder={t('takeaway:notesPlaceholder')}
                  value={customer.notes}
                  onChange={(e) => setCustomer({ ...customer, notes: e.target.value })}
                  className="h-12 text-base rounded-xl border border-line bg-surface-1 text-t1"
                />
              </div>

              <div className="rounded-xl bg-surface-1 border border-line p-4 space-y-1.5 tabular-nums">
                <div className="flex justify-between text-base">
                  <span className="text-t3">{t('takeaway:subtotal')}</span>
                  <span className="font-medium text-t1">{formatEuro(subtotal)}</span>
                </div>
                {discountType === 'percent' && orderDiscount > 0 && (
                  <div className="flex justify-between text-base text-t2">
                    <span>{t('takeaway:discount')} ({orderDiscount}%)</span>
                    <span className="font-medium">−{formatEuro(subtotal * orderDiscount / 100)}</span>
                  </div>
                )}
                {discountType === 'fixed' && fixedDiscountAmount > 0 && (
                  <div className="flex justify-between text-base text-t2">
                    <span>{t('takeaway:discount')}</span>
                    <span className="font-medium">−{formatEuro(fixedDiscountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between items-end pt-2 border-t border-line">
                  <span className="text-base font-semibold text-t1">{t('v2.total')}</span>
                  <span className="text-[1.75rem] leading-none font-bold text-t1" data-testid="checkout-total">{formatEuro(total)}</span>
                </div>
                {/* Με προκαταβολή πελάτη: το παραστατικό φεύγει για όλο το ποσό· πληρώνεται τώρα το υπόλοιπο */}
                {hasDeposit && (
                  <>
                    <div className="flex justify-between text-base text-t2" data-testid="checkout-deposit-line">
                      <span>{t('deposits:takeaway.title')}</span>
                      <span className="font-medium">−{formatEuro(deposit.appliedCents / 100)}</span>
                    </div>
                    <div className="flex justify-between items-end text-lg font-bold text-t1" data-testid="checkout-deposit-due">
                      <span>{t('deposits:takeaway.toPay')}</span>
                      <span>{formatEuro(amountDue)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {isCash && (
              <CashKeypad total={amountDue} value={cashReceived} onChange={setCashReceived} />
            )}
          </div>

          <div className="px-5 pt-3 pb-5 border-t border-line space-y-2">
            {!paymentMethod && !deposit.covered && (
              <p className="text-center text-sm text-t3">{t('takeaway:selectPaymentPrompt', { defaultValue: 'Επίλεξε τρόπο πληρωμής για να ολοκληρώσεις' })}</p>
            )}
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowCheckoutModal(false)}
                className="flex-1 h-16 rounded-xl border border-line-strong text-t2 text-base font-semibold hover:bg-surface-3">
                {t('takeaway:back')}
              </button>
              {isInvoiceDocumentType() ? (
                <button type="button" disabled={!paymentMethod && !deposit.covered}
                  onClick={() => {
                    if (!paymentMethod && !deposit.covered) return;
                    setShowCheckoutModal(false);
                    setShowInvoiceModal(true);
                  }}
                  className="flex-[2] h-16 rounded-xl bg-brand text-brand-fg text-lg font-semibold flex items-center justify-center gap-2 hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                  <FileText className="h-5 w-5" />
                  {t('takeaway:invoice')}
                </button>
              ) : deposit.covered ? (
                // Η προκαταβολή καλύπτει όλο το ποσό: ΕΝΑ κουμπί, κανένας τρόπος πληρωμής, κανένα τερματικό.
                <button type="button" data-testid="checkout-pay-deposit"
                  onClick={() => {
                    setShowCheckoutModal(false);
                    completeWithDeposit();
                  }}
                  className="flex-[2] h-16 rounded-xl bg-brand text-brand-fg text-lg font-semibold flex items-center justify-center gap-2 select-none touch-manipulation hover:brightness-110 active:scale-[0.98] transition-all">
                  <Check className="h-5 w-5" />
                  {t('deposits:takeaway.complete')}
                </button>
              ) : (
                <button type="button" data-testid="checkout-pay"
                  disabled={!paymentMethod || cashShortInCheckout}
                  onPointerDown={payPressStart}
                  onPointerUp={payPressEnd}
                  onPointerLeave={clearPayHold}
                  onPointerCancel={clearPayHold}
                  onContextMenu={e => e.preventDefault()}
                  className={`flex-[2] h-16 rounded-xl text-lg font-semibold flex items-center justify-center gap-2 select-none touch-manipulation active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                    isTrainingOrder ? 'bg-orange-500 text-white' : 'bg-brand text-brand-fg hover:brightness-110'
                  }`}>
                  <Receipt className="h-5 w-5" />
                  {isCash && change !== null && change > 0
                    ? t('v2.payWithChange', { amount: formatEuro(change) })
                    : t('takeaway:payment')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Γραμμές καλαθιού — ίδιες και στις δύο εμφανίσεις· αλλάζει μόνο η μορφή της γραμμής.
  const renderBasketRows = (rowLook: 'classic' | 'v2') => {
    // Με κλειστή την αυτόματη συμπλήρωση δεν υπόσχεται κανείς τέλος,
    // οπότε η σημείωση «+ τέλος €X / τεμ.» θα ήταν ψέμα στην οθόνη.
    // Ίδια πηγή ποσού με την αυτόματη γραμμή (feeUnitGross)· χωρίς ποσό → καμία σημείωση.
    const feeProduct = products.find(p => p.fee_category === 16);
    const feeUnit = takeawaySettings?.auto_plastic_fee_enabled === false
      ? 0
      : autoFeeNoteUnit(feeProduct);
    // ΜΟΝΟ η γραμμή που ενημερώνει ο αυτοματισμός είναι «αυτόματη» (utils/autoFee). Ως 19/10/2026
    // ήταν κάθε γραμμή κωδ. 16: ένα χειροκίνητο «ΕΙΣΦ ΠΡΟΣΤ ΠΕΡΙΒ» δεν έφευγε με × (το × έγραφε
    // feeOverride, που με κλειστό διακόπτη δεν διαβάζει κανείς) και δεν άλλαζε με ±.
    const managedFeeId = autoManagedFeeLineId({
      basket,
      feeProduct,
      feeOverride,
      autoEnabled: takeawaySettings?.auto_plastic_fee_enabled
    });
    const removeLine = rowLook === 'v2' ? removeLineWithUndo : removeFromBasket;
    return basket.map((item) => {
      // Catalog/size prices are net of the plastic fee; the fee
      // rides as its own visible basket line (and a rec_type=2 line
      // on the receipt). The cashier can waive/adjust it. Trigger
      // products carry a small note so the cashier sees they bear a
      // fee (the amount itself is on the separate fee line).
      const isFee16 = item.product.fee_category === 16;
      const managedFee = item.id === managedFeeId;
      // Η δική μας γραμμή: × / ± = επιλογή τέλους του ταμία (feeOverride), όπως πάντα.
      const ownAutoFee = managedFee && isOwnAutoFeeLine(item);
      // Χειροκίνητη γραμμή που υιοθέτησε ο αυτοματισμός (χωρίς δική μας δίπλα της): η αλλαγή γίνεται
      // στη γραμμή και το feeOverride 0 «παγώνει» τον κανόνα — αλλιώς ο επόμενος γύρος θα την ξαναγύριζε.
      const adoptedFee = managedFee && !ownAutoFee;
      const perUnitFee = (item.product.auto_plastic_fee_count || 0) * feeUnit;
      const infoNote = !isFee16 && perUnitFee > 0
        ? `+ τέλος ${formatMoney(perUnitFee)} / τεμ.`
        : '';
      return (
        <BasketItemRow
          key={item.id}
          item={item}
          onRemove={ownAutoFee
            ? () => setFeeOverride(0)
            : adoptedFee ? id => { setFeeOverride(0); removeLine(id); } : removeLine}
          onQuantityChange={ownAutoFee
            ? (_id, delta) => setFeeOverride(Math.max(0, item.quantity + delta))
            : adoptedFee ? (id, delta) => { setFeeOverride(0); updateQuantity(id, delta); } : updateQuantity}
          // Ζυγιζόμενη γραμμή από barcode έχει πλέον μέγεθος (FE-3)· η επεξεργασία
          // ξαναϋπολογίζει βάση + μέγεθος ΧΩΡΙΣ βάρος και θα άλλαζε το ποσό.
          onClick={row => { if (!row.weight) openEditModal(row); }}
          clickableOnlyWithSize
          getUnitPrice={p => productDisplayPrice(p, 'take_away')}
          priceField="totalPrice"
          infoNote={infoNote}
          look={rowLook}
        />
      );
    });
  };

  return (
    <div className="cashier-workspace h-screen bg-surface-1 overflow-hidden flex flex-col">
      {/* Use the main Navigation component */}
      <Navigation
        currentPage="takeaway"
        onPageChange={handlePageChange}
        onOpenFavorites={openFavorites}
      />

      <TakeAwayFavoritesModal
        isOpen={showFavoritesModal}
        onClose={() => setShowFavoritesModal(false)}
        products={products}
        categories={categories}
        favoriteIds={favoriteIds}
        onToggle={toggleFavorite}
      />

      {upsellPick && (
        <UpsellPrompt
          message={upsellPick.suggestion.message}
          products={upsellPick.products}
          channel="take_away"
          onAdd={handleUpsellAdd}
          onDecline={handleUpsellDecline}
        />
      )}

      {/* Order queue modal */}
      {showQueueModal && (
        <OrderQueueModal
          queue={queue.filter(p => p.type === 'takeaway')}
          onResume={handleResumeDraft}
          onRemove={remove}
          onClose={() => setShowQueueModal(false)}
          title={t('parkedOrdersTitle', { defaultValue: 'Parked Orders' })}
        />
      )}

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        
        {/* ============== LEFT SECTION: Categories/Subcategories/Products ============== */}
        <div className="flex-1 flex flex-col overflow-hidden">
          
          {/* Top Bar with Search and Back Button */}
          {!v2 && (
          <div className="cashier-toolbar bg-surface-2 border-b border-line-strong px-4 py-3 flex items-center gap-3">
            {/* Back / Home — only show when not on categories root */}
            {currentView !== 'categories' && (
              <>
                <button
                  onClick={handleBack}
                  className="p-3 bg-surface-3 hover:bg-surface-4 rounded-xl transition-all active:scale-95 text-t2"
                  title={t('back', { defaultValue: 'Πίσω' })}
                >
                  <ChevronRight className="h-6 w-6 rotate-180" />
                </button>
                <button
                  onClick={handleHome}
                  className="p-3 bg-surface-3 hover:bg-surface-4 rounded-xl transition-all active:scale-95 text-t2"
                  title={t('home', { defaultValue: 'Αρχή' })}
                >
                  <Home className="h-6 w-6" />
                </button>
              </>
            )}

            {/* Title — hidden while the search bar is expanded so it can take the full width */}
            {!searchOpen && (
              <div className="flex-shrink-0">
                <h1 className="text-xl font-bold text-t1">
                  {currentView === 'categories' && t('categories')}
                  {currentView === 'subcategories' && getSelectedCategory()?.name}
                  {currentView === 'products' && (favoritesView
                    ? t('favorites', { defaultValue: 'Αγαπημένα' })
                    : selectedSubcategory
                    ? getSelectedCategory()?.subcategories?.find(s => s.id === selectedSubcategory)?.name
                    : getSelectedCategory()?.name || t('allProducts'))}
                </h1>
              </div>
            )}

            {/* Barcode scanner feedback — no visible input needed, global listener handles it */}
            {!searchOpen && store?.barcode_enabled && (barcodeError || scanningBarcode) && (
              <div className={`flex items-center gap-2 px-3 h-10 rounded-xl border text-sm font-medium ${
                barcodeError
                  ? 'bg-red-50 dark:bg-red-900/30 border-red-500 text-red-700 dark:text-red-300'
                  : 'bg-violet-50 dark:bg-violet-900/30 border-violet-500 text-violet-700 dark:text-violet-300'
              }`}>
                {scanningBarcode
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> {t('searching')}</>
                  : barcodeError}
              </div>
            )}

            {/* Spacer so the search button sits on the right when collapsed */}
            {!searchOpen && <div className="flex-1" />}

            {/* Search — collapsed icon button by default; expands inline on click */}
            {searchOpen ? (
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-t3 h-5 w-5" />
                <Input
                  type="text"
                  autoFocus
                  placeholder={currentView === 'products' ? t('searchProducts') : t('searchGeneral')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 pr-12 h-12 text-base rounded-xl bg-surface-3 border-2 border-line-strong text-t1 placeholder:text-t3 focus:border-blue-400 focus:ring-blue-400"
                />
                <button
                  onClick={() => { setSearchTerm(''); setSearchOpen(false); }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 text-t3 hover:text-t1"
                  title={t('close', { defaultValue: 'Κλείσιμο' })}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setSearchOpen(true)}
                className="p-3 bg-surface-3 hover:bg-surface-4 rounded-xl transition-all active:scale-95 text-t2"
                title={t('searchGeneral', { defaultValue: 'Αναζήτηση' })}
              >
                <Search className="h-6 w-6" />
              </button>
            )}

            {/* Grid Settings Button */}
            <div className="relative">
              <button
                onClick={() => setShowGridSettings(!showGridSettings)}
                aria-label={t('gridSettings')}
                aria-expanded={showGridSettings}
                className={`p-3 rounded-xl transition-all active:scale-95 ${showGridSettings ? 'bg-blue-600 text-white' : 'bg-surface-3 hover:bg-surface-4 text-t2'}`}
              >
                <Settings className="h-6 w-6" />
              </button>

              {gridSettingsPopover}
            </div>

          </div>
          )}

          {/* Νέα εμφάνιση: τίτλος + αναζήτηση πάντα ανοιχτή + γρανάζι συσκευής */}
          {v2 && (
            <div className="shrink-0 px-5 pt-5 pb-4 flex items-center gap-3 bg-surface-1">
              <div className="min-w-0 mr-auto">
                <h1 className="text-2xl font-bold text-t1 leading-tight">Take Away</h1>
                <p className="text-sm text-t3 mt-0.5 truncate">{t('v2.newOrder')}</p>
              </div>
              {store?.barcode_enabled && (barcodeError || scanningBarcode) && (
                <div className={`flex items-center gap-2 px-3 h-10 rounded-xl border text-sm font-medium ${
                  barcodeError
                    ? 'bg-red-50 dark:bg-red-900/30 border-red-500 text-red-700 dark:text-red-300'
                    : 'bg-surface-2 border-line text-t2'
                }`}>
                  {scanningBarcode
                    ? <><Loader2 className="h-4 w-4 animate-spin" /> {t('searching')}</>
                    : barcodeError}
                </div>
              )}
              <div className="relative w-full max-w-sm">
                <Search aria-hidden="true" className="absolute left-3.5 top-1/2 -translate-y-1/2 text-t3 h-5 w-5" />
                <Input
                  type="text"
                  placeholder={t('v2.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  // Πληκτρολόγιο οθόνης μόνο σε οθόνη αφής (όπως στο Delivery)· με φυσικό πληκτρολόγιο όχι.
                  onFocus={() => { if (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) setSearchKbOpen(true); }}
                  className="pl-11 pr-10 h-11 text-base rounded-xl bg-surface-2 border border-line text-t1 placeholder:text-t3 focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-0"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-t3 hover:text-t1 hover:bg-surface-3"
                    aria-label={t('close', { defaultValue: 'Κλείσιμο' })}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="relative shrink-0">
                <button
                  onClick={() => setShowGridSettings(!showGridSettings)}
                  aria-label={t('gridSettings')}
                  aria-expanded={showGridSettings}
                  className={`w-11 h-11 rounded-xl border flex items-center justify-center transition-colors active:scale-95 ${showGridSettings ? 'bg-brand-soft border-brand-line text-brand' : 'bg-surface-2 border-line text-t2 hover:bg-surface-3'}`}
                >
                  <Settings className="h-5 w-5" />
                </button>
                {gridSettingsPopover}
              </div>
            </div>
          )}

          {/* Content Area — shared catalog */}
          <ProductCatalog
            categories={dailyCatalog.categories}
            products={dailyCatalog.products}
            loading={loading}
            currentView={currentView}
            selectedCategory={selectedCategory}
            selectedSubcategory={selectedSubcategory}
            searchTerm={searchTerm}
            categoryColumns={categoryColumns}
            productColumns={productColumns}
            onCategorySelect={handleCategorySelect}
            onSubcategorySelect={handleSubcategorySelect}
            onProductClick={handleProductClick}
            onProductMouseDown={handleMouseDown}
            onProductMouseUp={handleMouseUp}
            getProductPrice={p => productDisplayPrice(p, 'take_away')}
            translationNamespace="takeaway"
            favoriteProductIds={favoriteIds}
            favoritesSelected={favoritesView}
            onFavoritesSelect={handleFavoritesSelect}
            look={v2 ? catalogLookOf(catalogPrefs) : 'classic'}
            onBack={v2 ? handleBack : undefined}
            onHome={v2 ? handleHome : undefined}
            onAllProductsSelect={handleAllProductsSelect}
            basketCounts={v2 ? basketCounts : undefined}
          />
        </div>

        {/* ============== RIGHT SECTION: Cart (always visible) ============== */}
        {!v2 && (
        <div className="cashier-basket w-96 bg-surface-0 border-l border-line flex flex-col">
          {/* Basket Header */}
          <div className="cashier-basket-header p-4 border-b border-line bg-surface-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="cashier-basket-icon w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                  <ShoppingCart className="h-5 w-5 light:text-white dark:text-t1" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-t1">{t('cart')}</h2>
                  {basket.length > 0 && (
                    <span className="text-sm text-t3">{basket.filter(b => b.product.fee_category !== 16).length} {t('itemsCount')}</span>
                  )}
                </div>
              </div>
              {basket.length > 0 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={handlePark}
                    className="p-2 text-amber-700 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-lg transition-all active:scale-95"
                    title={t('parkOrder', { defaultValue: 'Park order' })}
                  >
                    <PauseCircle className="h-5 w-5" />
                  </button>
                  <button
                    onClick={clearBasket}
                    className="p-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-all active:scale-95"
                    title={t('clearBasket')}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Basket Items */}
          <div className="cashier-basket-items flex-1 overflow-y-auto p-4">
            {basket.length > 0 && store?.id && (
              <PromotionBanner
                storeId={store.id}
                items={promotionItems}
                appliedPromotionId={appliedPromotionId}
                onApply={applyPromotion}
                onRemove={removePromotion}
              />
            )}
            {basket.length === 0 ? (
              <div className="cashier-basket-empty text-center py-12">
                <div className="cashier-empty-icon w-20 h-20 bg-surface-1 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <ShoppingCart className="h-10 w-10 text-t4" />
                </div>
                <p className="font-medium text-t3">{t('emptyCart')}</p>
                <p className="text-sm mt-1 text-t4">{t('emptyCartHint')}</p>
                {queue.filter(p => p.type === 'takeaway').length > 0 && (
                  <button
                    onClick={() => setShowQueueModal(true)}
                    className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 bg-amber-100 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-700/60 hover:bg-amber-200 dark:hover:bg-amber-900/60 text-amber-800 dark:text-amber-300 rounded-xl text-sm font-medium transition-all active:scale-95"
                  >
                    <PauseCircle className="h-4 w-4" />
                    {t('parkedOrdersTitle', { defaultValue: 'Parked Orders' })}
                    <span className="bg-amber-500 text-black text-xs font-bold rounded-full px-1.5 py-0.5 leading-none">
                      {queue.filter(p => p.type === 'takeaway').length}
                    </span>
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {renderBasketRows('classic')}
              </div>
            )}
          </div>

          {/* Total & Checkout */}
          {basket.length > 0 && (
            <div className="cashier-basket-footer border-t border-line p-4 space-y-3 bg-surface-1">
              {/* Total */}
              <div className="flex justify-between items-center">
                <span className="text-t3 font-medium">{t('total')}</span>
                <span className="cashier-total text-2xl font-bold text-green-600 dark:text-green-400">{formatMoney(calculateTotal())}</span>
              </div>

              {/* Προκαταβολή πελάτη που αφαιρείται (επιλέγεται στο «Ολοκλήρωση»): η γραμμή και το υπόλοιπο */}
              {deposit.showControl && deposit.selection && (
                <TakeAwayDepositLine selection={deposit.selection} appliedCents={deposit.appliedCents} dueCents={deposit.dueCents}
                  onOpen={deposit.openSheet} onRemove={deposit.remove} />
              )}

              {/* Checkout Button */}
              <button 
                onClick={startCheckout}
                data-testid="takeaway-checkout"
                className="cashier-primary w-full py-5 px-6 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-lg flex items-center justify-center gap-3 active:scale-[0.98] transition-all shadow-lg shadow-blue-600/30"
              >
                <Receipt className="h-6 w-6" />
                {t('checkout')}
              </button>
            </div>
          )}
        </div>
        )}

        {/* ============== Νέα εμφάνιση: «Η παραγγελία» ============== */}
        {v2 && (
        <div className="xv2-basket w-[26rem] shrink-0 bg-surface-2 border-l border-line border-t-[3px] border-t-brand flex flex-col">
          <div className="px-5 pt-5 pb-4 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-t1 leading-tight">{t('v2.order')}</h2>
              <p className="text-sm text-t3 mt-0.5">
                {t('v2.lines', { count: basketLineCount })} · {t('v2.units', { count: basketUnitCount })}
              </p>
            </div>
            {/* Οι επιλογές της παραγγελίας («Σε αναμονή», «Άδειασμα») ΕΔΩ, πάνω στο καλάθι — όχι δίπλα στην
                «Ολοκλήρωση» (απόφαση ιδιοκτήτη 15/10/2026). */}
            <div className="shrink-0 flex items-center gap-2">
              {parkedCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowQueueModal(true)}
                  data-testid="takeaway-parked-orders"
                  aria-label={`${t('v2.parkedOrders')}: ${parkedCount}`}
                  title={t('v2.parkedOrders')}
                  className="inline-flex items-center gap-2 h-11 px-3 rounded-xl border border-line bg-surface-2 text-sm font-medium text-t2 hover:bg-surface-3 active:scale-95 transition-all"
                >
                  <PauseCircle className="h-4 w-4 text-t3" />
                  {t('v2.parkedShort')}
                  <span className="min-w-[1.25rem] h-5 px-1 rounded-full bg-t1 text-surface-2 text-xs font-bold leading-5 text-center">{parkedCount}</span>
                </button>
              )}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowBasketMenu(open => !open)}
                  aria-label={t('v2.more')}
                  aria-expanded={showBasketMenu}
                  aria-haspopup="menu"
                  disabled={basket.length === 0}
                  data-testid="takeaway-basket-menu"
                  className="w-11 h-11 rounded-xl border border-line bg-surface-2 text-t2 flex items-center justify-center hover:bg-surface-3 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Ellipsis className="h-5 w-5" />
                </button>
                {showBasketMenu && basket.length > 0 && (
                  <>
                    <div className="fixed inset-0 z-40" {...basketMenuBackdrop} data-testid="takeaway-basket-menu-backdrop" />
                    <div role="menu" className="absolute right-0 top-full mt-2 w-72 z-50 rounded-xl border border-line bg-surface-2 shadow-xl shadow-black/10 dark:shadow-black/40 py-1.5">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => { setShowBasketMenu(false); handlePark(); }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-t2 hover:bg-surface-3 text-left"
                      >
                        <PauseCircle className="h-4 w-4 text-t3" />
                        {t('v2.park')}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => { setShowBasketMenu(false); clearBasketWithUndo(); }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-left"
                      >
                        <X className="h-4 w-4" />
                        {t('v2.clear')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className={`grid ${BASKET_V2_COLUMNS} gap-x-2 px-5 py-2 border-y border-line bg-surface-1 text-xs font-medium text-t3`}>
            <span>{t('v2.colProduct')}</span>
            <span className="text-center">{t('v2.colQuantity')}</span>
            <span className="text-right">{t('v2.colTotal')}</span>
            <span />
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-5">
            {basket.length > 0 && store?.id && (
              <div className="pt-3">
                <PromotionBanner
                  storeId={store.id}
                  items={promotionItems}
                  appliedPromotionId={appliedPromotionId}
                  onApply={applyPromotion}
                  onRemove={removePromotion}
                />
              </div>
            )}
            {basket.length === 0 && lastSale ? (
              <div data-testid="takeaway-last-sale" className="mt-5 rounded-2xl border border-line bg-surface-1 p-5 text-center">
                <div className="mx-auto w-11 h-11 rounded-full bg-brand-soft text-brand flex items-center justify-center mb-3">
                  <Check className="h-6 w-6" />
                </div>
                <div className="text-base font-semibold text-t1">
                  {successOrderCode ? t('v2.lastSale', { code: successOrderCode }) : t('v2.lastSaleNoCode')}
                </div>
                <div className="text-sm text-t3 mt-0.5 tabular-nums">{t('v2.total')} {formatEuro(lastSale.total)}</div>
                {lastSale.deposit != null && (
                  <div className="text-sm text-t2 mt-0.5 tabular-nums" data-testid="takeaway-last-sale-deposit">
                    {t('deposits:takeaway.lastSale', {
                      deposit: formatEuro(lastSale.deposit),
                      paid: formatEuro(Math.max(0, Math.round((lastSale.total - lastSale.deposit) * 100) / 100)),
                    })}
                  </div>
                )}
                {lastSale.change !== null && (
                  <div className="mt-4">
                    <div className="text-sm text-t3">{t('v2.change')}</div>
                    <div className="text-4xl font-bold text-t1 tabular-nums leading-tight">{formatEuro(lastSale.change)}</div>
                  </div>
                )}
              </div>
            ) : basket.length === 0 ? (
              <p className="text-center text-sm text-t3 py-10">{t('v2.emptyHint')}</p>
            ) : (
              renderBasketRows('v2')
            )}
          </div>

          {undoRemoval && (
            <div role="status" data-testid="takeaway-undo" className="mx-5 mb-3 flex items-center justify-between gap-3 rounded-xl bg-t1 text-surface-2 pl-4 pr-2 h-12 text-sm">
              <span className="truncate">{undoRemoval.label}</span>
              <button type="button" onClick={undoLastRemoval}
                className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg font-semibold hover:bg-white/10 dark:hover:bg-black/10">
                <Undo2 className="h-4 w-4" />
                {t('v2.undo')}
              </button>
            </div>
          )}

          <div className="border-t border-line px-5 pt-4 pb-5 space-y-3">
            <div className="flex items-end justify-between gap-3">
              <div>
                <div className="text-base font-semibold text-t1">{t('v2.total')}</div>
                <div className="text-sm text-t3">{t('v2.units', { count: basketUnitCount })}</div>
              </div>
              <div className="text-[1.75rem] leading-none font-bold text-t1 tabular-nums">{formatEuro(calculateTotal())}</div>
            </div>
            {/* Προκαταβολή πελάτη που αφαιρείται (επιλέγεται στο «Ολοκλήρωση»): η γραμμή και το υπόλοιπο, όπως στην κλασική */}
            {deposit.showControl && deposit.selection && (
              <TakeAwayDepositLine compact selection={deposit.selection} appliedCents={deposit.appliedCents} dueCents={deposit.dueCents}
                onOpen={deposit.openSheet} onRemove={deposit.remove} />
            )}
            {/* Απόδειξη | Τιμολόγιο — αντί για αναδιπλούμενη λίστα (απόφαση 11/10/2026)· ίδια κατάσταση με το παράθυρο */}
            {ecrEnabled && v2DocTypes.length > 1 && (
              <div role="group" aria-label={t('documentType')} className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-surface-1 border border-line">
                {v2DocTypes.map(doc => (
                  <button key={doc.id} type="button" aria-pressed={selectedDocumentType === doc.id}
                    onClick={() => setSelectedDocumentType(doc.id)}
                    className={`h-10 rounded-lg text-sm font-semibold transition-colors ${
                      selectedDocumentType === doc.id ? 'bg-surface-2 text-t1 border border-line shadow-sm' : 'text-t3 hover:text-t1'
                    }`}>
                    {docLabel(doc)}
                  </button>
                ))}
              </div>
            )}
            {/* Πληρωμή με ένα πάτημα (επαναφορά 16/10/2026, μετά από μια μέρα με «Ολοκλήρωση»): ο τρόπος ΕΙΝΑΙ η
                ολοκλήρωση· κανένας προεπιλεγμένος. Τα «Μετρητά» ενεργούν στο pointerup (κράτημα 2″ = εκπαίδευση) με
                swallowGhostClick για την οθόνη αφής. «Άλλος τρόπος» = το παράθυρο πληρωμής (IRIS, «Επί πιστώσει»,
                έκπτωση, σημειώσεις, προκαταβολή πελάτη). Ποσά = ό,τι πληρώνεται τώρα (με προκαταβολή: το υπόλοιπο).
                Προκαταβολή που καλύπτει όλο το ποσό: μία «Ολοκλήρωση», χωρίς τρόπο πληρωμής. Ύψος 72px. */}
            {deposit.covered ? (
              <div className="grid grid-cols-1">
                <button
                  type="button"
                  data-testid="takeaway-checkout"
                  disabled={basket.length === 0}
                  onClick={() => { setShowBasketMenu(false); startCheckout(); }}
                  className="select-none touch-manipulation h-[4.5rem] rounded-xl font-semibold flex items-center justify-center gap-3 bg-brand text-brand-fg hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Receipt className="h-6 w-6" />
                  <span className="text-lg">{t('v2.complete')}</span>
                  <span className="text-lg tabular-nums opacity-90">{formatEuro(amountDue)}</span>
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_5.25rem] gap-2">
                <button
                  type="button"
                  data-testid="takeaway-pay-cash"
                  disabled={basket.length === 0}
                  onPointerDown={cashHoldStart}
                  onPointerUp={e => { swallowGhostClick(e); cashHoldEnd(true); }}
                  onPointerLeave={() => cashHoldEnd(false)}
                  onPointerCancel={() => cashHoldEnd(false)}
                  onClick={e => { if (e.detail === 0) startQuickPay('cash'); }}
                  onContextMenu={e => e.preventDefault()}
                  className={`select-none touch-manipulation h-[4.5rem] rounded-xl font-semibold flex flex-col items-center justify-center gap-0.5 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                    isTrainingOrder ? 'bg-orange-500 text-white' : 'bg-brand text-brand-fg hover:brightness-110'
                  }`}
                >
                  <span className="flex items-center gap-2 text-lg"><Banknote className="h-6 w-6" />{t('cashLabel')}</span>
                  <span className="text-base tabular-nums opacity-90">{formatEuro(amountDue)}</span>
                </button>
                <button
                  type="button"
                  data-testid="takeaway-pay-card"
                  disabled={basket.length === 0}
                  onClick={() => startQuickPay('card')}
                  className="select-none touch-manipulation h-[4.5rem] rounded-xl bg-brand text-brand-fg font-semibold flex flex-col items-center justify-center gap-0.5 hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <span className="flex items-center gap-2 text-lg"><CreditCard className="h-6 w-6" />{t('cardLabel')}</span>
                  <span className="text-base tabular-nums opacity-90">{formatEuro(amountDue)}</span>
                </button>
                <button
                  type="button"
                  data-testid="takeaway-checkout"
                  disabled={basket.length === 0}
                  onClick={() => { setShowBasketMenu(false); startCheckout(); }}
                  title={t('v2.payOther')}
                  className="select-none touch-manipulation h-[4.5rem] rounded-xl border border-line bg-surface-2 text-t2 font-semibold flex flex-col items-center justify-center gap-1 px-1 hover:bg-surface-3 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Ellipsis className="h-5 w-5" />
                  <span className="text-xs leading-tight text-center">{t('v2.otherWay')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Νέα εμφάνιση: μέγεθος + υποχρεωτικές επιλογές σε ΕΝΑ παράθυρο */}
      {v2 && quickAdd && (
        <QuickAddSheet
          product={quickAdd.product}
          sizes={quickAdd.product.sizes || []}
          groups={quickAdd.groups}
          initialOptions={quickAdd.defaults}
          sizePrice={size => lineUnitPrice(quickAdd.product, size, 'take_away')}
          onAdd={handleQuickAdd}
          onClose={closeQuickAdd}
        />
      )}

      {/* Νέα εμφάνιση: μετρητά — «Δόθηκαν» με +5/+10/+20/+50 ή αριθμητικό, ρέστα, και μετά «Πληρωμή».
          Με προκαταβολή πελάτη: για το υπόλοιπο. */}
      {v2 && showCashSheet && (
        <CashChangeSheet
          total={amountDue}
          value={cashReceived}
          onChange={setCashReceived}
          onPay={payCash}
          onClose={closeCashSheet}
          training={isTrainingOrder}
        />
      )}

      {/* Νέα εμφάνιση: «Μετρητά» στην ΙΔΙΑ παραγγελία μετά από κάρτα που ΣΙΓΟΥΡΑ δεν χρεώθηκε — ίδιο παράθυρο
          ρέστων· ποτέ εκπαίδευση (επιχειρήθηκε ήδη πραγματική χρέωση). Το κλείσιμο γυρνά στις τρεις επιλογές. */}
      {v2 && showCardCashSheet && (
        <CashChangeSheet
          total={cardDue}
          value={cashReceived}
          onChange={setCashReceived}
          onPay={payCardCashSheet}
          onClose={closeCardCashSheet}
          training={false}
        />
      )}

      {/* Νέα εμφάνιση: πληκτρολόγιο οθόνης για την αναζήτηση προϊόντος */}
      {v2 && searchKbOpen && (
        <TouchKeyboard
          mode="text"
          label={t('v2.searchPlaceholder')}
          value={searchTerm}
          onChange={setSearchTerm}
          onDone={() => setSearchKbOpen(false)}
        />
      )}

      {/* Size Selection Modal — immediate-add UX */}
      {showSizeModal && selectedProduct && (
        <ProductSizeModal
          product={selectedProduct}
          selectedSize={selectedSize}
          onSelectSize={(size) => {
            setShowSizeModal(false);
            const requiredOnly = productOptionGroups.filter(g => g.is_required);
            if (requiredOnly.length > 0) {
              setProductOptionGroups(requiredOnly);
              setOptionModalPendingSize(size);
              setShowOptionModal(true);
            } else {
              addToBasketWithSize(selectedProduct, size, { selected_options: pendingSelectedOptions.length ? pendingSelectedOptions : undefined });
            }
          }}
          onClose={() => {
            setShowSizeModal(false);
            setSelectedProduct(null);
            setSelectedSize(null);
            forgetPendingOptions();
          }}
          mode="immediate"
          translationNamespace="takeaway"
        />
      )}

      {/* Option Groups Modal — quick-click flow (required groups only) */}
      {showOptionModal && selectedProduct && (
        <ProductOptionsModal
          product={selectedProduct}
          pendingSize={optionModalPendingSize}
          groups={productOptionGroups}
          selectedOptions={pendingSelectedOptions}
          onChange={setPendingSelectedOptions}
          onClose={() => {
            setShowOptionModal(false);
            setProductOptionGroups([]);
            setPendingSelectedOptions([]);
            setOptionModalPendingSize(null);
            setSelectedProduct(null);
          }}
          onConfirm={() => {
            const missing = productOptionGroups.filter(g => g.is_required && !pendingSelectedOptions.some(o => o.group_id === g.id));
            if (missing.length > 0) {
              showToast('warning', t('takeaway:pleaseSelect', { groups: missing.map(g => g.name).join(', ') }));
              return;
            }
            if (optionModalPendingSize) {
              addToBasketWithSize(selectedProduct, optionModalPendingSize, { selected_options: pendingSelectedOptions });
            } else {
              // No-size product with options
              if (!addNoSizeWithOptions(selectedProduct, pendingSelectedOptions)) return;
              setShowOptionModal(false);
              setProductOptionGroups([]);
              setPendingSelectedOptions([]);
              setOptionModalPendingSize(null);
              setSelectedProduct(null);
            }
          }}
          translationNamespace="takeaway"
        />
      )}

      {/* Customization Modal */}
      {showCustomizeModal && selectedProduct && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-50 p-2 sm:p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(4px)' }}
        >
          <div className="bg-surface-1 rounded-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden shadow-2xl flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-line-strong bg-surface-2 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-t1">{selectedProduct.name}</h2>
                <p className="text-t3 text-sm">{t('takeaway:customizeOrder')}</p>
              </div>
              <button
                onClick={() => {
                  setShowCustomizeModal(false);
                  setSelectedProduct(null);
                  setSelectedSize(null);
                  setRemovedIngredients([]);
                  setAddedExtras([]);
                  setSpecialInstructions('');
                  setProductDiscount(0);
                  setProductOptionGroups([]);
                  setPendingSelectedOptions([]);
                }}
                className="w-12 h-12 rounded-xl bg-surface-3 hover:bg-surface-4 flex items-center justify-center active:scale-95 transition-all"
              >
                <X className="h-6 w-6 text-t2" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1 overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
            {/* Size Selection if multiple sizes */}
            {selectedProduct.sizes && selectedProduct.sizes.length > 1 && (
              <div className="mb-5">
                <Label className="text-base font-bold mb-3 block text-t2">{t('takeaway:size')}</Label>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                  {selectedProduct.sizes.map((size) => (
                    <button
                      key={size.id}
                      onClick={async () => {
                        setSelectedSize(size);
                        await fetchSizeMaterials(size.id);
                      }}
                      className={`p-4 rounded-xl border-2 transition-all active:scale-[0.97] min-h-[96px] flex flex-col items-center justify-center ${
                        selectedSize?.id === size.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md'
                          : 'border-line-strong hover:border-line-strong hover:bg-surface-2'
                      }`}
                    >
                      <div className="font-bold text-lg text-t1">{size.name}</div>
                      {/* Απόλυτη τιμή του μεγέθους (όχι «+»): ό,τι θα χρεωθεί χωρίς έξτρα. */}
                      <div className={`text-sm mt-1 font-medium ${selectedSize?.id === size.id ? 'text-blue-600 dark:text-blue-400' : 'text-t3'}`}>
                        {formatMoney(sizeChannelPrice(size, 'take_away'))}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Loading indicator */}
            {loadingMaterials && (
              <div className="mb-6 text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-t3 mt-2">{t('takeaway:loadingMaterials')}</p>
              </div>
            )}

            {/* Simple Ingredients for Non-Warehouse Stores — κανόνες «χωρίς»/«Έξτρα»: utils/simpleIngredients */}
            {store?.warehouse === false && selectedProduct?.simple_ingredients && selectedProduct.simple_ingredients.length > 0 && (
              <>
                {/* Base Ingredients (Removable) — ταυτότητα = store_ingredient_id, όχι θέση στη λίστα */}
                {removableSimpleIngredients(selectedProduct.simple_ingredients).length > 0 && (
                  <div className="mb-5">
                    <Label className="text-base font-bold mb-3 block text-t2">{t('takeaway:ingredients')}</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {removableSimpleIngredients(selectedProduct.simple_ingredients)
                        .map((ingredient) => {
                          const ingredientId = Number(ingredient.store_ingredient_id);
                          const isRemoved = removedIngredients.includes(ingredientId);
                          return (
                            <button
                              key={`base-${ingredientId}`}
                              onClick={() => {
                                if (isRemoved) {
                                  setRemovedIngredients(removedIngredients.filter(id => id !== ingredientId));
                                } else {
                                  setRemovedIngredients([...removedIngredients, ingredientId]);
                                }
                              }}
                              className={`p-3 rounded-xl border-2 transition-all active:scale-[0.97] text-left ${
                                isRemoved 
                                  ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 line-through' 
                                  : 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/30 text-t2'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                                  isRemoved ? 'bg-red-500' : 'bg-green-500'
                                }`}>
                                  {isRemoved ? (
                                    <X className="h-3 w-3 text-t1" />
                                  ) : (
                                    <Check className="h-3 w-3 text-t1" />
                                  )}
                                </div>
                                <span className="font-medium">{ingredient.name}</span>
                              </div>
                            </button>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* Extra Ingredients (Add More) - Grouped by category — όχι τα προεπιλεγμένα 0 € */}
                {extraSimpleIngredients(selectedProduct.simple_ingredients).length > 0 && (
                <div className="mb-5">
                  <Label className="text-base font-bold mb-3 block text-t2">{t('takeaway:extras')}</Label>
                  <div className="space-y-4">
                    {(() => {
                      const ingredients = extraSimpleIngredients(selectedProduct.simple_ingredients);
                      const categories = Array.from(new Set(ingredients.map(ing => ing.category || t('takeaway:otherCategory'))));
                      return categories.map(cat => (
                        <div key={cat}>
                          <p className="text-xs font-semibold text-t3 uppercase tracking-wider mb-2">{cat}</p>
                          <div className="space-y-2">
                            {ingredients
                              .filter(ing => (ing.category || t('takeaway:otherCategory')) === cat)
                              .map((ingredient, idx) => {
                                const existingExtra = addedExtras.find(e => e.name === ingredient.name);
                                const quantity = existingExtra?.quantity || 0;
                                return (
                                  <div key={idx} className="flex items-center justify-between p-3 bg-surface-2 rounded-xl border-2 border-line-strong">
                                    <div className="flex-1">
                                      <span className="font-medium text-t2">{ingredient.name}</span>
                                      {Number(ingredient.extra_price || 0) > 0 && (
                                        <span className="text-sm text-green-600 dark:text-green-400 ml-2">+{formatMoney(Number(ingredient.extra_price))}</span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => {
                                          if (quantity > 0) {
                                            if (quantity === 1) {
                                              setAddedExtras(addedExtras.filter(e => e.name !== ingredient.name));
                                            } else {
                                              setAddedExtras(addedExtras.map(e => 
                                                e.name === ingredient.name ? { ...e, quantity: e.quantity - 1 } : e
                                              ));
                                            }
                                          }
                                        }}
                                        className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all active:scale-95 ${
                                          quantity > 0 ? 'bg-surface-4 text-t1' : 'bg-surface-3 text-t4'
                                        }`}
                                        disabled={quantity === 0}
                                      >
                                        <Minus className="h-5 w-5" />
                                      </button>
                                      <span className="w-10 text-center font-bold text-lg text-t1">{quantity}</span>
                                      <button
                                        onClick={() => {
                                          if (quantity === 0) {
                                            setAddedExtras([...addedExtras, {
                                              raw_material_id: 0,
                                              name: ingredient.name,
                                              quantity: 1,
                                              price: Number(ingredient.extra_price || 0)
                                            }]);
                                          } else if (quantity < 5) {
                                            setAddedExtras(addedExtras.map(e => 
                                              e.name === ingredient.name ? { ...e, quantity: e.quantity + 1 } : e
                                            ));
                                          }
                                        }}
                                        className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all active:scale-95 ${
                                          quantity < 5 ? 'bg-blue-600 text-white' : 'bg-surface-3 text-t4'
                                        }`}
                                        disabled={quantity >= 5}
                                      >
                                        <Plus className="h-5 w-5" />
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
                )}
              </>
            )}

            {/* Removable Ingredients Only (for Warehouse mode) - Only show default ingredients */}
            {store?.warehouse !== false && !loadingMaterials && productIngredients && productIngredients.filter(i => i.is_default !== false && !i.is_extra_available && i.is_removable).length > 0 && (
              <div className="mb-5">
                <Label className="text-base font-bold mb-3 block text-t2">{t('takeaway:ingredients')}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {productIngredients.filter(ingredient => ingredient.is_default !== false && !ingredient.is_extra_available && ingredient.is_removable).map((ingredient) => {
                    const isRemoved = removedIngredients.includes(ingredient.id);
                    return (
                      <button
                        key={ingredient.id}
                        onClick={() => toggleIngredientRemoval(ingredient.id)}
                        className={`p-3 rounded-xl border-2 transition-all active:scale-[0.97] text-left ${
                          isRemoved 
                            ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 line-through' 
                            : 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/30 text-t2'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                            isRemoved ? 'bg-red-500' : 'bg-green-500'
                          }`}>
                            {isRemoved ? (
                              <X className="h-3 w-3 text-t1" />
                            ) : (
                              <Check className="h-3 w-3 text-t1" />
                            )}
                          </div>
                          <span className="font-medium">{ingredient.name}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Extra Ingredients Section (for Warehouse mode) - Show ALL ingredients as extras */}
            {store?.warehouse !== false && !loadingMaterials && productIngredients && productIngredients.filter(i => i.is_extra_available).length > 0 && (
              <div className="mb-5">
                <Label className="text-base font-bold mb-3 block text-t2">{t('takeaway:extras')}</Label>
                <div className="space-y-2">
                  {productIngredients.filter(ing => ing.is_extra_available).map(extra => {
                    const existingExtra = addedExtras.find(e => e.raw_material_id === (extra.raw_material_id || extra.id));
                    const quantity = existingExtra?.quantity || 0;
                    
                    return (
                      <div key={extra.raw_material_id || extra.id} className="flex items-center justify-between p-3 bg-surface-2 rounded-xl border-2 border-line-strong">
                        <div className="flex-1">
                          <span className="font-medium text-t2">{extra.name}</span>
                          <span className="text-sm text-green-600 dark:text-green-400 ml-2">+{formatMoney(Number(extra.extra_price || 0))}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              if (quantity > 0) {
                                if (quantity === 1) {
                                  setAddedExtras(addedExtras.filter(e => e.raw_material_id !== (extra.raw_material_id || extra.id)));
                                } else {
                                  setAddedExtras(addedExtras.map(e => 
                                    e.raw_material_id === (extra.raw_material_id || extra.id) ? { ...e, quantity: e.quantity - 1 } : e
                                  ));
                                }
                              }
                            }}
                            className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all active:scale-95 ${
                              quantity > 0 ? 'bg-surface-4 text-t1' : 'bg-surface-3 text-t4'
                            }`}
                            disabled={quantity === 0}
                          >
                            <Minus className="h-5 w-5" />
                          </button>
                          <span className="w-10 text-center font-bold text-lg text-t1">{quantity}</span>
                          <button
                            onClick={() => {
                              if (quantity === 0) {
                                setAddedExtras([...addedExtras, {
                                  raw_material_id: extra.raw_material_id || extra.id,
                                  name: extra.name,
                                  quantity: 1,
                                  price: Number(extra.extra_price || 0)
                                }]);
                              } else if (quantity < 5) {
                                setAddedExtras(addedExtras.map(e => 
                                  e.raw_material_id === (extra.raw_material_id || extra.id) ? { ...e, quantity: e.quantity + 1 } : e
                                ));
                              }
                            }}
                            className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all active:scale-95 ${
                              quantity < 5 ? 'bg-blue-600 text-white' : 'bg-surface-3 text-t4'
                            }`}
                            disabled={quantity >= 5}
                          >
                            <Plus className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Option Groups — ALL groups shown in customize modal */}
            {productOptionGroups.length > 0 && (
              <div className="mb-5">
                <Label className="text-base font-bold mb-3 block text-t2">{t('takeaway:options')}</Label>
                <div className="space-y-5">
                  {productOptionGroups.map(group => {
                    const choiceCount = group.choices.length;
                    const choiceGridCols =
                      choiceCount <= 1 ? 'grid-cols-1' :
                      choiceCount === 2 ? 'grid-cols-2' :
                      choiceCount === 3 ? 'grid-cols-3' :
                      choiceCount === 4 ? 'grid-cols-2 sm:grid-cols-4' :
                      'grid-cols-3 sm:grid-cols-4 lg:grid-cols-5';
                    return (
                    <div key={group.id}>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-base font-bold text-t1">{group.name}</span>
                        {group.is_required && <span className="text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full font-semibold">{t('takeaway:required')}</span>}
                        {group.selection_type === 'multiple' && <span className="text-xs text-t4">{t('takeaway:multiple')}</span>}
                      </div>
                      <div className={`grid ${choiceGridCols} gap-3`}>
                        {group.choices.map(choice => {
                          const isSelected = pendingSelectedOptions.some(o => o.group_id === group.id && o.choice_id === choice.id);
                          return (
                            <button
                              key={choice.id}
                              type="button"
                              onClick={() => {
                                const opt = { group_id: group.id, group_name: group.name, choice_id: choice.id, choice_name: choice.name, price_modifier: choice.price_modifier };
                                if (group.selection_type === 'single') {
                                  setPendingSelectedOptions(prev => [...prev.filter(o => o.group_id !== group.id), opt]);
                                } else {
                                  setPendingSelectedOptions(prev =>
                                    isSelected ? prev.filter(o => !(o.group_id === group.id && o.choice_id === choice.id)) : [...prev, opt]
                                  );
                                }
                              }}
                              className={`p-4 rounded-xl border-2 transition-all active:scale-[0.97] min-h-[96px] flex flex-col items-center justify-center text-center ${
                                isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md' : 'border-line-strong hover:border-line-strong hover:bg-surface-2'
                              }`}
                            >
                              <span className="font-bold text-lg text-t1">{choice.name}</span>
                              {choice.price_modifier > 0 && <span className="text-sm mt-1 font-bold text-blue-600 dark:text-blue-400">+{formatMoney(choice.price_modifier)}</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Special Instructions */}
            <div className="mb-5">
              <Label htmlFor="instructions" className="text-base font-bold mb-3 block text-t2">
                {t('takeaway:notes')}
              </Label>
              <textarea
                id="instructions"
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                placeholder={t('takeaway:specialInstructions')}
                className="w-full p-4 border-2 border-line-strong bg-surface-2 text-t1 rounded-xl resize-none text-base focus:border-blue-500 focus:outline-none placeholder-t3"
                rows={2}
              />
            </div>

            {/* Product Discount */}
            <div className="mb-5">
              <Label htmlFor="product-discount" className="text-base font-bold mb-3 block text-t2">
                {t('common:discount')}
              </Label>
              {/* Preset discount buttons + custom input */}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setProductDiscount(0)}
                  className={`h-12 px-5 rounded-xl font-bold transition-all active:scale-95 ${
                    productDiscount === 0
                      ? 'bg-blue-600 text-white'
                      : 'bg-surface-3 text-t2 hover:bg-surface-4'
                  }`}
                >
                  0%
                </button>
                {(takeawaySettings?.discount_presets || [10, 20, 30, 50]).map(percent => (
                  <button
                    key={percent}
                    onClick={() => setProductDiscount(Math.min(percent, takeawaySettings?.discount_max_percent || 100))}
                    className={`h-12 px-5 rounded-xl font-bold transition-all active:scale-95 ${
                      productDiscount === percent
                        ? 'bg-blue-600 text-white'
                        : 'bg-surface-3 text-t2 hover:bg-surface-4'
                    }`}
                  >
                    {percent}%
                  </button>
                ))}
                {/* Custom input — opens numpad */}
                {takeawaySettings?.discount_allow_custom_percent && (
                <button
                  onClick={() => openNumpad('productDiscount', productDiscount)}
                  className="flex items-center gap-1 h-12 px-3 border-2 border-line-strong rounded-xl bg-surface-2 hover:border-blue-500 active:scale-95 transition-all"
                >
                  <span className="w-10 text-center font-bold text-lg text-t1">
                    {productDiscount > 0 && !(takeawaySettings?.discount_presets || []).includes(productDiscount) ? productDiscount : '—'}
                  </span>
                  <span className="font-bold text-t3">%</span>
                </button>
                )}
                {productDiscount > 0 && (
                  <span className="text-base text-green-600 dark:text-green-400 font-bold ml-2">
                    -{formatMoney((((selectedProduct ? lineUnitPrice(selectedProduct, selectedSize, 'take_away') : 0) + addedExtras.reduce((sum, e) => sum + e.price * e.quantity, 0)) * productDiscount / 100))}
                  </span>
                )}
              </div>
            </div>
            </div>

            {/* Footer with Price and Actions - Fixed at bottom */}
            <div className="border-t border-line-strong bg-surface-1 p-4">
              {/* Price Summary - Compact */}
              <div className="flex items-center justify-between mb-4">
                <div className="text-t3">
                  <span className="text-sm">{selectedSize ? `${selectedSize.name}: ` : ''}{formatMoney(lineUnitPrice(selectedProduct, selectedSize, 'take_away'))}</span>
                  {addedExtras.length > 0 && (
                    <span className="text-sm text-green-600 dark:text-green-400 ml-2">| {t('takeaway:extrasTotal')}: +{formatMoney(addedExtras.reduce((sum, e) => sum + (e.price * e.quantity), 0))}</span>
                  )}
                  {productDiscount > 0 && (
                    <span className="text-sm text-red-500 ml-2">| -{productDiscount}%</span>
                  )}
                </div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  €{(() => {
                    const subtotal = lineUnitPrice(selectedProduct, selectedSize, 'take_away') +
                      addedExtras.reduce((sum, extra) => sum + (extra.price * extra.quantity), 0);
                    return (subtotal * (1 - productDiscount / 100)).toFixed(2);
                  })()}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCustomizeModal(false);
                    setSelectedProduct(null);
                    setSelectedSize(null);
                    setRemovedIngredients([]);
                    setAddedExtras([]);
                    setSpecialInstructions('');
                    forgetPendingOptions();
                  }}
                  className="flex-1 h-14 rounded-xl border-2 border-line-strong font-bold text-t3 hover:bg-surface-2 active:scale-[0.98] transition-all"
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={() => {
                    if (selectedProduct) {
                      // Get removed ingredient names
                      let removedIngredientNames: string[] = [];
                      if (store?.warehouse === false && selectedProduct.simple_ingredients) {
                        // Στη σειρά των ids (store_ingredient_id) — utils/simpleIngredients.
                        removedIngredientNames = simpleIngredientNames(selectedProduct.simple_ingredients, removedIngredients);
                      } else if (productIngredients && productIngredients.length > 0) {
                        removedIngredientNames = productIngredients
                          .filter(ing => removedIngredients.includes(ing.id))
                          .map(ing => ing.name);
                      }
                      
                      // Block if required option group has no selection
                      const missing = productOptionGroups.filter(g => g.is_required && !pendingSelectedOptions.some(o => o.group_id === g.id));
                      if (missing.length > 0) {
                        showToast('warning', t('takeaway:pleaseSelect', { groups: missing.map((g: any) => g.name).join(', ') }));
                        return;
                      }

                      const selectedOpts = pendingSelectedOptions.length > 0 ? [...pendingSelectedOptions] : undefined;

                      if (selectedSize) {
                        addToBasketWithSize(selectedProduct, selectedSize, {
                          removedIngredients,
                          removedIngredientNames,
                          addedExtras,
                          selected_options: selectedOpts,
                          specialInstructions,
                          discount: productDiscount > 0 ? productDiscount : undefined
                        });
                      } else {
                        const extrasPrice = addedExtras.reduce((sum, extra) => sum + (extra.price * extra.quantity), 0);
                        const optionsPrice = pendingSelectedOptions.reduce((s, o) => s + o.price_modifier, 0);
                        let totalPrice = lineUnitPrice(selectedProduct, null, 'take_away') + extrasPrice + optionsPrice;

                        if (productDiscount > 0) {
                          totalPrice = totalPrice * (1 - productDiscount / 100);
                        }

                        const basketItem: BasketItem = {
                          id: `${selectedProduct.id}-${Date.now()}`,
                          product: selectedProduct,
                          selectedSize: null,
                          quantity: 1,
                          removedIngredients,
                          removedIngredientNames,
                          addedExtras,
                          selected_options: selectedOpts,
                          specialInstructions,
                          discount: productDiscount > 0 ? productDiscount : undefined,
                          totalPrice
                        };

                        setBasket(prev => addOrMergeBasketLine(prev, basketItem));
                        setShowCustomizeModal(false);
                        setSelectedProduct(null);
                        setRemovedIngredients([]);
                        setAddedExtras([]);
                        setSpecialInstructions('');
                        setProductDiscount(0);
                        setProductOptionGroups([]);
                        setPendingSelectedOptions([]);
                      }
                    }
                  }}
                  className="flex-[2] h-14 rounded-xl bg-blue-600 text-white font-bold text-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                  disabled={!selectedProduct}
                >
                  <Plus className="h-5 w-5" />
                  {t('takeaway:add')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Basket Item Modal — shared ProductCustomizeModal so the cashier
          sees every field they saw at long-press: size, ingredients, extras,
          option groups (μέγεθος/ζάχαρη/γάλα), notes, discount. Previously this
          used a simplified inline modal that hid option groups, which made
          editing a customised drink lose its sugar/milk/size selections. */}
      {showEditModal && editingBasketItem && selectedProduct && (
        <ProductCustomizeModal
          open={showEditModal}
          product={selectedProduct}
          initialSize={selectedSize}
          initialRemovedIngredients={removedIngredients}
          initialAddedExtras={addedExtras as any}
          initialSelectedOptions={pendingSelectedOptions}
          initialSpecialInstructions={specialInstructions}
          initialDiscount={productDiscount}
          weight={editingBasketItem?.weight ?? null}
          lineOwn={editingBasketItem ? lineOwnOf(editingBasketItem) : null}
          productIngredients={productIngredients as any}
          productOptionGroups={productOptionGroups}
          loadingMaterials={loadingMaterials}
          warehouseMode={store?.warehouse !== false}
          discountPresets={takeawaySettings?.discount_presets || [10, 20, 30, 50]}
          maxDiscountPercent={takeawaySettings?.discount_max_percent || 100}
          allowCustomDiscount={takeawaySettings?.discount_allow_custom_percent !== false}
          translationNamespace="takeaway"
          onSizeChange={(size) => { setSelectedSize(size); fetchSizeMaterials(size.id); }}
          onClose={() => {
            setShowEditModal(false);
            setEditingBasketItem(null);
            setSelectedProduct(null);
            setSelectedSize(null);
            setRemovedIngredients([]);
            setAddedExtras([]);
            setSpecialInstructions('');
            setProductDiscount(0);
            setPendingSelectedOptions([]);
            setProductOptionGroups([]);
          }}
          onConfirm={(r: ProductCustomizeResult) => {
            updateBasketItem({
              removedIngredients: r.removedIngredients,
              removedIngredientNames: r.removedIngredientNames,
              addedExtras: r.addedExtras as any,
              selected_options: r.selectedOptions.length ? r.selectedOptions : undefined,
              specialInstructions: r.specialInstructions,
              discount: r.discount,
              newSize: r.size || undefined,
            });
          }}
        />
      )}

      {/* Weight Input Modal */}
      {showWeightModal && selectedProduct && (
        <WeightModal
          product={selectedProduct}
          // «Βάρος + τιμή κιλού»: καμία τιμή καταλόγου — η τιμή κιλού μπαίνει στο επόμενο βήμα.
          pricePerUnit={isWeightedOpenPrice(selectedProduct) ? null : productDisplayPrice(selectedProduct, 'take_away')}
          onConfirm={isWeightedOpenPrice(selectedProduct) ? takeWeightForOpenPrice : addWeightVariableToBasket}
          onClose={() => {
            setShowWeightModal(false);
            setSelectedProduct(null);
            setWeightInput('');
          }}
        />
      )}

      {/* Open Price Modal */}
      {showOpenPriceModal && selectedProduct && (
        <OpenPriceModal
          product={selectedProduct}
          weight={weightForOpenPrice}
          weightUnit={weightUnitOf(selectedProduct)}
          onConfirm={(product, price) => weightForOpenPrice !== null
            ? addWeightedOpenPriceToBasket(product, weightForOpenPrice, price)
            : addOpenPriceToBasket(product, price)}
          onClose={() => {
            setShowOpenPriceModal(false);
            setSelectedProduct(null);
            setOpenPriceInput('');
            setWeightForOpenPrice(null);
          }}
        />
      )}

      {/* Discount Modal */}
      {showDiscountModal && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowDiscountModal(false)}
        >
          <div 
            className="bg-surface-1 rounded-2xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-t1 mb-5">{t('takeaway:orderDiscount')}</h3>
            
            <div className="space-y-5">
              {/* Preset discount buttons */}
              <div className="flex flex-wrap gap-3">
                {(takeawaySettings?.discount_presets || [5, 10, 15, 20]).map(percent => (
                  <button
                    key={percent}
                    onClick={() => setTempOrderDiscount(Math.min(percent, takeawaySettings?.discount_max_percent || 100))}
                    className={`flex-1 min-w-[60px] p-4 rounded-xl font-bold text-lg transition-all active:scale-95 ${
                      tempOrderDiscount === percent
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-surface-3 text-t2 hover:bg-surface-4 border border-line-strong'
                    }`}
                  >
                    {percent}%
                  </button>
                ))}
              </div>
              
              {/* Custom discount — opens numpad (only if allowed by settings) */}
              {takeawaySettings?.discount_allow_custom_percent && (
              <div>
                <Label className="text-sm font-medium mb-2 block text-t2">
                  {t('takeaway:customDiscount')}
                </Label>
                <button
                  onClick={() => openNumpad('tempDiscount', tempOrderDiscount)}
                  className="flex items-center justify-center gap-2 w-full h-14 rounded-xl border-2 border-line-strong bg-surface-2 hover:border-blue-500 active:scale-[0.98] transition-all"
                >
                  <span className="text-2xl font-bold text-t1">
                    {tempOrderDiscount > 0 && !(takeawaySettings?.discount_presets || []).includes(tempOrderDiscount) ? tempOrderDiscount : '—'}
                  </span>
                  <span className="text-xl font-bold text-t3">%</span>
                </button>
              </div>
              )}
              
              {/* Preview */}
              {tempOrderDiscount > 0 && (
                <div className="p-4 bg-green-50 dark:bg-green-900/30 rounded-xl border border-green-300 dark:border-green-700">
                  <div className="space-y-2">
                    <div className="flex justify-between text-base">
                      <span className="text-t3">{t('takeaway:subtotal')}:</span>
                      <span className="font-medium text-t1">{formatMoney(basket.reduce((total, item) => total + (item.totalPrice * item.quantity), 0))}</span>
                    </div>
                    <div className="flex justify-between text-base text-green-600 dark:text-green-400">
                      <span>{t('common:discount')} ({tempOrderDiscount}%):</span>
                      <span className="font-bold">-{formatMoney((basket.reduce((total, item) => total + (item.totalPrice * item.quantity), 0) * tempOrderDiscount / 100))}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg pt-2 border-t border-green-300 dark:border-green-700 text-t1">
                      <span>{t('takeaway:newTotal')}:</span>
                      <span className="text-green-600 dark:text-green-400">{formatMoney((basket.reduce((total, item) => total + (item.totalPrice * item.quantity), 0) * (1 - tempOrderDiscount / 100)))}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Action buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setTempOrderDiscount(0);
                  setOrderDiscount(0);
                  setShowDiscountModal(false);
                }}
                className="flex-1 py-4 px-4 rounded-xl border-2 border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 font-semibold hover:bg-red-100 dark:hover:bg-red-900/30 active:scale-[0.98] transition-all"
              >
                {t('takeaway:remove')}
              </button>
              <button
                onClick={() => {
                  setOrderDiscount(tempOrderDiscount);
                  setShowDiscountModal(false);
                }}
                className="flex-1 py-4 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold active:scale-[0.98] transition-all"
              >
                {t('takeaway:apply')}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* POS Terminal Selection Modal */}
      {showTerminalModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-surface-1 rounded-xl p-6 max-w-md w-full mx-4 border border-line">
            <h3 className="text-xl font-bold text-t1 mb-4 flex items-center gap-2">
              <CreditCard className={`h-6 w-6 ${paymentMethod === 'iris' ? 'text-cyan-600 dark:text-cyan-400' : 'text-blue-600 dark:text-blue-400'}`} />
              {paymentMethod === 'iris' ? t('takeaway:irisSelectPos') : t('takeaway:selectPos')}
            </h3>
            
            <div className="space-y-3 mb-6">
              {posTerminals.map(terminal => (
                <button
                  key={terminal.id}
                  onClick={() => setSelectedTerminal(terminal)}
                  className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                    selectedTerminal?.id === terminal.id
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-line-strong hover:border-line-strong'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium text-t1">{terminal.terminal_name}</div>
                      <div className="text-sm text-t3">{terminal.provider_name}</div>
                    </div>
                    {terminal.is_default && (
                      <span className="text-xs bg-blue-500/20 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">
                        {t('takeaway:defaultTerminal')}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Amount Summary */}
            <div className="bg-surface-2 dark:bg-surface-2/50 rounded-lg p-4 mb-6 border border-line-strong">
              <div className="flex justify-between items-center text-lg font-bold text-t1">
                <span>{t('takeaway:amountToPay')}:</span>
                <span className="text-blue-600 dark:text-blue-400" data-testid="terminal-amount">{formatMoney(cardDue)}</span>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={closeTerminalModal}
                className="flex-1"
              >
                {t('common:cancel')}
              </Button>
              <Button
                onClick={() => {
                  processCardPayment(isPaymentRetry);
                  if (!isPaymentRetry) {
                    setIsPaymentRetry(false); // Reset after first attempt
                  }
                }}
                disabled={!selectedTerminal}
                className={`flex-1 ${paymentMethod === 'iris' ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                <CreditCard className="h-4 w-4 mr-2" />
                {isPaymentRetry ? t('takeaway:retry') : paymentMethod === 'iris' ? t('takeaway:irisPayment') : t('takeaway:payment')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Processing Modal */}
      {showPaymentProcessingModal && (
        <div className={`fixed inset-0 flex items-center justify-center z-50 ${v2 ? 'bg-black/40' : 'bg-black/70 backdrop-blur-sm'}`} data-testid="payment-processing-modal">
          <div className={v2 ? 'bg-surface-2 rounded-2xl p-6 max-w-md w-full mx-4 border border-line shadow-2xl' : 'bg-surface-1 rounded-xl p-8 max-w-md w-full mx-4 border border-line'}>
            <div className="text-center">
              {/* Status Icon */}
              {paymentStatus === 'processing' && (
                <div className={`mx-auto flex items-center justify-center h-20 w-20 rounded-full mb-6 ${v2 ? 'bg-brand-soft' : 'bg-blue-500/20'}`}>
                  <Loader2 className={`h-12 w-12 animate-spin ${v2 ? 'text-brand' : 'text-blue-600 dark:text-blue-400'}`} />
                </div>
              )}
              {paymentStatus === 'success' && (
                <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-500/20 mb-6">
                  <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
                </div>
              )}
              {paymentStatus === 'checking' && (
                <div className={`mx-auto flex items-center justify-center h-20 w-20 rounded-full mb-6 ${v2 ? 'bg-brand-soft' : 'bg-amber-500/20'}`}>
                  <Loader2 className={`h-12 w-12 animate-spin ${v2 ? 'text-brand' : 'text-amber-600 dark:text-amber-400'}`} />
                </div>
              )}
              {paymentStatus === 'failed' && (
                <div className={`mx-auto flex items-center justify-center h-20 w-20 rounded-full mb-6 ${v2 ? 'bg-surface-1 border border-line' : 'bg-red-500/20'}`}>
                  <XCircle className="h-12 w-12 text-red-600 dark:text-red-400" />
                </div>
              )}
              {paymentStatus === 'uncertain' && (
                <div className={`mx-auto flex items-center justify-center h-20 w-20 rounded-full mb-6 ${v2 ? 'bg-surface-1 border border-line' : 'bg-amber-500/20'}`}>
                  <AlertTriangle className="h-12 w-12 text-amber-600 dark:text-amber-400" />
                </div>
              )}

              {/* Status Message */}
              <h3 className={`text-xl font-bold mb-2 ${
                paymentStatus === 'success' ? 'text-green-600 dark:text-green-400' :
                paymentStatus === 'failed' ? 'text-red-600 dark:text-red-400' :
                paymentStatus === 'uncertain' ? 'text-amber-700 dark:text-amber-400' :
                'text-t1'
              }`}>
                {paymentStatus === 'processing' ? t('takeaway:processingPayment') :
                 paymentStatus === 'checking' ? t('takeaway:cardChecking') :
                 paymentStatus === 'success' ? t('takeaway:paymentSuccess') :
                 paymentStatus === 'uncertain' ? t('takeaway:cardUncertainHeading') :
                 t('takeaway:paymentFailed')}
              </h3>
              
              <p className="text-t3 whitespace-pre-line mb-6">
                {paymentMessage}
              </p>

              {/* Amount */}
              <div className={v2 ? 'bg-surface-1 rounded-xl p-4 mb-6 border border-line tabular-nums' : 'bg-surface-2 dark:bg-surface-2/50 rounded-lg p-4 mb-6 border border-line-strong'}>
                <div className="text-sm text-t3 mb-1">{t('takeaway:amount')}</div>
                <div className="text-2xl font-bold text-t1" data-testid="payment-processing-amount">
                  {formatMoney(cardDue)}
                </div>
                {/* Με προκαταβολή πελάτη η κάρτα χρεώνει μόνο το υπόλοιπο */}
                {hasDeposit && (
                  <div className="text-sm text-t3 mt-1 tabular-nums">
                    {t('deposits:takeaway.line', { amount: formatMoney(deposit.appliedCents / 100) })}
                  </div>
                )}
              </div>

              {/* Terminal Info */}
              {selectedTerminal && (
                <div className="text-sm text-t4 mb-6">
                  {t('takeaway:terminal')}: {selectedTerminal.terminal_name}
                </div>
              )}
              
              {/* Actions — η κάρτα δεν πέρασε (ή δεν ξέρουμε): ΙΔΙΑ παραγγελία, ποτέ νέα από εδώ */}
              {(paymentStatus === 'failed' || paymentStatus === 'uncertain') && (
                <CardFailureActions
                  mode={paymentStatus === 'uncertain' ? 'uncertain' : 'failed'}
                  look={v2 ? 'v2' : 'classic'}
                  method={paymentMethod === 'iris' ? 'iris' : 'card'}
                  cancelOnly={cardFailureCancelOnly}
                  busy={cardActionBusy}
                  onRetryCard={retryFailedCard}
                  onPayCash={payFailedCardWithCash}
                  onCancelOrder={cancelFailedCardOrder}
                  onCheckAgain={checkCardAgain}
                  onLeaveForManager={leaveUncertainForManager}
                />
              )}

              {/* Όσο χρεώνει το τερματικό, η ακύρωση γίνεται ΕΚΕΙ. Το παλιό «Ακύρωση» εδώ έκρυβε μόνο το
                  παράθυρο: το καλάθι άλλαζε ενώ η χρέωση έτρεχε ακόμα. */}
              {/* «Ακύρωση» (ζήτηση καταστημάτων 15/10/2026, «σε περίπτωση που το πατήσει κατά λάθος»): ΔΕΝ
                  κλείνει το παράθυρο — η χρέωση τρέχει στο τερματικό και θα περνούσε. Δείχνει πώς ακυρώνεται
                  ΕΚΕΙ· μόλις απαντήσει το τερματικό, ανοίγουν «Ξανά κάρτα / Μετρητά / Ακύρωση παραγγελίας». */}
              {paymentStatus === 'processing' && (
                cardCancelHelp ? (
                  <div role="alert" data-testid="card-cancel-help"
                    className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-400/10 dark:border-amber-400/40 p-4 text-left space-y-3">
                    <div>
                      <div className="text-base font-semibold text-amber-900 dark:text-amber-200">{t('v2.cardCancelTitle')}</div>
                      <p className="text-sm text-amber-900/90 dark:text-amber-200/90 mt-1">{t('v2.cardCancelHelp')}</p>
                    </div>
                    <button type="button" onClick={() => setCardCancelHelp(false)}
                      className="w-full h-12 rounded-xl border border-amber-300 dark:border-amber-400/40 text-amber-900 dark:text-amber-200 font-semibold hover:bg-amber-100 dark:hover:bg-amber-400/10">
                      {t('v2.cardCancelOk')}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-t3">{t('takeaway:cardCancelAtTerminal')}</p>
                    <button type="button" data-testid="card-cancel" onClick={() => setCardCancelHelp(true)}
                      className="w-full h-14 rounded-xl border border-line-strong text-t2 text-base font-semibold hover:bg-surface-3 active:scale-[0.98] transition-all">
                      {t('v2.cardCancel')}
                    </button>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* Generating Receipt Overlay */}
      {generatingReceipt && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-surface-1 rounded-xl p-8 max-w-sm w-full mx-4 text-center border border-line">
            <Loader2 className="h-12 w-12 text-blue-600 dark:text-blue-400 animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-t1">{t('takeaway:generatingReceipt')}</h3>
            <p className="text-sm text-t3 mt-2">{t('takeaway:pleaseWait')}</p>
          </div>
        </div>
      )}

      {/* Add to Cart Toast Notification */}
      {addedToCartAnimation && !v2 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="bg-green-600 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-3">
            <div className="bg-white/20 rounded-full p-1">
              <Check className="h-5 w-5" />
            </div>
            <span className="font-medium">{t('takeaway:addedToCart', { product: lastAddedProduct })}</span>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && !v2 && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShowSuccessModal(false)}
        >
          <div 
            className="bg-surface-1 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl border border-line transform transition-all animate-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              {/* Success Icon with Animation */}
              <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 mb-6 shadow-lg shadow-green-500/20">
                <svg className="h-10 w-10 text-t1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              {/* Success Message */}
              <h3 className="text-2xl font-bold text-t1 mb-2">
                {t('takeaway:orderCompleted')}
              </h3>
              <p className="text-t3 mb-6">{t('takeaway:orderPlacedSuccess')}</p>
              
              {/* Order Code */}
              <div className="bg-surface-2 dark:bg-surface-2/50 rounded-xl py-4 px-6 mb-6 border border-line-strong">
                <p className="text-sm text-t3 mb-1">{t('takeaway:orderNumber')}</p>
                <p className="text-3xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                  #{successOrderCode}
                </p>
              </div>
              
              {/* Order Type Badge */}
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-surface-2 border border-line-strong">
                <span className="font-medium text-t2">{t('takeaway:pickup')}</span>
              </div>
              
              {/* Status with Icon */}
              <div className="flex items-center justify-center gap-2 mt-6 text-green-600">
                <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                <p className="text-sm font-medium">{t('takeaway:orderPreparing')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          {/* max-h + internal scroll: on the small touch screens most stores run,
              the expanded customer form used to push the action buttons off-screen. */}
          <div className="bg-surface-1 rounded-xl w-full max-w-lg mx-4 shadow-2xl border border-line flex flex-col max-h-[92vh]">
            <div className="flex items-center justify-between px-6 pt-5 pb-3">
              <h2 className="text-xl font-bold text-t1 flex items-center gap-2">
                <FileText className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                {t('takeaway:createInvoice')}
              </h2>
              <button
                onClick={closeInvoiceModal}
                aria-label={t('common:close', { defaultValue: 'Κλείσιμο' })}
                className="text-t3 hover:text-t2"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* VAT Number Lookup */}
            <div className="space-y-4 overflow-y-auto flex-1 px-6 pb-4">
              <div>
                <Label className="text-sm font-medium text-t2">{t('takeaway:customerVat')}</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="text"
                    value={invoiceVatNumber}
                    onChange={(e) => setInvoiceVatNumber(e.target.value.replace(/\D/g, ''))}
                    onFocus={() => setInvoiceKb({ field: 'vat_search', mode: 'numeric', label: t('takeaway:customerVat'), maxLength: 9 })}
                    placeholder={t('takeaway:enterVat')}
                    className="flex-1 bg-surface-2 border-line-strong text-t1 placeholder:text-t4"
                    maxLength={9}
                  />
                  <Button
                    onClick={lookupVatNumber}
                    disabled={lookingUpVat || invoiceVatNumber.length < 9}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {lookingUpVat ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Error Message */}
              {vatLookupError && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
                  <XCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <span>{vatLookupError}</span>
                </div>
              )}

              {/* AADE lookup only knows Greek businesses — foreign companies
                  (or a lookup outage) fall back to typing everything in. */}
              {!invoiceCustomer && (
                <button
                  type="button"
                  onClick={() => {
                    setVatLookupError(null);
                    setManualInvoiceEntry(true);
                    setInvoiceCustomer({
                      vat_number: invoiceVatNumber,
                      name: '',
                      country_code: 'GR',
                      branch_code: 0,
                      address: { street: '', number: '', postal_code: '', city: '' }
                    });
                  }}
                  className="w-full py-2.5 rounded-lg border border-dashed border-line-strong text-t2 text-sm hover:bg-surface-3 dark:hover:bg-surface-2/50 active:bg-surface-2"
                >
                  {t('takeaway:manualEntry')} <span className="text-t4">— {t('takeaway:manualEntryHint')}</span>
                </button>
              )}

              {/* Customer Details (from AADE lookup) - Editable */}
              {invoiceCustomer && (
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg space-y-3">
                  <div className="flex items-center gap-2 text-green-600 dark:text-green-400 font-medium">
                    <CheckCircle className="h-5 w-5" />
                    {manualInvoiceEntry ? t('takeaway:manualEntry') : t('takeaway:customerFound')}
                    <span className="text-xs text-t4 font-normal ml-2">{t('takeaway:editable')}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-t3">{t('takeaway:vatNumber')}</Label>
                      <Input
                        value={invoiceCustomer.vat_number || ''}
                        onChange={(e) => setInvoiceCustomer({...invoiceCustomer, vat_number: e.target.value.replace(/[^0-9A-Za-z]/g, '').toUpperCase()})}
                        onFocus={() => setInvoiceKb({ field: 'vat_number', mode: 'text', label: t('takeaway:vatNumber') })}
                        className="mt-1 text-sm bg-surface-2 border-line-strong text-t1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-t3">{t('takeaway:countryCode')}</Label>
                      <CountryCodeInput
                        value={invoiceCustomer.country_code || 'GR'}
                        query={invoiceCountryQuery}
                        onQueryChange={setInvoiceCountryQuery}
                        onSelect={(code) => {
                          setInvoiceCustomer({...invoiceCustomer, country_code: code});
                          setInvoiceCountryQuery(countryLabel(code));
                          setInvoiceKb(null);
                        }}
                        onFocus={() => setInvoiceKb({ field: 'country', mode: 'text', label: t('takeaway:countryCode') })}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-t3">{t('takeaway:companyName')}</Label>
                    <Input
                      value={invoiceCustomer.name || ''}
                      onChange={(e) => setInvoiceCustomer({...invoiceCustomer, name: e.target.value})}
                      onFocus={() => setInvoiceKb({ field: 'name', mode: 'text', label: t('takeaway:companyName') })}
                      className="mt-1 text-sm bg-surface-2 border-line-strong text-t1"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-t3">{t('takeaway:street')}</Label>
                      <Input
                        value={invoiceCustomer.address?.street || ''}
                        onChange={(e) => setInvoiceCustomer({
                          ...invoiceCustomer,
                          address: {...invoiceCustomer.address, street: e.target.value}
                        })}
                        onFocus={() => setInvoiceKb({ field: 'street', mode: 'text', label: t('takeaway:street') })}
                        className="mt-1 text-sm bg-surface-2 border-line-strong text-t1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-t3">{t('takeaway:number')}</Label>
                      <Input
                        value={invoiceCustomer.address?.number || ''}
                        onChange={(e) => setInvoiceCustomer({
                          ...invoiceCustomer,
                          address: {...invoiceCustomer.address, number: e.target.value}
                        })}
                        onFocus={() => setInvoiceKb({ field: 'number', mode: 'text', label: t('takeaway:number') })}
                        className="mt-1 text-sm bg-surface-2 border-line-strong text-t1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-t3">{t('takeaway:city')}</Label>
                      <Input
                        value={invoiceCustomer.address?.city || ''}
                        onChange={(e) => setInvoiceCustomer({
                          ...invoiceCustomer,
                          address: {...invoiceCustomer.address, city: e.target.value}
                        })}
                        onFocus={() => setInvoiceKb({ field: 'city', mode: 'text', label: t('takeaway:city') })}
                        className="mt-1 text-sm bg-surface-2 border-line-strong text-t1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-t3">{t('takeaway:postalCode')}</Label>
                      <Input
                        value={invoiceCustomer.address?.postal_code || ''}
                        onChange={(e) => setInvoiceCustomer({
                          ...invoiceCustomer,
                          address: {...invoiceCustomer.address, postal_code: e.target.value}
                        })}
                        onFocus={() => setInvoiceKb({ field: 'postal_code', mode: 'numeric', label: t('takeaway:postalCode'), maxLength: 5 })}
                        className="mt-1 text-sm bg-surface-2 border-line-strong text-t1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-t3">{t('takeaway:branchCode')}</Label>
                      <Input
                        type="number"
                        value={invoiceCustomer.branch_code || 0}
                        onChange={(e) => setInvoiceCustomer({...invoiceCustomer, branch_code: parseInt(e.target.value) || 0})}
                        onFocus={() => setInvoiceKb({ field: 'branch_code', mode: 'numeric', label: t('takeaway:branchCode'), maxLength: 3 })}
                        className="mt-1 text-sm bg-surface-2 border-line-strong text-t1"
                      />
                    </div>
                  </div>
                  {/* Email/phone inputs removed: optional fields nobody filled — they
                      only made the modal taller than small touch screens. */}
                </div>
              )}

              {/* Order Summary */}
              <div className="p-4 bg-surface-2 dark:bg-surface-2/50 rounded-lg space-y-2 border border-line-strong">
                <h3 className="font-medium text-t1">{t('takeaway:orderSummary')}</h3>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-t3">{t('takeaway:productsCount')}:</span>
                    <span className="text-t1">{basket.reduce((sum, item) => sum + item.quantity, 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-t3">{t('takeaway:subtotal')}:</span>
                    <span className="text-t1">{formatMoney(basket.reduce((total, item) => total + (item.totalPrice * item.quantity), 0))}</span>
                  </div>
                  {orderDiscount > 0 && (
                    <div className="flex justify-between text-green-600 dark:text-green-400">
                      <span>{t('common:discount')} ({orderDiscount}%):</span>
                      <span>-{formatMoney((basket.reduce((total, item) => total + (item.totalPrice * item.quantity), 0) * orderDiscount / 100))}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-t3">{t('takeaway:vat')}:</span>
                    <span className="text-t1">{formatMoney(calculateVAT())}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg pt-2 border-t border-line-strong text-t1">
                    <span>{t('common:total')}:</span>
                    <span className="text-purple-600 dark:text-purple-400">{formatMoney(calculateTotal())}</span>
                  </div>
                  {hasDeposit && (
                    <>
                      <div className="flex justify-between" data-testid="invoice-deposit-line">
                        <span className="text-t3">{t('deposits:takeaway.title')}:</span>
                        <span className="text-t1 tabular-nums">−{formatMoney(deposit.appliedCents / 100)}</span>
                      </div>
                      <div className="flex justify-between font-semibold text-t1" data-testid="invoice-deposit-due">
                        <span>{t('deposits:takeaway.toPay')}:</span>
                        <span className="tabular-nums">{formatMoney(amountDue)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Προκαταβολή πελάτη: και στο τιμολόγιο (αλλαγή/αφαίρεση πριν την έκδοση) */}
              {deposit.showControl && !invoiceOrderRef.current && (
                <TakeAwayDepositLine selection={deposit.selection} appliedCents={deposit.appliedCents} dueCents={deposit.dueCents}
                  onOpen={deposit.openSheet} onRemove={deposit.remove} disabled={generatingInvoice} />
              )}
            </div>

            {/* Footer: payment method + actions — outside the scroll area so the
                buttons stay reachable even when the customer form is expanded. */}
            <div className="px-6 pt-3 pb-5 border-t border-line space-y-3">
              {/* Payment Method — Μετρητά/Κάρτα γρήγορα, IRIS και «Επί πιστώσει» κάτω από «Περισσότερα».
                  Η προκαταβολή καλύπτει όλο το ποσό: κανένας τρόπος (το τιμολόγιο δηλώνει «Επί πιστώσει», 5). */}
              {deposit.covered ? (
                <p className="text-sm text-t2" data-testid="invoice-deposit-covers">{t('deposits:takeaway.completeHint')}</p>
              ) : (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <button
                    onClick={() => setPaymentMethod('cash')}
                    aria-pressed={paymentMethod === 'cash'}
                    className={`flex-1 py-2 px-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                      paymentMethod === 'cash'
                        ? 'bg-green-600 text-white'
                        : 'bg-surface-2 text-t2 hover:bg-surface-3 border border-line-strong'
                    }`}
                  >
                    <Banknote className="h-4 w-4" />
                    {t('common:payment.cash')}
                  </button>
                  <button
                    onClick={() => setPaymentMethod('card')}
                    aria-pressed={paymentMethod === 'card'}
                    className={`flex-1 py-2 px-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                      paymentMethod === 'card'
                        ? 'bg-yellow-600 text-white'
                        : 'bg-surface-2 text-t2 hover:bg-surface-3 border border-line-strong'
                    }`}
                  >
                    <CreditCard className="h-4 w-4" />
                    {t('common:payment.card')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowMoreMethods(!moreMethodsOpen)}
                    aria-expanded={moreMethodsOpen}
                    aria-controls="takeaway-invoice-more-payment-methods"
                    className="py-2 px-3 rounded-lg font-medium transition-all flex items-center justify-center gap-1 text-sm bg-surface-2 text-t2 hover:bg-surface-3 border border-line-strong"
                  >
                    {t('takeaway:morePaymentMethods')}
                    <ChevronDown className={`h-4 w-4 transition-transform ${moreMethodsOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>
                {moreMethodsOpen && (
                  <div id="takeaway-invoice-more-payment-methods" className="flex gap-2">
                    <button
                      onClick={() => setPaymentMethod('iris')}
                      aria-pressed={paymentMethod === 'iris'}
                      className={`flex-1 py-2 px-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                        paymentMethod === 'iris'
                          ? 'bg-cyan-600 text-white'
                          : 'bg-surface-2 text-t2 hover:bg-surface-3 border border-line-strong'
                      }`}
                    >
                      IRIS
                    </button>
                    <button
                      onClick={() => setPaymentMethod('credit')}
                      aria-pressed={paymentMethod === 'credit'}
                      className={`flex-1 py-2 px-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                        paymentMethod === 'credit'
                          ? 'bg-amber-600 text-white'
                          : 'bg-surface-2 text-t2 hover:bg-surface-3 border border-line-strong'
                      }`}
                    >
                      <HandCoins className="h-4 w-4" />
                      {t('common:payment.credit')}
                    </button>
                  </div>
                )}
              </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={closeInvoiceModal}
                  className="flex-1 border-line-strong text-t2 hover:bg-surface-2"
                  disabled={generatingInvoice}
                >
                  {t('takeaway:cancel')}
                </Button>
                {/* Μετρητά ή «Επί πιστώσει»: έκδοση χωρίς τερματικό (κωδ. 3 / 5)· προκαταβολή για όλο το ποσό: κωδ. 5 */}
                {deposit.covered ? (
                  <Button
                    onClick={generateInvoice}
                    data-testid="invoice-pay-deposit"
                    disabled={!invoiceCustomer || !invoiceCustomer.vat_number || !invoiceCustomer.name?.trim() || generatingInvoice}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    {generatingInvoice ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('takeaway:generating')}
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        {t('deposits:takeaway.complete')}
                      </>
                    )}
                  </Button>
                ) : isNoTerminalMethod(paymentMethod) ? (
                  <Button
                    onClick={generateInvoice}
                    disabled={!invoiceCustomer || !invoiceCustomer.vat_number || !invoiceCustomer.name?.trim() || generatingInvoice}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    {generatingInvoice ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('takeaway:generating')}
                      </>
                    ) : paymentMethod === 'credit' ? (
                      <>
                        <HandCoins className="h-4 w-4 mr-2" />
                        {t('takeaway:creditAndIssue')}
                      </>
                    ) : (
                      <>
                        <Banknote className="h-4 w-4 mr-2" />
                        {t('takeaway:cashAndIssue')}
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      setShowInvoiceModal(false);
                      setShowTerminalModal(true);
                    }}
                    disabled={!invoiceCustomer || !invoiceCustomer.vat_number || !invoiceCustomer.name?.trim()}
                    className={`flex-1 ${paymentMethod === 'iris' ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-yellow-600 hover:bg-yellow-700'}`}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    {paymentMethod === 'iris' ? t('takeaway:payWithIris') : t('takeaway:payWithCard')}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* On-screen keyboard for touch-only POS screens */}
          {invoiceKb && (
            <TouchKeyboard
              mode={invoiceKb.mode}
              label={invoiceKb.label}
              value={invoiceKbValue()}
              onChange={invoiceKbChange}
              onDone={invoiceKbDone}
              maxLength={invoiceKb.maxLength}
            />
          )}
        </div>
      )}


      {/* Checkout Modal — νέα εμφάνιση: renderCheckoutV2 (ίδια λογική, άλλη μορφή) */}
      {showCheckoutModal && v2 && renderCheckoutV2()}
      {showCheckoutModal && !v2 && (
        <div 
          className="cashier-checkout-overlay fixed inset-0 flex items-center justify-center z-50 p-4"
          onClick={() => setShowCheckoutModal(false)}
        >
          <div 
            className={`cashier-checkout bg-surface-1 rounded-2xl w-full shadow-2xl overflow-hidden ${
              paymentMethod === 'cash' && !deposit.covered ? 'max-w-4xl' : 'max-w-lg'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-5 border-b border-line-strong bg-surface-2 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-t1">{t('takeaway:completeOrder')}</h2>
                <p className="text-t3 text-sm">{basket.length} {t('takeaway:itemsCount')}</p>
              </div>
              <button
                onClick={() => setShowCheckoutModal(false)}
                className="w-10 h-10 rounded-xl bg-surface-3 hover:bg-surface-4 flex items-center justify-center active:scale-95 transition-all"
              >
                <X className="h-5 w-5 text-t2" />
              </button>
            </div>

            <div className={`p-5 ${paymentMethod === 'cash' && !deposit.covered ? 'flex gap-6' : ''}`}>
              {/* Left Column - Numpad: πάντα με μετρητά (ο διακόπτης numpad_enabled καταργήθηκε 12/10/2026).
                  Με προκαταβολή πελάτη τα ρέστα βγαίνουν από το υπόλοιπο. */}
              {paymentMethod === 'cash' && !deposit.covered && (
                <div className="w-80 flex-shrink-0">
                  <Label className="text-sm font-bold mb-3 block text-t2">{t('takeaway:amountGiven')}</Label>
                  
                  {/* Cash Display */}
                  <div className="cashier-cash-display bg-surface-2 rounded-xl p-4 mb-4">
                    <div className="text-right">
                      <span className="text-3xl font-bold text-t1">€{cashReceived || '0.00'}</span>
                    </div>
                  </div>
                  
                  {/* Change Display */}
                  {parseFloat(cashReceived || '0') >= amountDue && (
                    <div className="cashier-change bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 rounded-xl p-4 mb-4 text-center" data-testid="checkout-change">
                      <div className="text-sm font-medium">{t('takeaway:change')}</div>
                      <div className="text-3xl font-bold">{formatMoney((parseFloat(cashReceived || '0') - amountDue))}</div>
                    </div>
                  )}
                  
                  {/* Numpad Grid */}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', '←'].map((key) => (
                      <button
                        key={key}
                        onClick={() => {
                          if (key === '←') {
                            setCashReceived(prev => prev.slice(0, -1));
                          } else if (key === '.') {
                            if (!cashReceived.includes('.')) {
                              setCashReceived(prev => prev + '.');
                            }
                          } else {
                            setCashReceived(prev => prev + key);
                          }
                        }}
                        className={`h-14 rounded-xl font-bold text-xl transition-all active:scale-95 ${
                          key === '←' ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/60' : 'bg-surface-3 text-t1 hover:bg-surface-4'
                        }`}
                      >
                        {key}
                      </button>
                    ))}
                  </div>
                  
                  {/* Quick Amount Buttons */}
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {[5, 10, 20, 50].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => {
                          const current = parseFloat(cashReceived || '0');
                          setCashReceived((current + amount).toFixed(2));
                        }}
                        className="cashier-cash-shortcut h-12 rounded-xl bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 font-bold hover:bg-green-200 dark:hover:bg-green-900/60 active:scale-95 transition-all"
                      >
                        +€{amount}
                      </button>
                    ))}
                  </div>
                  
                  {/* Exact & Clear Buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setCashReceived(amountDue.toFixed(2))}
                      className="cashier-cash-shortcut h-12 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 font-bold hover:bg-blue-200 dark:hover:bg-blue-900/60 active:scale-95 transition-all"
                    >
                      {t('takeaway:exactAmount')}
                    </button>
                    <button
                      onClick={() => setCashReceived('')}
                      className="h-12 rounded-xl bg-surface-3 text-t2 font-bold hover:bg-surface-4 active:scale-95 transition-all"
                    >
                      {t('takeaway:clear')}
                    </button>
                  </div>
                </div>
              )}

              {/* Right Column - Payment Options & Summary */}
              <div className={`space-y-4 ${paymentMethod === 'cash' && !deposit.covered ? 'flex-1' : ''}`}>
                {/* Προκαταβολή πελάτη (14/10/2026): πριν από τον τρόπο — αν καλύπτει όλο το ποσό, τρόπος δεν χρειάζεται */}
                {deposit.showControl && (
                  <div>
                    <Label className="text-sm font-bold mb-3 block text-t2">{t('deposits:takeaway.title')}</Label>
                    <TakeAwayDepositLine selection={deposit.selection} appliedCents={deposit.appliedCents} dueCents={deposit.dueCents}
                      onOpen={deposit.openSheet} onRemove={deposit.remove} />
                  </div>
                )}

                {/* Payment Method */}
                {deposit.covered ? (
                  <p className="text-sm text-t2" data-testid="checkout-deposit-covers">{t('deposits:takeaway.completeHint')}</p>
                ) : (
                <div>
                  <Label className="text-sm font-bold mb-3 block text-t2">{t('takeaway:paymentMethod')}</Label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setPaymentMethod('cash')}
                      aria-pressed={paymentMethod === 'cash'}
                      className={`cashier-payment-method flex-1 py-4 px-4 rounded-xl font-bold transition-all flex items-center justify-center gap-3 active:scale-95 ${
                        paymentMethod === 'cash'
                          ? 'bg-green-600 text-white shadow-lg'
                          : 'bg-surface-3 text-t2 hover:bg-surface-4 border-2 border-line-strong'
                      }`}
                    >
                      <Banknote className="h-6 w-6" />
                      {t('takeaway:cashLabel')}
                    </button>
                    <button
                      onClick={() => setPaymentMethod('card')}
                      aria-pressed={paymentMethod === 'card'}
                      className={`cashier-payment-method flex-1 py-4 px-4 rounded-xl font-bold transition-all flex items-center justify-center gap-3 active:scale-95 ${
                        paymentMethod === 'card'
                          ? 'bg-yellow-500 text-white shadow-lg'
                          : 'bg-surface-3 text-t2 hover:bg-surface-4 border-2 border-line-strong'
                      }`}
                    >
                      <CreditCard className="h-6 w-6" />
                      {t('takeaway:cardLabel')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowMoreMethods(!moreMethodsOpen)}
                      aria-expanded={moreMethodsOpen}
                      aria-controls="takeaway-more-payment-methods"
                      className="cashier-payment-method py-4 px-4 rounded-xl font-bold transition-all flex items-center justify-center gap-1 active:scale-95 text-sm bg-surface-3 text-t2 hover:bg-surface-4 border-2 border-line-strong"
                    >
                      {t('takeaway:morePaymentMethods')}
                      <ChevronDown className={`h-4 w-4 transition-transform ${moreMethodsOpen ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                  {/* «Περισσότερα»: IRIS και «Επί πιστώσει» (κωδ. 5, χωρίς ρέστα, χωρίς τερματικό) */}
                  {moreMethodsOpen && (
                    <div id="takeaway-more-payment-methods" className="flex gap-3 mt-3">
                      <button
                        onClick={() => setPaymentMethod('iris')}
                        aria-pressed={paymentMethod === 'iris'}
                        className={`cashier-payment-method flex-1 py-4 px-4 rounded-xl font-bold transition-all flex items-center justify-center gap-3 active:scale-95 ${
                          paymentMethod === 'iris'
                            ? 'bg-cyan-600 text-white shadow-lg'
                            : 'bg-surface-3 text-t2 hover:bg-surface-4 border-2 border-line-strong'
                        }`}
                      >
                        IRIS
                      </button>
                      <button
                        onClick={() => setPaymentMethod('credit')}
                        aria-pressed={paymentMethod === 'credit'}
                        className={`cashier-payment-method flex-1 py-4 px-4 rounded-xl font-bold transition-all flex items-center justify-center gap-3 active:scale-95 ${
                          paymentMethod === 'credit'
                            ? 'bg-amber-600 text-white shadow-lg'
                            : 'bg-surface-3 text-t2 hover:bg-surface-4 border-2 border-line-strong'
                        }`}
                      >
                        <HandCoins className="h-6 w-6" />
                        {t('common:payment.creditCaps')}
                      </button>
                    </div>
                  )}
                </div>
                )}

                {/* Document Type Selection (if ECR is enabled) */}
                {ecrEnabled && documentTypes.length > 0 && (
                  <div>
                    <Label className="text-sm font-bold mb-2 block text-t2">{t('takeaway:documentType')}</Label>
                    <select
                      value={selectedDocumentType || ''}
                      onChange={(e) => setSelectedDocumentType(Number(e.target.value))}
                      className="w-full px-4 py-3 border-2 border-line-strong rounded-xl text-base bg-surface-2 text-t1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {documentTypes
                        .filter(doc => {
                          const basketCategory = getBasketProductCategory();
                          if (!basketCategory) return true;
                          if (basketCategory === 'services') {
                            return doc.invoice_type_code === '11.2' || doc.invoice_type_code === '2.1';
                          } else {
                            return doc.invoice_type_code === '11.1' || doc.invoice_type_code === '1.1';
                          }
                        })
                        .map(doc => (
                          <option key={doc.id} value={doc.id}>
                            {doc.name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                {/* Discount */}
                {hasPermission('takeaway_apply_discount') && (
                <div>
                  <Label className="text-sm font-bold mb-2 block text-t2">{t('takeaway:discount')}</Label>

                  {/* Discount type tabs (show only if fixed amount is allowed) */}
                  {takeawaySettings?.discount_allow_fixed_amount && (
                    <div className="flex gap-2 mb-3">
                      <button
                        onClick={() => { setDiscountType('percent'); setFixedDiscountAmount(0); }}
                        className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                          discountType === 'percent' ? 'bg-purple-600 text-white' : 'bg-surface-3 text-t3 hover:bg-surface-4'
                        }`}
                      >
                        {t('takeaway:discountPercentType')}
                      </button>
                      <button
                        onClick={() => { setDiscountType('fixed'); setOrderDiscount(0); }}
                        className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                          discountType === 'fixed' ? 'bg-purple-600 text-white' : 'bg-surface-3 text-t3 hover:bg-surface-4'
                        }`}
                      >
                        {t('takeaway:discountFixedType')}
                      </button>
                    </div>
                  )}

                  {discountType === 'percent' ? (
                    <div className="space-y-2">
                      {/* Preset percentage buttons */}
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => setOrderDiscount(0)}
                          aria-pressed={orderDiscount === 0}
                          className={`cashier-discount-preset flex-1 min-w-[50px] py-3 rounded-xl font-bold transition-all active:scale-95 ${
                            orderDiscount === 0 ? 'bg-green-600 text-white' : 'bg-surface-3 text-t2 hover:bg-surface-4'
                          }`}
                        >
                          0%
                        </button>
                        {(takeawaySettings?.discount_presets || [5, 10, 15, 20]).map((preset) => (
                          <button
                            key={preset}
                            onClick={() => setOrderDiscount(Math.min(preset, takeawaySettings?.discount_max_percent || 100))}
                            aria-pressed={orderDiscount === preset}
                            className={`cashier-discount-preset flex-1 min-w-[50px] py-3 rounded-xl font-bold transition-all active:scale-95 ${
                              orderDiscount === preset ? 'bg-green-600 text-white' : 'bg-surface-3 text-t2 hover:bg-surface-4'
                            }`}
                          >
                            {preset}%
                          </button>
                        ))}
                      </div>
                      {/* Custom percent input — opens numpad */}
                      {takeawaySettings?.discount_allow_custom_percent && (
                        <button
                          onClick={() => openNumpad('orderPercent', orderDiscount > 0 && !(takeawaySettings?.discount_presets || []).includes(orderDiscount) ? orderDiscount : 0)}
                          className="flex items-center gap-2 h-12 px-4 rounded-xl border-2 border-line-strong bg-surface-2 hover:border-purple-500 active:scale-95 transition-all"
                        >
                          <span className="text-xl font-bold text-t1 min-w-[3ch] text-center">
                            {orderDiscount > 0 && !(takeawaySettings?.discount_presets || []).includes(orderDiscount) ? orderDiscount : '—'}
                          </span>
                          <span className="text-t3 font-bold text-lg">%</span>
                        </button>
                      )}
                    </div>
                  ) : (
                    /* Fixed amount input — opens numpad */
                    <button
                      onClick={() => openNumpad('orderFixed', fixedDiscountAmount)}
                      className="flex items-center gap-2 h-12 px-4 rounded-xl border-2 border-line-strong bg-surface-2 hover:border-purple-500 active:scale-95 transition-all"
                    >
                      <span className="text-xl font-bold text-t1 min-w-[4ch] text-center">
                        {fixedDiscountAmount > 0 ? fixedDiscountAmount.toFixed(2) : '—'}
                      </span>
                      <span className="text-t3 font-bold text-lg">€</span>
                    </button>
                  )}
                </div>
                )}

                {/* Order Notes */}
                <div>
                  <Label className="text-sm font-bold mb-2 block text-t2">{t('takeaway:notes')}</Label>
                  <Input
                    placeholder={t('takeaway:notesPlaceholder')}
                    value={customer.notes}
                    onChange={(e) => setCustomer({ ...customer, notes: e.target.value })}
                    className="h-12 text-base rounded-xl border-2 border-line-strong bg-surface-2 text-t1 placeholder-t3 focus:border-blue-500"
                  />
                </div>

                {/* Totals */}
                <div className="cashier-checkout-summary bg-surface-2 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between text-base">
                    <span className="text-t3">{t('takeaway:subtotal')}</span>
                    <span className="font-medium text-t1">{formatMoney(basket.reduce((total, item) => total + (item.totalPrice * item.quantity), 0))}</span>
                  </div>
                  {discountType === 'percent' && orderDiscount > 0 && (
                    <div className="flex justify-between text-base text-green-600 dark:text-green-400">
                      <span>{t('takeaway:discount')} ({orderDiscount}%)</span>
                      <span className="font-medium">-{formatMoney((basket.reduce((total, item) => total + (item.totalPrice * item.quantity), 0) * orderDiscount / 100))}</span>
                    </div>
                  )}
                  {discountType === 'fixed' && fixedDiscountAmount > 0 && (
                    <div className="flex justify-between text-base text-green-600 dark:text-green-400">
                      <span>{t('takeaway:discount')}</span>
                      <span className="font-medium">-{formatMoney(fixedDiscountAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-xl pt-3 border-t border-line-strong text-t1">
                    <span>{t('common:total')}</span>
                    <span className="text-blue-600 dark:text-blue-400">{formatMoney(calculateTotal())}</span>
                  </div>
                  {hasDeposit && (
                    <>
                      <div className="flex justify-between text-base text-t2" data-testid="checkout-deposit-line">
                        <span>{t('deposits:takeaway.title')}</span>
                        <span className="font-medium tabular-nums">−{formatMoney(deposit.appliedCents / 100)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg text-t1" data-testid="checkout-deposit-due">
                        <span>{t('deposits:takeaway.toPay')}</span>
                        <span className="tabular-nums">{formatMoney(amountDue)}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Prompt to pick a payment method — nothing is preselected, so
                    the complete/invoice buttons stay disabled until one is chosen. */}
                {!paymentMethod && !deposit.covered && (
                  <p className="text-center text-sm text-amber-800/90 dark:text-amber-300/90 pt-1">
                    {t('takeaway:selectPaymentPrompt', { defaultValue: 'Επίλεξε τρόπο πληρωμής για να ολοκληρώσεις' })}
                  </p>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowCheckoutModal(false)}
                    className="flex-1 py-4 px-6 rounded-xl border-2 border-line-strong font-semibold text-t3 hover:bg-surface-2 active:scale-[0.98] transition-all"
                  >
                    {t('takeaway:back')}
                  </button>
                  {isInvoiceDocumentType() ? (
                    <button
                      onClick={() => {
                        if (!paymentMethod && !deposit.covered) return;
                        setShowCheckoutModal(false);
                        setShowInvoiceModal(true);
                      }}
                      disabled={!paymentMethod && !deposit.covered}
                      className="cashier-primary flex-1 py-4 px-6 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-purple-600"
                    >
                      <FileText className="h-5 w-5" />
                      {t('takeaway:invoice')}
                    </button>
                  ) : deposit.covered ? (
                    <button
                      type="button"
                      data-testid="checkout-pay-deposit"
                      onClick={() => {
                        setShowCheckoutModal(false);
                        completeWithDeposit();
                      }}
                      className="cashier-primary flex-1 py-4 px-6 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all select-none bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Check className="h-5 w-5" />
                      {t('deposits:takeaway.complete')}
                    </button>
                  ) : (
                    <button
                      onPointerDown={() => {
                        if (!paymentMethod) return;
                        // Με προκαταβολή πελάτη ποτέ εκπαίδευση (ο server τη δέχεται μόνο σε κανονική παραγγελία).
                        if (trainingModeEnabled && paymentMethod === 'cash' && !deposit.selection) {
                          holdTimerRef.current = setTimeout(() => {
                            setIsTrainingOrder(true);
                          }, 2000);
                        }
                      }}
                      onPointerUp={(e) => {
                        if (holdTimerRef.current) {
                          clearTimeout(holdTimerRef.current);
                          holdTimerRef.current = null;
                        }
                        if (!paymentMethod) return;
                        // Το παράθυρο κλείνει εδώ· το click της αφής θα έπεφτε σε ό,τι είναι από κάτω.
                        swallowGhostClick(e);
                        setShowCheckoutModal(false);
                        handleCheckout();
                      }}
                      onPointerLeave={() => {
                        if (holdTimerRef.current) {
                          clearTimeout(holdTimerRef.current);
                          holdTimerRef.current = null;
                        }
                      }}
                      disabled={!paymentMethod}
                      className={`${isTrainingOrder ? '' : 'cashier-primary'} flex-1 py-4 px-6 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all select-none disabled:opacity-40 disabled:cursor-not-allowed ${
                        isTrainingOrder
                          ? 'bg-orange-500 hover:bg-orange-600 text-white'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      <Receipt className="h-5 w-5" />
                      {t('takeaway:payment')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Προκαταβολή πελάτη: αναζήτηση με τηλέφωνο, επιλογή και ποσά — πάνω από πάνελ, ολοκλήρωση και τιμολόγιο */}
      {deposit.sheetOpen && store?.id && (
        <TakeAwayDepositSheet
          storeId={store.id}
          totalCents={orderTotalCents}
          initial={deposit.selection}
          onApply={selection => {
            deposit.apply(selection);
            // Καλύπτει όλο το ποσό: κανένας τρόπος/ρέστα από πριν (θα έμεναν για την επόμενη πληρωμή).
            if (selection.uses.reduce((s, u) => s + u.amountCents, 0) >= orderTotalCents) {
              setPaymentMethod(null);
              setCashReceived('');
            }
          }}
          onRemove={deposit.remove}
          onClose={deposit.closeSheet}
        />
      )}

      {/* Το παραστατικό δεν εκδόθηκε (409 receipt_documents_pending): «Ξανά έκδοση» στην ΙΔΙΑ παραγγελία */}
      {docRetry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="takeaway-doc-retry">
          <div role="alertdialog" aria-modal="true" aria-labelledby="takeaway-doc-retry-title"
            className="w-full max-w-md rounded-2xl border border-line bg-surface-1 p-6 shadow-2xl">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/15">
              <AlertTriangle className="h-9 w-9 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 id="takeaway-doc-retry-title" className="text-center text-xl font-bold text-t1">{t('deposits:takeaway.docRetryTitle')}</h3>
            {docRetry.message && <p className="mt-2 text-center text-t2" data-testid="takeaway-doc-retry-message">{docRetry.message}</p>}
            <p className="mt-2 text-center text-sm text-t3">{t('deposits:takeaway.docRetryHint', { code: docRetry.shortCode })}</p>
            <div className="mt-6 grid gap-2">
              <button type="button" onClick={retryDocument} disabled={docRetryBusy} data-testid="takeaway-doc-retry-again"
                className="h-14 rounded-xl bg-blue-600 text-lg font-bold text-white hover:bg-blue-700 active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                {docRetryBusy && <Loader2 className="h-5 w-5 animate-spin" />}
                {t('deposits:takeaway.docRetry')}
              </button>
              <button type="button" onClick={leaveDocumentForManager} disabled={docRetryBusy}
                className="h-12 rounded-xl border border-line-strong font-semibold text-t2 hover:bg-surface-2 disabled:opacity-50">
                {t('deposits:takeaway.docRetryLeave')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discount numpad — shared component. The numpadTarget state routes
          the confirmed value to the appropriate setter (product/order/temp). */}
      <NumpadModal
        open={showNumpad}
        initialValue={parseFloat(numpadValue) || 0}
        suffix={numpadTarget === 'orderFixed' ? '€' : '%'}
        allowDecimal={numpadTarget === 'orderFixed'}
        min={0}
        max={
          numpadTarget === 'orderFixed'
            ? (takeawaySettings?.discount_max_amount || 9999)
            : numpadTarget === 'productDiscount'
              ? 100
              : (takeawaySettings?.discount_max_percent || 100)
        }
        onClose={() => setShowNumpad(false)}
        onConfirm={handleSharedNumpadConfirm}
      />
    </div>
  );
};

export default TakeAway;
